import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, LineChart, Line } from 'recharts';
import {
    BarChart2, Users, Briefcase, Building2, User, ChevronDown, X, Search,
    TrendingUp, Clock, CheckCircle2, AlertTriangle, Loader2,
    Target, Brain, ArrowRight, AlertCircle, ChevronRight,
    Activity, Award, Flame, Shield, Trash2, Filter, GitBranch, Archive, Mail, Phone, MessageCircle,
} from 'lucide-react';
import { supabase, supabase_get_vacancies } from '../services/supabaseService';
import {
    applicationVacancyLinkedToPortalCompany,
    vacancyLinkedToPortalOrgSet,
    isPortalArchivedVacancy,
    filterPortalVisibleVacancies,
    isClientUuid,
} from '../utils/clientPortalGuards';
import { fetchRecruitmentDashboard } from '../services/recruitmentDashboardApi';
import type { RecruitmentDashboardPayload } from '../recruitmentDashboard.types';
import {
    resolveReportingKpiTargets,
    getReportingKpiTargetBasis,
    scaleReportingKpiTargetsToFilterWindow,
    DEFAULT_REPORTING_KPI_TARGETS,
    type ReportingKpiTargets,
} from '../services/reportingKpiTargets';
import {
    loadReportingBranches,
    addReportingBranch,
    deleteReportingBranch,
    reportingBranchesStorageEvent,
    type ReportingBranch,
} from '../services/reportingBranches';
import { DonutKpiCard } from './reporting/DonutKpiCard';
import VacancyDetailModal from './VacancyDetailModal';
import {
    reportingStatusToCanonical,
    type ReportingCanonicalStage,
} from '../utils/reportingStageCanonical';
import {
    isWebsiteChannelApplication,
    websiteChannelSourceDisplayLabel,
} from '../utils/candidateJourneyBuckets';
import {
    applicationInReportingPeriod,
    createdAtInReportingPeriod,
    getEffectiveReportingFilterDays,
    getReportingPeriodIso,
    reportingPeriodLabel,
    reportingPeriodRangeLabel,
    type ReportingPeriodCustom,
} from '../utils/reportingPeriod';
import {
    enrichApplicationsWithCandidateProfiles,
    fetchCandidateProfilesByCandidateIds,
    fetchWebsiteSourceProfiles,
    profileIndicatesWebsiteChannel,
    type CandidateClientProfileRow,
} from '../utils/reportingWebsiteApplications';
import { computeReportingScopedMetrics } from '../utils/reportingScopedMetrics';
import { fetchReportingScopedPlacements } from '../utils/reportingPlacements';
import { searchTextIncludes } from '../utils/searchText';

type KpiDrawerRow = {
    vacancy?: string;
    candidate?: string;
    stage?: string;
    at?: string;
    vacancyId?: string;
    candidateId?: string;
    applicationId?: string;
    /** Vacature lifecycle: Doing / Open / Gesloten / Gepubliceerd */
    vacancyLiveStatus?: string;
    /** Huidige sollicitatie-pipelinestatus */
    applicationLiveStatus?: string;
    /** ISO — laatste wijziging (historiek of updated_at) */
    statusLastChangedAt?: string;
    /** Bron website / campaign */
    sourceLabel?: string;
    opdrachtgeverName?: string;
    opdrachtgeverLogoUrl?: string;
    eindklantName?: string;
    eindklantLogoUrl?: string;
    /** Live pipeline vs stage-historiek in gekozen periode */
    recordKind?: 'live' | 'historical';
};

function buildDefaultReportingFilterSnapshot(): string {
    return JSON.stringify({
        filterVacancyId: '',
        filterClientId: '',
        filterEindklantId: '',
        filterRecruiterId: '',
        filterContactId: '',
        filterPeriod: 'week',
        customDateFrom: '',
        customDateTo: '',
        activeBranchId: '',
        filterVacancyArchive: 'all',
    });
}

function tagKpiDrawerLiveRows(rows: KpiDrawerRow[]): KpiDrawerRow[] {
    return rows.map((r) => ({ ...r, recordKind: 'live' as const }));
}

function tagKpiDrawerHistoricalRows(rows: KpiDrawerRow[]): KpiDrawerRow[] {
    return rows.map((r) => ({ ...r, recordKind: 'historical' as const }));
}

function sortKpiDrawerRowsByDate(rows: KpiDrawerRow[]): KpiDrawerRow[] {
    return [...rows].sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());
}

function mergeKpiDrawerLiveAndHistory(live: KpiDrawerRow[], historical: KpiDrawerRow[]): KpiDrawerRow[] {
    return sortKpiDrawerRowsByDate([...tagKpiDrawerLiveRows(live), ...tagKpiDrawerHistoricalRows(historical)]);
}

function kpiDrawerRecordKindLabel(kind: KpiDrawerRow['recordKind']): string {
    if (kind === 'historical') return 'Historische status';
    if (kind === 'live') return 'Huidige status';
    return '—';
}

function formatKpiHistoricalOnlyFootLine(historical: number | null): string {
    const h = historical === null ? '…' : historical.toLocaleString('nl-NL');
    return `Aantal op historische status: ${h}`;
}

type ReportingClientOption = {
    id: string;
    name: string;
    city: string;
    logoUrl?: string;
    bullhornId?: string;
};

type KpiDrawerKind =
    | 'jobsAdded'
    | 'shortlists'
    | 'cvsSent'
    | 'firstInterviews'
    | 'furtherInterviews'
    | 'offers'
    | 'rejectedApplications'
    | 'permPlacements'
    | 'websiteApplications'
    | null;

type KpiDrawerHistoryKind = Exclude<KpiDrawerKind, null | 'jobsAdded' | 'websiteApplications'>;

function isKpiDrawerHistoryKind(k: KpiDrawerKind): k is KpiDrawerHistoryKind {
    return Boolean(
        k &&
            k !== 'jobsAdded' &&
            k !== 'websiteApplications' &&
            k !== 'permPlacements' &&
            k !== 'rejectedApplications'
    );
}

function placementDataObject(p: any): Record<string, unknown> | null {
    const raw = p?.data;
    if (!raw) return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
        } catch {
            return null;
        }
    }
    return null;
}

function placementVacancyTitle(p: any, vacanciesById: Map<string, any>): string {
    const vacEmbed = Array.isArray(p?.vacancy) ? p.vacancy[0] : p?.vacancy;
    const vac =
        vacEmbed || (p?.vacancy_id ? vacanciesById.get(String(p.vacancy_id)) : null);
    if (vac?.title?.trim()) return vac.title.trim();
    const d = placementDataObject(p);
    const jobOrder = d?.jobOrder as { title?: string } | undefined;
    if (jobOrder?.title?.trim()) return jobOrder.title.trim();
    if (typeof d?.title === 'string' && d.title.trim()) return d.title.trim();
    if (p?.end_client?.trim()) return String(p.end_client).trim();
    if (p?.number != null && String(p.number).trim()) return `Plaatsing #${p.number}`;
    return '—';
}

function reportingClientFieldsForPlacement(
    p: any,
    clients: ReportingClientOption[],
    vacanciesById: Map<string, any>
): Pick<KpiDrawerRow, 'opdrachtgeverName' | 'opdrachtgeverLogoUrl' | 'eindklantName' | 'eindklantLogoUrl'> {
    const vacEmbed = Array.isArray(p?.vacancy) ? p.vacancy[0] : p?.vacancy;
    const vacResolved =
        vacEmbed || (p?.vacancy_id ? vacanciesById.get(String(p.vacancy_id)) : null);
    if (vacResolved) return reportingClientFieldsForVacancy(vacResolved, clients);
    return reportingClientFieldsForVacancy(
        {
            opdrachtgever_id: p?.opdrachtgever_id,
            eindklant_id: p?.eindklant_id,
        },
        clients
    );
}

/** KPI-drawerrijen 1:1 met `public.placements` (zelfde bron als hoofdtelling). */
function buildPermPlacementKpiRows(
    placements: any[],
    vacanciesById: Map<string, any>,
    reportingClients: ReportingClientOption[]
): KpiDrawerRow[] {
    return (placements || []).map((p: any) => {
        const vacEmbed = Array.isArray(p?.vacancy) ? p.vacancy[0] : p?.vacancy;
        const vacResolved =
            vacEmbed || (p?.vacancy_id ? vacanciesById.get(String(p.vacancy_id)) : null);
        const cand = Array.isArray(p?.candidate) ? p.candidate[0] : p?.candidate;
        const num = p?.number != null && String(p.number).trim() ? `#${p.number} ` : '';
        return {
            vacancy: `${num}${placementVacancyTitle(p, vacanciesById)}`.trim(),
            candidate: cand?.name || '—',
            stage: p?.status ? `Plaatsing · ${p.status}` : 'Plaatsing',
            at: p?.created_at || p?.updated_at,
            vacancyId: p?.vacancy_id || undefined,
            candidateId: p?.candidate_id,
            applicationId: undefined,
            vacancyLiveStatus: formatVacancyLiveStatusLabel(vacResolved),
            applicationLiveStatus: p?.status || 'Plaatsing',
            statusLastChangedAt: p?.updated_at || p?.created_at,
            ...reportingClientFieldsForPlacement(p, reportingClients, vacanciesById),
        };
    });
}

function formatPermPlacementsFootLine(count: number): string {
    return `${count.toLocaleString('nl-NL')} plaatsingen (placements-tabel; created_at of start_date in periode)`;
}

/** Donut-onderregel: live = huidige pipeline-telling (zelfde als lijst); historisch = treffers in stage-historiek binnen periode. */
function formatKpiLiveHistFootLine(live: number, historical: number | null): string {
    const l = live.toLocaleString('nl-NL');
    const h = historical === null ? '…' : historical.toLocaleString('nl-NL');
    return `Aantal op live status: ${l} · Aantal op historische status: ${h}`;
}

function formatWebsiteAppsFootLine(live: number): string {
    return `Aantal op live status: ${live.toLocaleString('nl-NL')} · Aantal op historische status: n.v.t.`;
}

type ReportingAppliedFilters = {
    filterVacancyId: string;
    filterClientId: string;
    filterEindklantId: string;
    filterRecruiterId: string;
    filterContactId: string;
    filterPeriod: string;
    customDateFrom: string;
    customDateTo: string;
    activeBranchId: string;
    filterVacancyArchive: 'all' | 'active' | 'archived';
};

function parseAppliedReportingFilters(snapshot: string): ReportingAppliedFilters {
    const defaults: ReportingAppliedFilters = {
        filterVacancyId: '',
        filterClientId: '',
        filterEindklantId: '',
        filterRecruiterId: '',
        filterContactId: '',
        filterPeriod: 'week',
        customDateFrom: '',
        customDateTo: '',
        activeBranchId: '',
        filterVacancyArchive: 'all',
    };
    try {
        const p = JSON.parse(snapshot || '{}');
        const arch = p.filterVacancyArchive;
        return {
            filterVacancyId: String(p.filterVacancyId || ''),
            filterClientId: String(p.filterClientId || ''),
            filterEindklantId: String(p.filterEindklantId || ''),
            filterRecruiterId: String(p.filterRecruiterId || ''),
            filterContactId: String(p.filterContactId || ''),
            filterPeriod: String(p.filterPeriod || 'week'),
            customDateFrom: String(p.customDateFrom || ''),
            customDateTo: String(p.customDateTo || ''),
            activeBranchId: String(p.activeBranchId || ''),
            filterVacancyArchive:
                arch === 'active' || arch === 'archived' ? arch : 'all',
        };
    } catch {
        return defaults;
    }
}

/** `client_id` = Werftools-klant; opdrachtgever = alleen `opdrachtgever_id`. */
function vacancyOpdrachtgeverId(v: any): string {
    return String(
        v?.opdrachtgever_id ?? v?.data?.opdrachtgever_id ?? v?.opdrachtgever?.id ?? ''
    ).trim();
}

function vacancyEindklantId(v: any): string {
    return String(v?.eindklant_id ?? v?.data?.eindklant_id ?? v?.eindklant?.id ?? '').trim();
}

function vacancyMatchesOpdrachtgeverFilter(v: any, opdrachtgeverId: string): boolean {
    if (!opdrachtgeverId) return true;
    if (!v) return false;
    return vacancyOpdrachtgeverId(v) === String(opdrachtgeverId).trim();
}

function vacancyMatchesEindklantFilter(v: any, eindklantId: string): boolean {
    if (!eindklantId) return true;
    if (!v) return false;
    return vacancyEindklantId(v) === String(eindklantId).trim();
}

function resolveVacancyFromApplication(a: any, vacanciesById: Map<string, any>): any {
    const embed = a?.vacancies ?? a?.vacancy;
    if (embed) return Array.isArray(embed) ? embed[0] : embed;
    const vid = String(a?.vacancy_id || '').trim();
    if (vid && vacanciesById.has(vid)) return vacanciesById.get(vid);
    return null;
}

function applicationMatchesOpdrachtgeverFilter(
    a: any,
    opdrachtgeverId: string,
    vacanciesById: Map<string, any>
): boolean {
    return vacancyMatchesOpdrachtgeverFilter(resolveVacancyFromApplication(a, vacanciesById), opdrachtgeverId);
}

function applicationMatchesEindklantFilter(
    a: any,
    eindklantId: string,
    vacanciesById: Map<string, any>
): boolean {
    return vacancyMatchesEindklantFilter(resolveVacancyFromApplication(a, vacanciesById), eindklantId);
}

/** Unieke eindklant/werkplek-ids op portaal-vacatures (nooit globale CRM-index). */
function collectPortalWerkplekEindklantIds(
    portalVacancies: readonly any[],
    applications: readonly any[],
    portalScopeIds: readonly string[],
    vacanciesById: Map<string, any>
): Set<string> {
    const ids = new Set<string>();
    for (const v of portalVacancies) {
        const ek = vacancyEindklantId(v);
        if (ek) ids.add(ek);
    }
    for (const a of applications) {
        if (!applicationVacancyLinkedToPortalCompany(a, undefined, portalScopeIds)) continue;
        const vac = resolveVacancyFromApplication(a, vacanciesById);
        if (vac) {
            const ek = vacancyEindklantId(vac);
            if (ek) ids.add(ek);
        }
    }
    return ids;
}

/** Klantportaal dropdown: filter op werkplek = vacature.eindklant_id. */
function vacancyMatchesPortalWerkplekFilter(v: any, werkplekEindklantId: string): boolean {
    if (!werkplekEindklantId) return true;
    return vacancyEindklantId(v) === String(werkplekEindklantId).trim();
}

function applicationMatchesPortalWerkplekFilter(
    a: any,
    werkplekEindklantId: string,
    portalScopeIds: string[],
    vacanciesById: Map<string, any>
): boolean {
    if (!applicationVacancyLinkedToPortalCompany(a, undefined, portalScopeIds)) return false;
    if (!werkplekEindklantId) return true;
    const vac = resolveVacancyFromApplication(a, vacanciesById) || a?.vacancies;
    const embed = vac ? (Array.isArray(vac) ? vac[0] : vac) : null;
    return vacancyMatchesPortalWerkplekFilter(embed, werkplekEindklantId);
}

/** Vacature-ids voor sollicitatie-load, volgens toegepaste rapportagefilters. */
function vacancyIdsForReportingLoad(
    vacancies: any[],
    applied: ReportingAppliedFilters,
    isClientPortal: boolean,
    portalScopeIds: string[]
): string[] {
    let r = vacancies;
    if (applied.filterVacancyId) {
        return r
            .filter((v) => v.id === applied.filterVacancyId)
            .map((v) => String(v?.id || '').trim())
            .filter(Boolean);
    }
    if (isClientPortal) {
        if (!portalScopeIds.length) return [];
        r = r.filter((v) => vacancyLinkedToPortalOrgSet(v, portalScopeIds) && !isPortalArchivedVacancy(v));
        if (applied.filterClientId) {
            r = r.filter((v) => vacancyMatchesPortalWerkplekFilter(v, applied.filterClientId));
        }
    } else if (applied.filterClientId) {
        r = r.filter((v) => vacancyMatchesOpdrachtgeverFilter(v, applied.filterClientId));
    }
    if (!isClientPortal && applied.filterEindklantId) {
        r = r.filter((v) => vacancyMatchesEindklantFilter(v, applied.filterEindklantId));
    }
    if (applied.filterRecruiterId) {
        r = r.filter((v) => vacancyMatchesRecruiterFilter(v, applied.filterRecruiterId));
    }
    return r.map((v) => String(v?.id || '').trim()).filter(Boolean);
}

function vacancyMatchesRecruiterFilter(v: any, recruiterId: string): boolean {
    if (!recruiterId) return true;
    if (!v) return false;
    return vacancyRecruiterIdFromRow(v) === String(recruiterId).trim();
}

function applicationMatchesRecruiterFilter(
    a: any,
    recruiterId: string,
    vacanciesById: Map<string, any>
): boolean {
    if (!recruiterId) return true;
    const target = String(recruiterId).trim();
    const embed = a?.vacancies ?? a?.vacancy;
    const vacEmbed = embed ? (Array.isArray(embed) ? embed[0] : embed) : null;
    if (vacancyRecruiterIdFromRow(vacEmbed) === target) return true;
    const v = resolveVacancyFromApplication(a, vacanciesById);
    return vacancyRecruiterIdFromRow(v) === target;
}

/** Vacature-ids voor recruiter uit DB (`data.contactPersonId` + legacy), los van `liveVacancies`. */
async function fetchVacancyIdsForRecruiter(
    recruiterId: string,
    scope?: { opdrachtgeverId?: string; eindklantId?: string }
): Promise<string[]> {
    const target = String(recruiterId).trim();
    if (!target) return [];
    const ids = new Set<string>();
    const og = String(scope?.opdrachtgeverId || '').trim();
    const ek = String(scope?.eindklantId || '').trim();

    const addMatching = (rows: any[] | null | undefined) => {
        for (const row of rows || []) {
            if (vacancyRecruiterIdFromRow(row) !== target) continue;
            const id = String(row?.id || '').trim();
            if (id) ids.add(id);
        }
    };

    const applyScope = (q: any) => {
        if (og) q = q.eq('opdrachtgever_id', og);
        if (ek) q = q.eq('eindklant_id', ek);
        return q;
    };

    try {
        const { data, error } = await applyScope(
            supabase
                .from('vacancies')
                .select('id, assigned_recruiter_id, data, opdrachtgever_id, eindklant_id')
                .eq('data->>contactPersonId' as any, target)
        );
        if (error) throw error;
        addMatching(data);
    } catch (e) {
        console.warn('[Reporting] recruiter vacancies (contactPersonId)', e);
    }

    try {
        const orExpr = [
            `data->>recruiter_id.eq.${target}`,
            `data->>assignedRecruiterId.eq.${target}`,
            `assigned_recruiter_id.eq.${target}`,
        ].join(',');
        const { data, error } = await applyScope(
            supabase
                .from('vacancies')
                .select('id, assigned_recruiter_id, data, opdrachtgever_id, eindklant_id')
                .or(orExpr)
        );
        if (error) throw error;
        addMatching(data);
    } catch (e) {
        console.warn('[Reporting] recruiter vacancies (legacy json/column)', e);
    }

    return Array.from(ids);
}

function vacancyMatchesBranchClients(v: any, memberIds: string[]): boolean {
    if (!memberIds.length) return true;
    if (!v) return false;
    const og = vacancyOpdrachtgeverId(v);
    const ek = vacancyEindklantId(v);
    return memberIds.some((id) => id === og || id === ek);
}

function applicationMatchesBranchClients(
    a: any,
    memberIds: string[],
    vacanciesById: Map<string, any>
): boolean {
    const vac = resolveVacancyFromApplication(a, vacanciesById);
    const embed = vac ?? (Array.isArray(a?.vacancies) ? a.vacancies[0] : a?.vacancies);
    return vacancyMatchesBranchClients(embed, memberIds);
}

/** Scope filters on vacatures (geen periode — periode geldt voor sollicitaties). */
function applyReportingVacancyScopeFilters(
    source: any[],
    applied: ReportingAppliedFilters,
    branches: ReportingBranch[],
    isClientPortal: boolean,
    portalScopeIds: string[]
): any[] {
    let r = source;

    if (applied.activeBranchId) {
        const br = branches.find((s) => s.id === applied.activeBranchId);
        if (br?.kind === 'clients' && br.memberIds.length > 0) {
            r = r.filter((v) => vacancyMatchesBranchClients(v, br.memberIds));
        }
        if (br?.kind === 'recruiters' && br.memberIds.length > 0) {
            r = r.filter((v) => {
                const rid = vacancyRecruiterIdFromRow(v);
                return Boolean(rid && br.memberIds.includes(rid));
            });
        }
    }

    if (applied.filterVacancyId) {
        r = r.filter((v) => v.id === applied.filterVacancyId);
    }
    if (applied.filterClientId) {
        if (isClientPortal) {
            if (portalScopeIds.length) {
                r = r.filter(
                    (v) =>
                        vacancyLinkedToPortalOrgSet(v, portalScopeIds) &&
                        vacancyMatchesPortalWerkplekFilter(v, applied.filterClientId)
                );
            } else {
                r = [];
            }
        } else {
            r = r.filter((v) => vacancyMatchesOpdrachtgeverFilter(v, applied.filterClientId));
        }
    } else if (isClientPortal && portalScopeIds.length) {
        r = r.filter((v) => vacancyLinkedToPortalOrgSet(v, portalScopeIds));
    } else if (isClientPortal) {
        r = [];
    }
    if (!isClientPortal && applied.filterEindklantId) {
        r = r.filter((v) => vacancyMatchesEindklantFilter(v, applied.filterEindklantId));
    }
    if (applied.filterRecruiterId) {
        r = r.filter((v) => vacancyMatchesRecruiterFilter(v, applied.filterRecruiterId));
    }

    return r;
}

const KPI_HISTORY_CANON: Record<
    Exclude<KpiDrawerKind, null | 'jobsAdded' | 'websiteApplications'>,
    ReportingCanonicalStage
> = {
    shortlists: 'shortlisted',
    cvsSent: 'cv_sent',
    firstInterviews: 'interview_1',
    furtherInterviews: 'interview_further',
    offers: 'offer',
    permPlacements: 'placement',
};

type KpiHistKind = keyof typeof KPI_HISTORY_CANON;

function emptyKpiHistFootCounts(): Record<KpiHistKind, number> {
    return {
        shortlists: 0,
        cvsSent: 0,
        firstInterviews: 0,
        furtherInterviews: 0,
        offers: 0,
        permPlacements: 0,
    };
}

interface ReportingProps {
    vacancies: any[];
    candidates: any[];
    clients: any[];
    user: any;
    portalAllowedClientIds?: string[];
}

/** Live pipeline: Shortlist-donut / funnel (exacte application.status uit DB). */
const REPORTING_SHORTLIST_STATUSES = new Set([
    'Op shortlist',
    'Shortlisted',
    'Shortlist',
    'New',
    'Lead',
]);

/** Live pipeline: Voorgesteld-donut / funnel. */
const REPORTING_VOORGESTELD_STATUSES = new Set(['Voorgesteld', 'Voorstellen']);

function applicationStatusInReportingShortlist(status: string | null | undefined): boolean {
    const s = String(status ?? '').trim();
    if (!s) return false;
    if (REPORTING_SHORTLIST_STATUSES.has(s)) return true;
    const l = s.toLowerCase();
    return ['op shortlist', 'shortlisted', 'shortlist', 'new', 'lead'].includes(l);
}

function applicationStatusInReportingVoorgesteld(status: string | null | undefined): boolean {
    const s = String(status ?? '').trim();
    if (!s) return false;
    if (REPORTING_VOORGESTELD_STATUSES.has(s)) return true;
    const l = s.toLowerCase();
    return l === 'voorgesteld' || l === 'voorstellen';
}

function createReportingStatusMatcher(exactStatuses: readonly string[]) {
    const exact = new Set(exactStatuses);
    const lower = exactStatuses.map((x) => x.toLowerCase());
    return (status: string | null | undefined): boolean => {
        const s = String(status ?? '').trim();
        if (!s) return false;
        if (exact.has(s)) return true;
        return lower.includes(s.toLowerCase());
    };
}

const applicationStatusInReportingFirstInterview = createReportingStatusMatcher([
    'Gesprek',
    '1e gesprek',
]);

const applicationStatusInReportingFurtherInterview = createReportingStatusMatcher([
    '2e gesprek',
    '3e gesprek',
    'Laatste Gesprek',
]);

const applicationStatusInReportingOffer = createReportingStatusMatcher(['Aanbod', 'Offers']);

const applicationStatusInReportingRejected = createReportingStatusMatcher([
    'Afgewezen door opdrachtgever',
    'Afgewezen',
]);

/** Overige statussen: label + DB-varianten (case-insensitive). */
const REPORTING_OTHER_STATUS_DEFS: readonly { label: string; variants: readonly string[] }[] = [
    { label: 'Niet voorgesteld', variants: ['Niet voorgesteld'] },
    { label: 'Submitted', variants: ['Submitted'] },
    { label: 'Afgewezen', variants: ['Afgewezen'] },
    { label: 'Nog af te wijzen', variants: ['Nog af te wijzen'] },
    { label: 'Interview bevestiging mailen', variants: ['Interview bevestiging mailen'] },
    { label: 'Kandidaat teruggetrokken', variants: ['Kandidaat teruggetrokken'] },
    { label: 'Bevestiging niet mailen', variants: ['Bevestiging niet mailen'] },
    { label: 'Geen feedback klant', variants: ['Geen feedback klant'] },
    { label: 'opdracht ingetrokken', variants: ['opdracht ingetrokken', 'Opdracht ingetrokken'] },
    { label: 'Positief klant', variants: ['Positief klant'] },
    { label: 'Warme stoel', variants: ['Warme stoel'] },
    { label: 'New Submission', variants: ['New Submission'] },
    { label: 'Negatief klant', variants: ['Negatief klant'] },
    { label: 'Interview gewenst (klantportaal)', variants: ['Interview gewenst (klantportaal)'] },
];

function applicationStatusMatchesReportingOtherDef(
    status: string | null | undefined,
    variants: readonly string[]
): boolean {
    const s = String(status ?? '').trim();
    if (!s) return false;
    const l = s.toLowerCase();
    return variants.some((v) => v === s || v.toLowerCase() === l);
}

function countReportingOtherStatuses(apps: { status?: string | null }[]): { label: string; count: number }[] {
    const out: { label: string; count: number }[] = [];
    for (const def of REPORTING_OTHER_STATUS_DEFS) {
        const count = apps.filter((a) =>
            applicationStatusMatchesReportingOtherDef(a.status, def.variants)
        ).length;
        if (count > 0) out.push({ label: def.label, count });
    }
    return out;
}

// Funnel stages (ordered)
const FUNNEL_STAGES = [
    { id: 'Shortlist',    label: 'Shortlist',    color: '#e51b23' },
    { id: 'Voorgesteld',  label: 'Voorgesteld',  color: '#ef4444' },
    { id: 'Gesprek',      label: 'Gesprek',      color: '#f97316' },
    { id: 'Aanbod',       label: 'Aanbod',       color: '#f59e0b' },
    { id: 'Plaatsing',    label: 'Geplaatst',    color: '#16a34a' },
];

const TABS = [
    { id: 'overview',        label: 'Overzicht',        icon: Activity },
    { id: 'vacancies',       label: 'Vacatures',        icon: Briefcase },
    { id: 'recruiters',      label: 'Recruiters',       icon: User },
    { id: 'rejections',      label: 'Afwijzingen',      icon: AlertTriangle },
    { id: 'conversions',     label: 'Conversies',       icon: Target },
];

const SOURCES = ['LinkedIn', 'Jobboard', 'Database', 'Sourcing', 'Overig'];

type PeriodCustom = ReportingPeriodCustom;

const getPeriodIso = getReportingPeriodIso;

/** PostgREST OR on embedded `vacancies`: klantportaal = opdrachtgever_id of eindklant_id. */
function portalVacancyForeignTableOrFilter(ids: string[]): string {
    return ids.flatMap((id) => [`opdrachtgever_id.eq.${id}`, `eindklant_id.eq.${id}`]).join(',');
}

const getEffectiveFilterDays = getEffectiveReportingFilterDays;

/** PostgREST filter: sollicitaties aangemaakt ó bijgewerkt (e.g. pipeline) binnen het venster. */
function applicationsReportingPeriodOrFilter(dateFrom: string, dateTo: string): string {
    return `and(created_at.gte.${dateFrom},created_at.lte.${dateTo}),and(updated_at.gte.${dateFrom},updated_at.lte.${dateTo})`;
}

/** Boven dit aantal: geen `.in(vacancy_id)`-chunks (veroorzaakt 408 timeouts bij grote tenants). */
const REPORTING_MAX_VAC_IDS_CHUNK_LOAD = 80;
const REPORTING_APPS_PAGE_SIZE = 1000;
const REPORTING_APPS_MAX_ROWS = 20000;

function reportingApplicationsSelectForPortal(baseSelect: string): string {
    return baseSelect.replace('vacancies(id', 'vacancies!inner(id');
}

/**
 * Eén gepagineerde applications-query (period + optioneel vac-filter), i.p.v. honderden parallelle requests.
 */
async function fetchReportingApplicationsPaged(opts: {
    select: string;
    periodOr?: string | null;
    vacIds?: readonly string[];
    portalIds?: readonly string[];
    portalInnerVacancy?: boolean;
}): Promise<{ rows: any[]; selectUsed: string }> {
    const vacIdSet =
        opts.vacIds && opts.vacIds.length > REPORTING_MAX_VAC_IDS_CHUNK_LOAD
            ? new Set(opts.vacIds.map((id) => String(id).trim()).filter(Boolean))
            : null;
    const byId = new Map<string, any>();
    let from = 0;
    let selectUsed = opts.select;

    while (from < REPORTING_APPS_MAX_ROWS) {
        let select = selectUsed;
        if (opts.portalInnerVacancy) {
            select = reportingApplicationsSelectForPortal(select);
        }
        let q = supabase.from('applications').select(select);
        if (opts.periodOr) q = q.or(opts.periodOr);
        if (opts.vacIds?.length && opts.vacIds.length <= REPORTING_MAX_VAC_IDS_CHUNK_LOAD) {
            q = q.in('vacancy_id', [...opts.vacIds]);
        }
        if (opts.portalInnerVacancy && opts.portalIds?.length) {
            q = q.or(portalVacancyForeignTableOrFilter([...opts.portalIds]), {
                foreignTable: 'vacancies',
            });
        }
        const { data, error } = await q
            .order('updated_at', { ascending: false })
            .range(from, from + REPORTING_APPS_PAGE_SIZE - 1);

        if (error) {
            const slim =
                '*, candidates(id,name,email,phone,ai_parsed_data,status,source), vacancies(id,title,status,client_id,data,assigned_recruiter_id,created_at,updated_at,bullhorn_id,eindklant_id,opdrachtgever_id)';
            if (selectUsed === opts.select && opts.select !== slim) {
                selectUsed = slim;
                continue;
            }
            throw error;
        }
        if (!data?.length) break;
        for (const row of data) {
            const id = String(row?.id || '').trim();
            if (!id) continue;
            const vid = String(row?.vacancy_id || '').trim();
            if (vacIdSet && vid && !vacIdSet.has(vid)) continue;
            byId.set(id, row);
        }
        if (data.length < REPORTING_APPS_PAGE_SIZE) break;
        from += REPORTING_APPS_PAGE_SIZE;
    }

    return { rows: Array.from(byId.values()), selectUsed };
}

const applicationInSelectedPeriod = applicationInReportingPeriod;
const createdAtInSelectedPeriod = createdAtInReportingPeriod;

function profileMatchesReportingScope(
    p: CandidateClientProfileRow,
    applied: ReportingAppliedFilters,
    branches: ReportingBranch[],
    isClientPortal: boolean,
    portalScopeIds: string[],
    portalWerkplekIds?: ReadonlySet<string>
): boolean {
    if (applied.filterVacancyId || applied.filterRecruiterId) return false;
    if (!isClientPortal && applied.filterEindklantId) return false;

    if (applied.activeBranchId) {
        const br = branches.find((b) => b.id === applied.activeBranchId);
        if (br?.kind === 'clients' && br.memberIds.length > 0) {
            // Branch-leden zijn CRM opdrachtgever/eindklant — niet profiles.client_id (tenant).
            return false;
        }
        if (br?.kind === 'recruiters') return false;
    }

    if (isClientPortal) {
        if (!portalScopeIds.length) return false;
        const cid = String(p.client_id || '').trim();
        if (!cid) return false;
        if (portalScopeIds.includes(cid)) return true;
        if (portalWerkplekIds?.has(cid)) return true;
        return false;
    }

    if (applied.filterClientId) {
        return String(p.client_id || '') === String(applied.filterClientId);
    }

    return true;
}

function isArchivedVacancy(v: any): boolean {
    if (v?.data?.isClosed === true || v?.data?.archived === true) return true;
    const s = String(v?.status || v?.data?.status || v?.data?.lifecycleStatus || '')
        .toLowerCase()
        .trim();
    if (['ingevuld', 'gesloten', 'closed', 'filled', 'cancelled', 'archived', 'archief'].some((x) => s.includes(x))) return true;
    return false;
}

/** Doing / Open / Gesloten / Gepubliceerd — voor rapportage-tegels en KPI-tabel. */
function formatVacancyLiveStatusLabel(v: any): string {
    if (!v) return '—';
    if (isArchivedVacancy(v)) return 'Gesloten';
    const life = String(v?.data?.lifecycleStatus || '').toLowerCase().trim();
    if (life === 'doing') return 'Doing';
    if (v?.data?.publishedToSite === true) return 'Gepubliceerd';
    const st = String(v?.status || v?.data?.status || '').toLowerCase();
    if (['open', 'actief', 'put_live', 'live'].some((x) => st.includes(x) || life.includes(x))) return 'Open';
    return String(v?.status || v?.data?.status || 'Overig').trim() || 'Overig';
}

function vacancySearchHaystack(v: any): string {
    const parts = [
        v?.title,
        v?.vacancy_number,
        v?.data?.vacancy_number,
        v?.bullhorn_id,
        v?.data?.bullhorn_id,
        v?.id,
        v?.client_id,
        v?.opdrachtgever_id,
        v?.data?.opdrachtgever_id,
        v?.eindklant_id,
        v?.data?.eindklant_id,
    ];
    return parts.filter(Boolean).join(' ').toLowerCase();
}

function clientRecordFromId(clients: any[], id: string | undefined | null): any | null {
    const sid = String(id || '').trim();
    if (!sid) return null;
    return clients.find((c: any) => String(c.id) === sid) || null;
}

function buildReportingClientOption(id: string, clients: any[], vacancyFallback?: { name?: string }): ReportingClientOption {
    const cl = clientRecordFromId(clients, id);
    const bullhornId = String(cl?.bullhorn_id ?? cl?.data?.bullhorn_id ?? '').trim();
    return {
        id,
        name: cl?.name || vacancyFallback?.name || 'Onbekend',
        city: cl?.address_city || cl?.data?.city || cl?.city || '',
        logoUrl: String(cl?.logo_url || '').trim() || undefined,
        bullhornId: bullhornId || undefined,
    };
}

function buildReportingClientOptionFromEmbed(
    id: string,
    clients: any[],
    embed?: { id?: string; name?: string | null; logo_url?: string | null } | null,
    vacancyFallback?: { name?: string }
): ReportingClientOption {
    const cl = clientRecordFromId(clients, id);
    const bullhornId = String(cl?.bullhorn_id ?? cl?.data?.bullhorn_id ?? '').trim();
    return {
        id,
        name: embed?.name || cl?.name || vacancyFallback?.name || 'Onbekend',
        city: cl?.address_city || cl?.data?.city || cl?.city || '',
        logoUrl: String(embed?.logo_url || cl?.logo_url || '').trim() || undefined,
        bullhornId: bullhornId || undefined,
    };
}

function reportingClientFieldsForVacancy(
    v: any | null | undefined,
    clients: any[]
): Pick<KpiDrawerRow, 'opdrachtgeverName' | 'opdrachtgeverLogoUrl' | 'eindklantName' | 'eindklantLogoUrl'> {
    if (!v) {
        return {
            opdrachtgeverName: '—',
            opdrachtgeverLogoUrl: undefined,
            eindklantName: '—',
            eindklantLogoUrl: undefined,
        };
    }
    const ogId = vacancyOpdrachtgeverId(v);
    const ekId = vacancyEindklantId(v);
    const og = ogId ? buildReportingClientOptionFromEmbed(ogId, clients, v?.opdrachtgever) : null;
    const ek = ekId ? buildReportingClientOptionFromEmbed(ekId, clients, v?.eindklant) : null;
    return {
        opdrachtgeverName: og?.name || '—',
        opdrachtgeverLogoUrl: og?.logoUrl,
        eindklantName: ek?.name || '—',
        eindklantLogoUrl: ek?.logoUrl,
    };
}

function registerVacancyClientOnIndex(
    v: any,
    opdrachtgeverIds: Set<string>,
    eindklantIds: Set<string>
): void {
    const og = vacancyOpdrachtgeverId(v);
    const ek = vacancyEindklantId(v);
    if (og) opdrachtgeverIds.add(og);
    if (ek) eindklantIds.add(ek);
}

function kpiDrawerRowSearchHaystack(r: KpiDrawerRow): string {
    return [
        r.vacancy,
        r.candidate,
        r.stage,
        r.opdrachtgeverName,
        r.eindklantName,
        r.applicationLiveStatus,
        r.vacancyLiveStatus,
        r.sourceLabel,
    ]
        .filter(Boolean)
        .join(' ');
}

function clientOptionSearchHaystack(c: ReportingClientOption): string {
    return [c.name, c.city, c.bullhornId, c.id, c.bullhornId ? `#${c.bullhornId}` : ''].filter(Boolean).join(' ');
}

function formatClientOptionMeta(c: ReportingClientOption): string {
    const num = c.bullhornId ? `#${c.bullhornId}` : formatReportingEntityNum(c.id);
    return c.city ? `${num} · ${c.city}` : num;
}

function ReportingKpiClientCell({
    name,
    logoUrl,
    compact = false,
}: {
    name?: string;
    logoUrl?: string;
    compact?: boolean;
}) {
    const label = String(name || '—').trim() || '—';
    const box = compact ? 'h-4 w-4 rounded' : 'h-6 w-6 rounded-md';
    const text = compact ? 'text-[10px]' : 'text-[11px]';
    return (
        <span className={`flex min-w-0 items-center gap-1.5 ${compact ? 'max-w-[140px]' : ''}`}>
            {logoUrl ? (
                <img
                    src={logoUrl}
                    alt=""
                    className={`${box} shrink-0 border border-slate-100 object-contain bg-white`}
                />
            ) : (
                <span
                    className={`flex ${box} shrink-0 items-center justify-center border border-slate-100 bg-slate-50 text-slate-400`}
                >
                    <Building2 size={compact ? 10 : 12} />
                </span>
            )}
            <span className={`truncate font-medium text-slate-800 ${text}`} title={label}>
                {label}
            </span>
        </span>
    );
}

function vacancyDropdownMeta(v: any): { num: string; og: string; ek: string } {
    const num = String(v?.vacancy_number ?? v?.data?.vacancy_number ?? v?.bullhorn_id ?? v?.data?.bullhorn_id ?? '').trim();
    const og = vacancyOpdrachtgeverId(v);
    const ek = vacancyEindklantId(v);
    return { num, og, ek };
}

function formatVacancyFilterButtonLabel(v: any | undefined): string {
    if (!v) return 'Alle vacatures';
    const m = vacancyDropdownMeta(v);
    const bits = [m.num && `#${m.num}`, m.og && `OG:${m.og}`, m.ek && `EK:${m.ek}`].filter(Boolean);
    const base = v.title || 'Vacature';
    return bits.length ? `${base} (${bits.join(' ')})` : base;
}

/** Kort publiek label i.p.v. volledige UUID (stabiel per id). */
function formatReportingEntityNum(id: string | undefined | null): string {
    const s = String(id || '');
    if (!s) return '#0000';
    let n = 0;
    for (let i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) >>> 0;
    return `#${String(n % 10000).padStart(4, '0')}`;
}

/** Groepeer application.status voor recruiter-/detailtabellen (pipeline-kolommen = zelfde sets als donuts). */
function reportingStatusBucket(status: string | undefined): 'shortlist' | 'cvSent' | 'gesprek' | 'offer' | 'placed' | null {
    if (applicationStatusInReportingShortlist(status)) return 'shortlist';
    if (applicationStatusInReportingVoorgesteld(status)) return 'cvSent';
    if (
        applicationStatusInReportingFirstInterview(status) ||
        applicationStatusInReportingFurtherInterview(status)
    )
        return 'gesprek';
    if (applicationStatusInReportingOffer(status)) return 'offer';
    const l = String(status || '')
        .toLowerCase()
        .trim();
    if (['plaatsing', 'geplaatst', 'aangenomen', 'hired', 'placed'].some((x) => l.includes(x))) return 'placed';
    return null;
}

/**
 * Toegewezen recruiter op vacature.
 * In Werftools staat dit vrijwel altijd in `data.contactPersonId` (profiles.id), niet in `assigned_recruiter_id`.
 */
function vacancyRecruiterIdFromRow(v: any): string | null {
    if (!v) return null;
    const d = v.data ?? {};
    const raw =
        d.contactPersonId ??
        v.contactPersonId ??
        d.recruiter_id ??
        d.assignedRecruiterId ??
        v.assignedRecruiterId ??
        v.assigned_recruiter_id ??
        null;
    const s = String(raw ?? '').trim();
    return s || null;
}

function isApplicationRejected(a: any): boolean {
    const s = String(a?.status || '').toLowerCase();
    if (['rejected', 'afgewezen', 'afgekeurd', 'declined', 'withdrawn', 'teruggetrokken', 'niet gehaald'].some((x) => s.includes(x))) return true;
    if (['Rejected', 'Afgewezen'].includes(a?.status)) return true;
    const cs = String(a?.candidates?.status || '').toLowerCase();
    if (cs.includes('reject') || cs.includes('afgewezen')) return true;
    return false;
}

function funnelStageIdFromStatusBucket(b: ReturnType<typeof reportingStatusBucket>): string | null {
    if (b === 'shortlist') return 'Shortlist';
    if (b === 'cvSent') return 'Voorgesteld';
    if (b === 'gesprek') return 'Gesprek';
    if (b === 'offer') return 'Aanbod';
    if (b === 'placed') return 'Plaatsing';
    return null;
}

/** Zelfde regels als `funnelData` — gebruikt voor donuts, funnel en KPI-drawer (live). */
function appInFunnelStage(a: any, stageId: string): boolean {
    if (stageId === 'Shortlist') return applicationStatusInReportingShortlist(a.status);
    if (stageId === 'Voorgesteld') return applicationStatusInReportingVoorgesteld(a.status);
    if (stageId === 'Gesprek') {
        return (
            applicationStatusInReportingFirstInterview(a.status) ||
            applicationStatusInReportingFurtherInterview(a.status)
        );
    }
    if (stageId === 'Aanbod') return applicationStatusInReportingOffer(a.status);
    if (stageId === 'Plaatsing') {
        return (
            reportingStatusToCanonical(a.status) === 'placement' ||
            reportingStatusBucket(a.status) === 'placed'
        );
    }
    return a.status === stageId;
}

function funnelStagesFromDashboard(d: RecruitmentDashboardPayload) {
    const by = Object.fromEntries(d.funnel.stages.map((s) => [s.id, s.count])) as Record<string, number>;
    return FUNNEL_STAGES.map((stage) => {
        let value = 0;
        if (stage.id === 'Shortlist') value = (by.applied || 0) + (by.shortlisted || 0);
        else if (stage.id === 'Voorgesteld') value = by.cv_sent || 0;
        else if (stage.id === 'Gesprek') value = (by.interview_1 || 0) + (by.interview_further || 0);
        else if (stage.id === 'Aanbod') value = by.offer || 0;
        else if (stage.id === 'Plaatsing') value = by.placement || 0;
        return { ...stage, value };
    });
}

function healthScore(vacs: any[], apps: any[]): { score: number; label: string; color: string; bg: string } {
    let score = 0;
    if (vacs.length > 0) score += 25;
    if (apps.length > 0) score += 25;
    const hired = apps.filter(a => a.status === 'Plaatsing').length;
    if (hired > 0) score += 30;
    const recent = vacs.filter(v => new Date(v.created_at) > new Date(Date.now() - 30 * 86400000)).length;
    if (recent > 0) score += 20;
    if (score >= 80) return { score, label: 'Gezond', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
    if (score >= 50) return { score, label: 'Risico', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
    return { score, label: 'Churn risico', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200' };
}

function avgDays(start: string, end: string) {
    return Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000));
}

function ReportingSectionLoading({ message = 'Laden…', className = '' }: { message?: string; className?: string }) {
    return (
        <div
            className={`flex min-h-[100px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200/80 bg-slate-50/50 py-8 text-center ${className}`}
            role="status"
            aria-live="polite"
        >
            <Loader2 className="h-7 w-7 shrink-0 animate-spin text-[#e51b23]" aria-hidden />
            <p className="max-w-[220px] text-[11px] font-semibold leading-snug text-slate-500">{message}</p>
        </div>
    );
}

const Reporting: React.FC<ReportingProps> = ({ vacancies, candidates, clients, user, portalAllowedClientIds }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [applications, setApplications] = useState<any[]>([]);
    const [websiteSourceProfiles, setWebsiteSourceProfiles] = useState<CandidateClientProfileRow[]>([]);
    const [shortlistHistoryByApp, setShortlistHistoryByApp] = useState<Record<string, string>>({});
    const [shortlistInPeriodByApp, setShortlistInPeriodByApp] = useState<Record<string, string>>({});
    const [shortlistHistoryLoading, setShortlistHistoryLoading] = useState(false);
    const [recruiters, setRecruiters] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [applicationsLoadError, setApplicationsLoadError] = useState<string | null>(null);
    const [scopedPlacements, setScopedPlacements] = useState<any[]>([]);
    const [placementsLoading, setPlacementsLoading] = useState(false);
    /** Klantportaal: namen/logos alleen voor werkplekken op eigen vacatures. */
    const [portalClientsById, setPortalClientsById] = useState<Record<string, ReportingClientOption>>({});

    // Filters
    const [filterVacancyId, setFilterVacancyId] = useState('');
    const [filterClientId, setFilterClientId] = useState('');
    /** Intern: filter op vacature.eindklant_id (naast opdrachtgever = filterClientId) */
    const [filterEindklantId, setFilterEindklantId] = useState('');
    const [filterRecruiterId, setFilterRecruiterId] = useState('');
    const [vacancySearch, setVacancySearch] = useState('');
    const [vacancyOpen, setVacancyOpen] = useState(false);
    const [clientOpen, setClientOpen] = useState(false);
    const [eindklantOpen, setEindklantOpen] = useState(false);
    const [recruiterOpen, setRecruiterOpen] = useState(false);

    const [contacts, setContacts] = useState<any[]>([]);
    const [filterContactId, setFilterContactId] = useState('');
    const [contactOpen, setContactOpen] = useState(false);
    
    const [filterPeriod, setFilterPeriod] = useState('week');
    const [periodOpen, setPeriodOpen] = useState(false);
    const [customDateFrom, setCustomDateFrom] = useState('');
    const [customDateTo, setCustomDateTo] = useState('');
    const [filtersToken, setFiltersToken] = useState(0);
    const [lastAppliedSnapshot, setLastAppliedSnapshot] = useState(buildDefaultReportingFilterSnapshot);

    const [clientListSearch, setClientListSearch] = useState('');
    const [ekListSearch, setEkListSearch] = useState('');
    const [recruiterListSearch, setRecruiterListSearch] = useState('');

    /** Vacatures tab: toon alleen actief, alleen archief, of beide */
    const [filterVacancyArchive, setFilterVacancyArchive] = useState<'all' | 'active' | 'archived'>('all');

    const [branches, setBranches] = useState<ReportingBranch[]>([]);
    const [activeBranchId, setActiveBranchId] = useState('');
    const [branchMenuOpen, setBranchMenuOpen] = useState(false);

    const [dashboard, setDashboard] = useState<RecruitmentDashboardPayload | null>(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardError, setDashboardError] = useState<string | null>(null);

    const [kpiRefresh, setKpiRefresh] = useState(0);
    const [activityExtraOpen, setActivityExtraOpen] = useState(false);
    const [revenueExtraOpen, setRevenueExtraOpen] = useState(false);
    const [vacancyDetailModalId, setVacancyDetailModalId] = useState<string | null>(null);
    const [recruiterExtraById, setRecruiterExtraById] = useState<
        Record<string, { emails: number; whatsapp: number; calls: number; notes: number; system: number; statusChanges: number }>
    >({});
    const [recruiterExtraLoading, setRecruiterExtraLoading] = useState(false);
    const [kpiDrawer, setKpiDrawer] = useState<{
        open: boolean;
        title: string;
        kind: KpiDrawerKind;
        rows: KpiDrawerRow[];
        loading: boolean;
        error: string | null;
    }>({
        open: false,
        title: '',
        kind: null,
        rows: [],
        loading: false,
        error: null,
    });

    const [kpiHistoryFootCounts, setKpiHistoryFootCounts] = useState<Record<KpiHistKind, number> | null>(null);
    const [kpiDrawerSearch, setKpiDrawerSearch] = useState('');
    const [vacancyClientIndex, setVacancyClientIndex] = useState<{
        opdrachtgeverIds: string[];
        eindklantIds: string[];
        clientsById: Record<string, ReportingClientOption>;
    }>({ opdrachtgeverIds: [], eindklantIds: [], clientsById: {} });

    const kpiDrawerKindRef = useRef<KpiDrawerKind>(null);

    const isClientPortal =
        (typeof window !== 'undefined' &&
            (window.location.hostname.toLowerCase().includes('clientportal') ||
                new URLSearchParams(window.location.search).get('clientportal') === 'true')) ||
        false;

    const portalOrgScopeId = useMemo(
        () => String(user?.clientId || (user as any)?.client_id || '').trim(),
        [user?.clientId, (user as any)?.client_id]
    );

    const portalAllowedIdsSignature = useMemo(
        () =>
            (portalAllowedClientIds || [])
                .map((x) => String(x || '').trim())
                .filter((x) => isClientUuid(x))
                .sort()
                .join(','),
        [portalAllowedClientIds]
    );

    const portalScopeIds = useMemo(() => {
        const fromProp = (portalAllowedClientIds || [])
            .map((x) => String(x || '').trim())
            .filter((x) => isClientUuid(x));
        if (fromProp.length) return fromProp;
        const root = String(user?.clientId || (user as any)?.client_id || '').trim();
        return isClientUuid(root) ? [root] : [];
    }, [portalAllowedClientIds, user?.clientId, (user as any)?.client_id]);

    /** Parent `clients` + alle opdrachtgever/eindklant op vacatures (DB), voor filters en KPI-rijen. */
    const reportingClients = useMemo(() => {
        const seen = new Set<string>();
        const out: any[] = [];
        for (const c of clients) {
            const id = String(c?.id || '').trim();
            if (id && !seen.has(id)) {
                seen.add(id);
                out.push(c);
            }
        }
        if (!isClientPortal) {
            for (const opt of Object.values(vacancyClientIndex.clientsById)) {
                if (!seen.has(opt.id)) {
                    seen.add(opt.id);
                    out.push({
                        id: opt.id,
                        name: opt.name,
                        logo_url: opt.logoUrl,
                        bullhorn_id: opt.bullhornId,
                        address_city: opt.city,
                    });
                }
            }
        }
        return out;
    }, [clients, vacancyClientIndex.clientsById, isClientPortal]);

    const portalScopeIdsKey = portalScopeIds.join('|');

    /** Klantportaal: vacatures uit App, scoped op opdrachtgever_id / eindklant_id, zonder archief. */
    const reportingVacanciesBase = useMemo(() => {
        if (!isClientPortal) return vacancies;
        if (!portalScopeIds.length) return [];
        const scoped = vacancies.filter((v) => vacancyLinkedToPortalOrgSet(v, portalScopeIds));
        return filterPortalVisibleVacancies(scoped);
    }, [vacancies, isClientPortal, portalScopeIdsKey]);

    useEffect(() => {
        if (isClientPortal && filterVacancyArchive === 'all') {
            setFilterVacancyArchive('active');
        }
    }, [isClientPortal]);

    useEffect(() => {
        if (activeTab === 'companies' || activeTab === 'opdrachtgevers' || activeTab === 'werkplekken') setActiveTab('overview');
    }, [activeTab]);

    useEffect(() => {
        const refresh = () => setKpiRefresh((n) => n + 1);
        window.addEventListener('werftools-reporting-kpi-saved', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('werftools-reporting-kpi-saved', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [user?.clientId]);

    const periodCustom = useMemo(
        (): PeriodCustom | undefined =>
            filterPeriod === 'custom' && customDateFrom && customDateTo
                ? { from: customDateFrom, to: customDateTo }
                : undefined,
        [filterPeriod, customDateFrom, customDateTo]
    );

    const filterSnapshot = useMemo(
        () =>
            JSON.stringify({
                filterVacancyId,
                filterClientId,
                filterEindklantId,
                filterRecruiterId,
                filterContactId,
                filterPeriod,
                customDateFrom,
                customDateTo,
                activeBranchId,
                filterVacancyArchive,
            }),
        [
            filterVacancyId,
            filterClientId,
            filterEindklantId,
            filterRecruiterId,
            filterContactId,
            filterPeriod,
            customDateFrom,
            customDateTo,
            activeBranchId,
            filterVacancyArchive,
        ]
    );

    const filtersDirty = filterSnapshot !== lastAppliedSnapshot;
    const canApplyFilters =
        filterPeriod !== 'custom' || (Boolean(customDateFrom) && Boolean(customDateTo));

    /** Alleen toegepaste filters bepalen cijfers (draft in UI tot «Toepassen»). */
    const applied = useMemo(
        () => parseAppliedReportingFilters(lastAppliedSnapshot),
        [lastAppliedSnapshot]
    );
    const appliedPeriodCustom = useMemo(
        (): PeriodCustom | undefined =>
            applied.filterPeriod === 'custom' && applied.customDateFrom && applied.customDateTo
                ? { from: applied.customDateFrom, to: applied.customDateTo }
                : undefined,
        [applied.filterPeriod, applied.customDateFrom, applied.customDateTo]
    );

    const applyFilters = useCallback(() => {
        if (!canApplyFilters) return;
        setLastAppliedSnapshot(filterSnapshot);
        setFiltersToken((n) => n + 1);
        setVacancyOpen(false);
        setClientOpen(false);
        setEindklantOpen(false);
        setRecruiterOpen(false);
        setContactOpen(false);
        setPeriodOpen(false);
        setBranchMenuOpen(false);
    }, [canApplyFilters, filterSnapshot]);

    const effectiveFilterDays = useMemo(
        () => getEffectiveFilterDays(filterPeriod, periodCustom),
        [filterPeriod, periodCustom]
    );

    const effectiveFilterDaysApplied = useMemo(
        () => getEffectiveFilterDays(applied.filterPeriod, appliedPeriodCustom),
        [applied.filterPeriod, appliedPeriodCustom]
    );

    const kpiTargetsRaw = useMemo(
        () =>
            resolveReportingKpiTargets(user?.clientId, {
                clientId:
                    (isClientPortal
                        ? applied.filterClientId || portalOrgScopeId
                        : applied.filterClientId || applied.filterEindklantId) || null,
                recruiterId: applied.filterRecruiterId || null,
                organizationId: null,
            }),
        [
            user?.clientId,
            isClientPortal,
            applied.filterClientId,
            applied.filterEindklantId,
            applied.filterRecruiterId,
            kpiRefresh,
            portalOrgScopeId,
        ]
    );

    const kpiTargets = useMemo(
        () =>
            scaleReportingKpiTargetsToFilterWindow(
                kpiTargetsRaw,
                getReportingKpiTargetBasis(user?.clientId),
                effectiveFilterDaysApplied
            ),
        [kpiTargetsRaw, user?.clientId, effectiveFilterDaysApplied, kpiRefresh]
    );

    /**
     * Parent often passes a new `vacancies` array reference on every refresh (realtime, focus)
     * without the id-set changing. Depending on that reference retriggers the heavy applications
     * fetch and keeps KPI spinners stuck on "Cijfers laden…".
     */
    const vacancyIdsSignature = useMemo(
        () =>
            reportingVacanciesBase
                .map((v: any) => String(v?.id || '').trim())
                .filter(Boolean)
                .sort()
                .join(','),
        [reportingVacanciesBase]
    );

    const vacanciesById = useMemo(() => {
        const m = new Map<string, any>();
        for (const v of reportingVacanciesBase) {
            const id = String(v?.id || '').trim();
            if (id) m.set(id, v);
        }
        return m;
    }, [vacancyIdsSignature]);

    const reportingUserSignature = useMemo(
        () =>
            `${user?.id || ''}|${String(user?.clientId || (user as any)?.client_id || '')}|${portalAllowedIdsSignature}`,
        [user?.id, user?.clientId, (user as any)?.client_id, portalAllowedIdsSignature]
    );

    /** Same idea as vacancies: `loadReportingBranches` returns a new array on each read. */
    const reportingBranchesSignature = useMemo(
        () =>
            branches
                .map(
                    (b) =>
                        `${b.id}:${b.kind}:${Array.isArray(b.memberIds) ? [...b.memberIds].sort().join(',') : ''}`
                )
                .sort()
                .join('||'),
        [branches]
    );

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setApplicationsLoading(true);
            setApplicationsLoadError(null);
            setKpiHistoryFootCounts(null);
            try {
                const profilesRes = await supabase
                    .from('profiles')
                    .select('id,name,email,role')
                    .in('role', ['RECRUITER', 'ADMIN', 'CLIENT', 'CLIENT_RECRUITER']);

                if (!cancelled) {
                    setRecruiters(profilesRes.data || []);
                    setBranches(loadReportingBranches(user?.clientId));
                    setLoading(false);
                }

                let allApps: any[] = [];
                const appliedLoad = parseAppliedReportingFilters(lastAppliedSnapshot);
                const appliedLoadPeriodCustom: PeriodCustom | undefined =
                    appliedLoad.filterPeriod === 'custom' &&
                    appliedLoad.customDateFrom &&
                    appliedLoad.customDateTo
                        ? { from: appliedLoad.customDateFrom, to: appliedLoad.customDateTo }
                        : undefined;
                const pageSize = REPORTING_APPS_PAGE_SIZE;
                const VAC_ID_CHUNK = 40;
                const VAC_CHUNK_PARALLEL = 3;
                // Profiles: apart via fetchCandidateProfilesByCandidateIds (geen nested embed).
                const APP_SELECT_FULL =
                    '*, candidates(id,name,email,phone,ai_parsed_data,status,source), vacancies(id,title,status,client_id,data,assigned_recruiter_id,created_at,updated_at,bullhorn_id,eindklant_id,opdrachtgever_id,opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id,name,logo_url),eindklant:clients!vacancies_eindklant_id_fkey(id,name,logo_url))';
                const APP_SELECT_SLIM =
                    '*, candidates(id,name,email,phone,ai_parsed_data,status,source), vacancies(id,title,status,client_id,data,assigned_recruiter_id,created_at,updated_at,bullhorn_id,eindklant_id,opdrachtgever_id)';
                let appSelect = APP_SELECT_SLIM;
                let portalVacanciesForLoad = reportingVacanciesBase;
                if (isClientPortal && portalVacanciesForLoad.length === 0 && portalScopeIds.length) {
                    const rootId = portalScopeIds[0];
                    try {
                        const fetched = await supabase_get_vacancies(rootId, undefined, true);
                        if (fetched?.length) {
                            portalVacanciesForLoad = filterPortalVisibleVacancies(
                                fetched.filter((v: any) => vacancyLinkedToPortalOrgSet(v, portalScopeIds))
                            );
                        }
                    } catch (fetchVacErr) {
                        console.warn('[Reporting] portal vacancy fallback fetch failed', fetchVacErr);
                    }
                }

                let vacIds = vacancyIdsForReportingLoad(
                    isClientPortal ? portalVacanciesForLoad : vacancies,
                    appliedLoad,
                    isClientPortal,
                    portalScopeIds
                );
                if (appliedLoad.filterRecruiterId) {
                    const dbVacIds = await fetchVacancyIdsForRecruiter(appliedLoad.filterRecruiterId, {
                        opdrachtgeverId: isClientPortal ? undefined : appliedLoad.filterClientId || undefined,
                        eindklantId: isClientPortal ? appliedLoad.filterClientId || undefined : appliedLoad.filterEindklantId || undefined,
                    });
                    vacIds = [...new Set([...vacIds, ...dbVacIds])];
                }

                const { dateFrom: appPeriodFrom, dateTo: appPeriodTo } = getPeriodIso(
                    appliedLoad.filterPeriod,
                    appliedLoadPeriodCustom
                );
                const appPeriodOr =
                    appliedLoad.filterPeriod !== 'all'
                        ? applicationsReportingPeriodOrFilter(appPeriodFrom, appPeriodTo)
                        : null;
                const vacIdSet = new Set(vacIds.map((id) => String(id).trim()).filter(Boolean));
                const usePagedAppLoad =
                    Boolean(appPeriodOr) || vacIds.length > REPORTING_MAX_VAC_IDS_CHUNK_LOAD;

                const fetchAppsForVacancyIdChunk = async (vacChunk: string[]): Promise<Map<string, any>> => {
                    const chunkMap = new Map<string, any>();
                    let from = 0;
                    let select = appSelect;
                    while (from < 8000) {
                        let q = supabase
                            .from('applications')
                            .select(select)
                            .in('vacancy_id', vacChunk);
                        if (appPeriodOr) q = q.or(appPeriodOr);
                        let { data, error } = await q
                            .order('updated_at', { ascending: false })
                            .range(from, from + pageSize - 1);
                        if (error && select !== APP_SELECT_SLIM) {
                            select = APP_SELECT_SLIM;
                            appSelect = APP_SELECT_SLIM;
                            let q2 = supabase
                                .from('applications')
                                .select(select)
                                .in('vacancy_id', vacChunk);
                            if (appPeriodOr) q2 = q2.or(appPeriodOr);
                            ({ data, error } = await q2
                                .order('updated_at', { ascending: false })
                                .range(from, from + pageSize - 1));
                        }
                        if (error) throw error;
                        if (!data?.length) break;
                        for (const row of data) {
                            if (row?.id) chunkMap.set(String(row.id), row);
                        }
                        if (data.length < pageSize) break;
                        from += pageSize;
                    }
                    return chunkMap;
                };

                const loadAppsForVacancyChunks = async (ids: string[]) => {
                    const byId = new Map<string, any>();
                    const vacChunks: string[][] = [];
                    for (let i = 0; i < ids.length; i += VAC_ID_CHUNK) {
                        vacChunks.push(ids.slice(i, i + VAC_ID_CHUNK));
                    }
                    for (let i = 0; i < vacChunks.length; i += VAC_CHUNK_PARALLEL) {
                        const batch = vacChunks.slice(i, i + VAC_CHUNK_PARALLEL);
                        const maps = await Promise.all(batch.map((c) => fetchAppsForVacancyIdChunk(c)));
                        for (const m of maps) {
                            for (const [k, v] of m) byId.set(k, v);
                        }
                    }
                    return Array.from(byId.values());
                };

                const portalIdsForApps =
                    portalScopeIds.length > 0
                        ? portalScopeIds
                        : isClientUuid(String(user?.clientId || user?.client_id || '').trim())
                          ? [String(user?.clientId || user?.client_id || '').trim()]
                          : [];

                if (usePagedAppLoad) {
                    const paged = await fetchReportingApplicationsPaged({
                        select: appSelect,
                        periodOr: appPeriodOr,
                        vacIds: vacIds.length <= REPORTING_MAX_VAC_IDS_CHUNK_LOAD ? vacIds : undefined,
                        portalIds: isClientPortal && vacIds.length === 0 ? portalIdsForApps : undefined,
                        portalInnerVacancy: isClientPortal && vacIds.length === 0,
                    });
                    appSelect = paged.selectUsed;
                    allApps = paged.rows;
                    if (vacIdSet.size > 0) {
                        allApps = allApps.filter((a) => {
                            const vid = String(a?.vacancy_id || '').trim();
                            return vid && vacIdSet.has(vid);
                        });
                    }
                } else if (isClientPortal && vacIds.length === 0) {
                    const paged = await fetchReportingApplicationsPaged({
                        select: appSelect,
                        periodOr: appPeriodOr,
                        portalIds: portalIdsForApps,
                        portalInnerVacancy: true,
                    });
                    appSelect = paged.selectUsed;
                    allApps = paged.rows;
                } else if (vacIds.length > 0) {
                    allApps = await loadAppsForVacancyChunks(vacIds);
                } else if (appliedLoad.filterRecruiterId) {
                    const dbVacIds = await fetchVacancyIdsForRecruiter(appliedLoad.filterRecruiterId, {
                        opdrachtgeverId: isClientPortal ? undefined : appliedLoad.filterClientId || undefined,
                        eindklantId: isClientPortal ? appliedLoad.filterClientId || undefined : appliedLoad.filterEindklantId || undefined,
                    });
                    if (dbVacIds.length > 0) {
                        if (dbVacIds.length > REPORTING_MAX_VAC_IDS_CHUNK_LOAD || appPeriodOr) {
                            const paged = await fetchReportingApplicationsPaged({
                                select: appSelect,
                                periodOr: appPeriodOr,
                            });
                            appSelect = paged.selectUsed;
                            const recruiterVacSet = new Set(dbVacIds);
                            allApps = paged.rows.filter((a) =>
                                recruiterVacSet.has(String(a?.vacancy_id || '').trim())
                            );
                        } else {
                            allApps = await loadAppsForVacancyChunks(dbVacIds);
                        }
                    } else {
                        allApps = [];
                    }
                } else if (
                    appliedLoad.filterClientId ||
                    appliedLoad.filterEindklantId ||
                    appliedLoad.filterVacancyId
                ) {
                    allApps = [];
                } else {
                    const paged = await fetchReportingApplicationsPaged({
                        select: appSelect,
                        periodOr: appPeriodOr,
                    });
                    appSelect = paged.selectUsed;
                    allApps = paged.rows;
                }

                if (!cancelled) {
                    if (isClientPortal && portalScopeIds.length) {
                        allApps = allApps.filter((a) =>
                            applicationVacancyLinkedToPortalCompany(a, undefined, portalScopeIds)
                        );
                    } else if (isClientPortal) {
                        allApps = [];
                    }
                    setApplications(allApps);
                    setApplicationsLoading(false);
                    setLoading(false);

                    void (async () => {
                        try {
                            const candidateIds = [
                                ...new Set(
                                    allApps.map((a: any) => String(a?.candidate_id || '').trim()).filter(Boolean)
                                ),
                            ];
                            const profileMap = await fetchCandidateProfilesByCandidateIds(candidateIds);
                            if (cancelled) return;
                            setApplications((prev) =>
                                enrichApplicationsWithCandidateProfiles(prev, profileMap)
                            );
                        } catch (profileErr) {
                            console.warn('[Reporting] profile enrich failed', profileErr);
                        }
                        try {
                            const wp = await fetchWebsiteSourceProfiles();
                            if (!cancelled) setWebsiteSourceProfiles(wp);
                        } catch (wpErr) {
                            console.warn('[Reporting] website profiles fetch failed', wpErr);
                            if (!cancelled) setWebsiteSourceProfiles([]);
                        }
                    })();
                }
            } catch (e: any) {
                console.error('[Reporting] applications load failed', e);
                if (!cancelled) {
                    setApplications([]);
                    setApplicationsLoadError(
                        e?.message ||
                            'Sollicitaties laden mislukt (time-out of netwerk). Verklein de periode of kies een filter.'
                    );
                    setApplicationsLoading(false);
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // `vacancies` / `user` are read from the latest render; effect is keyed by stable signatures
        // so parent re-renders with new array references do not restart this fetch loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vacancyIdsSignature, reportingUserSignature, isClientPortal, filtersToken, portalScopeIdsKey, lastAppliedSnapshot]);

    useEffect(() => {
        const ev = reportingBranchesStorageEvent();
        const sync = () => setBranches(loadReportingBranches(user?.clientId));
        window.addEventListener(ev, sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener(ev, sync);
            window.removeEventListener('storage', sync);
        };
    }, [user?.clientId]);

    /** Opdrachtgever/eindklant uit reeds geladen vacatures (geen aparte 25k-vacatures-scan). */
    useEffect(() => {
        if (isClientPortal) return;
        let cancelled = false;
        (async () => {
            const og = new Set<string>();
            const ek = new Set<string>();
            for (const v of vacancies) {
                const o = vacancyOpdrachtgeverId(v);
                const e = vacancyEindklantId(v);
                if (o) og.add(o);
                if (e) ek.add(e);
            }
            const clientsById: Record<string, ReportingClientOption> = {};
            for (const c of clients) {
                const id = String(c?.id || '').trim();
                if (!id) continue;
                clientsById[id] = {
                    id,
                    name: c.name || 'Onbekend',
                    city: c.address_city || c.data?.city || '',
                    logoUrl: String(c.logo_url || '').trim() || undefined,
                    bullhornId: String(c.bullhorn_id ?? c.data?.bullhorn_id ?? '').trim() || undefined,
                };
            }
            const allIds = [...new Set([...og, ...ek])].filter((id) => !clientsById[id]);
            try {
                for (let i = 0; i < allIds.length; i += 150) {
                    const chunk = allIds.slice(i, i + 150);
                    const { data: clientRows, error: clientErr } = await supabase
                        .from('clients')
                        .select('id, name, logo_url, bullhorn_id, address_city, data')
                        .in('id', chunk);
                    if (clientErr) throw clientErr;
                    for (const row of clientRows || []) {
                        const id = String(row.id || '').trim();
                        if (!id) continue;
                        clientsById[id] = {
                            id,
                            name: row.name || 'Onbekend',
                            city: row.address_city || row.data?.city || '',
                            logoUrl: String(row.logo_url || '').trim() || undefined,
                            bullhornId: String(row.bullhorn_id ?? row.data?.bullhorn_id ?? '').trim() || undefined,
                        };
                    }
                }
            } catch (e) {
                console.warn('[Reporting] vacancy client names', e);
            }
            if (!cancelled) {
                setVacancyClientIndex({
                    opdrachtgeverIds: Array.from(og),
                    eindklantIds: Array.from(ek),
                    clientsById,
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isClientPortal, vacancies, clients]);

    const portalWerkplekEindklantIds = useMemo(
        () =>
            isClientPortal
                ? collectPortalWerkplekEindklantIds(
                      reportingVacanciesBase,
                      applications,
                      portalScopeIds,
                      vacanciesById
                  )
                : new Set<string>(),
        [
            isClientPortal,
            reportingVacanciesBase,
            applications,
            portalScopeIdsKey,
            vacancyIdsSignature,
            vacanciesById,
        ]
    );

    const portalWerkplekIdsKey = useMemo(
        () => Array.from(portalWerkplekEindklantIds).sort().join('|'),
        [portalWerkplekEindklantIds]
    );

    useEffect(() => {
        if (!isClientPortal || !filterClientId) return;
        if (!portalWerkplekEindklantIds.has(filterClientId)) {
            setFilterClientId('');
        }
    }, [isClientPortal, filterClientId, portalWerkplekIdsKey, portalWerkplekEindklantIds]);

    useEffect(() => {
        if (!isClientPortal || portalWerkplekEindklantIds.size === 0) {
            setPortalClientsById({});
            return;
        }
        let cancelled = false;
        (async () => {
            const ids = Array.from(portalWerkplekEindklantIds);
            const next: Record<string, ReportingClientOption> = {};
            try {
                for (let i = 0; i < ids.length; i += 100) {
                    const chunk = ids.slice(i, i + 100);
                    const { data, error } = await supabase
                        .from('clients')
                        .select('id, name, logo_url, bullhorn_id, address_city, data')
                        .in('id', chunk);
                    if (error) throw error;
                    for (const row of data || []) {
                        const id = String(row.id || '').trim();
                        if (!id) continue;
                        next[id] = {
                            id,
                            name: row.name || 'Onbekend',
                            city: row.address_city || row.data?.city || '',
                            logoUrl: String(row.logo_url || '').trim() || undefined,
                            bullhornId:
                                String(row.bullhorn_id ?? row.data?.bullhorn_id ?? '').trim() || undefined,
                        };
                    }
                }
            } catch (e) {
                console.warn('[Reporting] portal werkplek client names', e);
            }
            if (!cancelled) setPortalClientsById(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [isClientPortal, portalWerkplekIdsKey, portalWerkplekEindklantIds]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setDashboardLoading(true);
            setDashboardError(null);
            try {
                const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
                const periodFilters: Record<string, unknown> = {
                    dateFrom,
                    dateTo,
                    useEndClient: isClientPortal,
                };
                const branch = applied.activeBranchId
                    ? branches.find((b) => b.id === applied.activeBranchId)
                    : null;

                if (applied.filterVacancyId) periodFilters.vacancyId = applied.filterVacancyId;

                if (isClientPortal) {
                    if (portalOrgScopeId) periodFilters.clientId = portalOrgScopeId;
                    if (portalScopeIds.length) {
                        (periodFilters as Record<string, unknown>).portalLinkedClientIds = portalScopeIds;
                    }
                    if (applied.filterClientId) periodFilters.eindklantId = applied.filterClientId;
                } else {
                    if (applied.filterEindklantId) periodFilters.eindklantId = applied.filterEindklantId;
                    if (applied.filterClientId) periodFilters.clientId = applied.filterClientId;
                }

                if (branch?.kind === 'clients' && branch.memberIds.length) {
                    periodFilters.clientIds = branch.memberIds;
                } else if (applied.filterClientId && !isClientPortal && !periodFilters.clientId) {
                    periodFilters.clientId = applied.filterClientId;
                }

                if (branch?.kind === 'recruiters' && branch.memberIds.length) {
                    periodFilters.recruiterIds = branch.memberIds;
                } else if (applied.filterRecruiterId) {
                    periodFilters.recruiterId = applied.filterRecruiterId;
                }

                const secret = (import.meta as any).env?.VITE_RECRUITMENT_DASHBOARD_SECRET as string | undefined;
                const data = await fetchRecruitmentDashboard(periodFilters as any, { secret });
                if (!cancelled) setDashboard(data);
            } catch (e: any) {
                if (!cancelled) {
                    setDashboard(null);
                    setDashboardError(e?.message || 'Dashboard API niet bereikbaar');
                }
            } finally {
                if (!cancelled) setDashboardLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersToken, reportingBranchesSignature, lastAppliedSnapshot, isClientPortal, portalOrgScopeId, portalScopeIdsKey]);

    // Filtered sets (toegepaste filters — draft in UI tot «Toepassen»)
    const filteredApps = useMemo(() => {
        let r = applications;

        r = r.filter((a) => applicationInSelectedPeriod(a, applied.filterPeriod, appliedPeriodCustom));

        if (applied.activeBranchId) {
            const br = branches.find((s) => s.id === applied.activeBranchId);
            if (br?.kind === 'clients' && br.memberIds.length > 0) {
                r = r.filter((a) => applicationMatchesBranchClients(a, br.memberIds, vacanciesById));
            }
            if (br?.kind === 'recruiters' && br.memberIds.length > 0) {
                r = r.filter((a) => {
                    const vac = resolveVacancyFromApplication(a, vacanciesById);
                    const embed = vac ?? (Array.isArray(a?.vacancies) ? a.vacancies[0] : a?.vacancies);
                    const rid = vacancyRecruiterIdFromRow(embed);
                    return Boolean(rid && br.memberIds.includes(rid));
                });
            }
        }

        if (applied.filterVacancyId) {
            const sv = vacancies.find((v) => v.id === applied.filterVacancyId);
            const bhId = sv?.bullhorn_id || sv?.data?.bullhorn_id;
            r = r.filter((a) => {
                if (a.vacancy_id === applied.filterVacancyId) return true;
                if (bhId && (a.vacancies?.bullhorn_id === bhId || a.vacancies?.data?.bullhorn_id === bhId))
                    return true;
                return false;
            });
        }
        if (applied.filterClientId) {
            if (isClientPortal) {
                if (portalScopeIds.length) {
                    r = r.filter((a) =>
                        applicationMatchesPortalWerkplekFilter(
                            a,
                            applied.filterClientId,
                            portalScopeIds,
                            vacanciesById
                        )
                    );
                } else {
                    r = [];
                }
            } else {
                r = r.filter((a) =>
                    applicationMatchesOpdrachtgeverFilter(a, applied.filterClientId, vacanciesById)
                );
            }
        } else if (isClientPortal && portalScopeIds.length) {
            r = r.filter((a) => applicationVacancyLinkedToPortalCompany(a, undefined, portalScopeIds));
        } else if (isClientPortal) {
            r = [];
        }
        if (!isClientPortal && applied.filterEindklantId) {
            r = r.filter((a) =>
                applicationMatchesEindklantFilter(a, applied.filterEindklantId, vacanciesById)
            );
        }
        if (applied.filterRecruiterId) {
            r = r.filter((a) => applicationMatchesRecruiterFilter(a, applied.filterRecruiterId, vacanciesById));
        }

        return r;
    }, [
        applications,
        applied,
        appliedPeriodCustom,
        vacanciesById,
        branches,
        isClientPortal,
        portalScopeIdsKey,
    ]);

    /** Scope filters zonder periode — alleen voor stage-historiek (apps die in periode een event hadden). */
    const filteredAppsAllTime = useMemo(() => {
        let r = applications;

        if (applied.activeBranchId) {
            const br = branches.find((s) => s.id === applied.activeBranchId);
            if (br?.kind === 'clients' && br.memberIds.length > 0) {
                r = r.filter((a) => applicationMatchesBranchClients(a, br.memberIds, vacanciesById));
            }
            if (br?.kind === 'recruiters' && br.memberIds.length > 0) {
                r = r.filter((a) => {
                    const vac = resolveVacancyFromApplication(a, vacanciesById);
                    const embed = vac ?? (Array.isArray(a?.vacancies) ? a.vacancies[0] : a?.vacancies);
                    const rid = vacancyRecruiterIdFromRow(embed);
                    return Boolean(rid && br.memberIds.includes(rid));
                });
            }
        }

        if (applied.filterVacancyId) {
            const sv = vacancies.find((v) => v.id === applied.filterVacancyId);
            const bhId = sv?.bullhorn_id || sv?.data?.bullhorn_id;
            r = r.filter((a) => {
                if (a.vacancy_id === applied.filterVacancyId) return true;
                if (bhId && (a.vacancies?.bullhorn_id === bhId || a.vacancies?.data?.bullhorn_id === bhId))
                    return true;
                return false;
            });
        }

        if (applied.filterClientId) {
            if (isClientPortal) {
                if (portalScopeIds.length) {
                    r = r.filter((a) =>
                        applicationMatchesPortalWerkplekFilter(
                            a,
                            applied.filterClientId,
                            portalScopeIds,
                            vacanciesById
                        )
                    );
                } else {
                    r = [];
                }
            } else {
                r = r.filter((a) =>
                    applicationMatchesOpdrachtgeverFilter(a, applied.filterClientId, vacanciesById)
                );
            }
        } else if (isClientPortal && portalScopeIds.length) {
            r = r.filter((a) => applicationVacancyLinkedToPortalCompany(a, undefined, portalScopeIds));
        } else if (isClientPortal) {
            r = [];
        }

        if (!isClientPortal && applied.filterEindklantId) {
            r = r.filter((a) =>
                applicationMatchesEindklantFilter(a, applied.filterEindklantId, vacanciesById)
            );
        }

        if (applied.filterRecruiterId) {
            r = r.filter((a) => applicationMatchesRecruiterFilter(a, applied.filterRecruiterId, vacanciesById));
        }

        return r;
    }, [
        applications,
        applied,
        vacanciesById,
        branches,
        isClientPortal,
        portalScopeIdsKey,
    ]);

    const websiteReportingMetrics = useMemo(() => {
        const appRows = filteredApps.filter((a) => isWebsiteChannelApplication(a));
        const countedCandidates = new Set(
            appRows.map((a) => String(a.candidate_id || '').trim()).filter(Boolean)
        );
        const profileOnlyRows: CandidateClientProfileRow[] = [];

        for (const p of websiteSourceProfiles) {
            if (!profileIndicatesWebsiteChannel(p)) continue;
            if (
                !applicationInSelectedPeriod(
                    { created_at: p.created_at, updated_at: p.updated_at },
                    applied.filterPeriod,
                    appliedPeriodCustom
                )
            ) {
                continue;
            }
            if (
                !profileMatchesReportingScope(
                    p,
                    applied,
                    branches,
                    isClientPortal,
                    portalScopeIds,
                    portalWerkplekEindklantIds
                )
            )
                continue;
            const cid = String(p.candidate_id || '').trim();
            if (cid && countedCandidates.has(cid)) continue;
            if (cid) countedCandidates.add(cid);
            profileOnlyRows.push(p);
        }

        return {
            count: appRows.length + profileOnlyRows.length,
            appRows,
            profileOnlyRows,
        };
    }, [
        filteredApps,
        websiteSourceProfiles,
        applied,
        appliedPeriodCustom,
        branches,
        isClientPortal,
        portalScopeIds,
        portalWerkplekEindklantIds,
    ]);

    /**
     * Shortlist history: for apps in scope, mark apps that ever hit a shortlist-like stage.
     * Uses `application_stage_history` when available; fails open (no history) if table isn't present.
     */
    useEffect(() => {
        if (applicationsLoading) return;
        const ids = Array.from(new Set(filteredAppsAllTime.map((a) => a?.id).filter(Boolean)));
        if (ids.length === 0) {
            setShortlistHistoryByApp({});
            return;
        }
        let cancelled = false;
        (async () => {
            setShortlistHistoryLoading(true);
            try {
                const next: Record<string, string> = {};
                for (let i = 0; i < ids.length; i += 200) {
                    const chunk = ids.slice(i, i + 200);
                    const { data, error } = await supabase
                        .from('application_stage_history')
                        .select('application_id, stage, occurred_at')
                        .in('application_id', chunk)
                        .order('occurred_at', { ascending: true });
                    if (error) throw error;
                    for (const row of data || []) {
                        const stage = String((row as any).stage || '').toLowerCase();
                        if (!stage.includes('shortlist')) continue;
                        const appId = String((row as any).application_id || '');
                        const at = String((row as any).occurred_at || '');
                        if (!appId || !at) continue;
                        // Keep latest shortlist timestamp per application.
                        const prev = next[appId];
                        if (!prev || new Date(at).getTime() > new Date(prev).getTime()) next[appId] = at;
                    }
                }
                if (!cancelled) setShortlistHistoryByApp(next);
            } catch (e) {
                if (!cancelled) setShortlistHistoryByApp({});
            } finally {
                if (!cancelled) setShortlistHistoryLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [applicationsLoading, filteredAppsAllTime]);

    /** Option 3 shortlist logic: union of (apps created in period) OR (shortlist history event in period). */
    useEffect(() => {
        if (applicationsLoading) return;
        const ids = Array.from(new Set(filteredAppsAllTime.map((a) => a?.id).filter(Boolean)));
        if (ids.length === 0) {
            setShortlistInPeriodByApp({});
            return;
        }
        const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
        const fromMs = new Date(dateFrom).getTime();
        const toMs = new Date(dateTo).getTime();
        if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
            setShortlistInPeriodByApp({});
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const next: Record<string, string> = {};
                for (let i = 0; i < ids.length; i += 200) {
                    const chunk = ids.slice(i, i + 200);
                    const { data, error } = await supabase
                        .from('application_stage_history')
                        .select('application_id, stage, occurred_at')
                        .in('application_id', chunk)
                        .gte('occurred_at', dateFrom)
                        .lte('occurred_at', dateTo)
                        .order('occurred_at', { ascending: true });
                    if (error) throw error;
                    for (const row of data || []) {
                        const stage = String((row as any).stage || '').toLowerCase();
                        if (!stage.includes('shortlist')) continue;
                        const appId = String((row as any).application_id || '');
                        const at = String((row as any).occurred_at || '');
                        if (!appId || !at) continue;
                        const prev = next[appId];
                        if (!prev || new Date(at).getTime() > new Date(prev).getTime()) next[appId] = at;
                    }
                }
                if (!cancelled) setShortlistInPeriodByApp(next);
            } catch {
                if (!cancelled) setShortlistInPeriodByApp({});
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationsLoading, filteredAppsAllTime, filtersToken]);

    const scopedVacancies = useMemo(
        () =>
            applyReportingVacancyScopeFilters(
                reportingVacanciesBase,
                applied,
                branches,
                isClientPortal,
                portalScopeIds
            ),
        [reportingVacanciesBase, applied, branches, isClientPortal, portalScopeIdsKey]
    );

    /** Vacatures in scope + vacatures met sollicitaties in de gekozen periode (periode filtert apps, niet created_at van vacature). */
    const filteredVacancies = useMemo(() => {
        const byId = new Map<string, any>();
        for (const v of scopedVacancies) {
            const id = String(v?.id || '').trim();
            if (id) byId.set(id, v);
        }
        for (const a of filteredApps) {
            const vid = String(a?.vacancy_id || '').trim();
            if (!vid || byId.has(vid)) continue;
            const v = vacanciesById.get(vid);
            if (v) byId.set(vid, v);
        }
        return Array.from(byId.values());
    }, [scopedVacancies, filteredApps, vacanciesById]);

    const vacanciesCreatedInPeriod = useMemo(() => {
        if (isClientPortal) return scopedVacancies;
        return scopedVacancies.filter((v: any) =>
            createdAtInSelectedPeriod(
                v?.data?.bullhorn?.dateAdded || v.created_at,
                applied.filterPeriod,
                appliedPeriodCustom
            )
        );
    }, [scopedVacancies, applied.filterPeriod, appliedPeriodCustom, isClientPortal]);

    const closeKpiDrawer = useCallback(() => {
        kpiDrawerKindRef.current = null;
        setKpiDrawerSearch('');
        setKpiDrawer({
            open: false,
            title: '',
            kind: null,
            rows: [],
            loading: false,
            error: null,
        });
    }, []);

    const navigateToVacancy = useCallback(
        (vacancyId: string) => {
            window.dispatchEvent(new CustomEvent('open-vacancy', { detail: vacancyId }));
            closeKpiDrawer();
        },
        [closeKpiDrawer]
    );

    const navigateToCandidate = useCallback(
        (candidateId: string) => {
            window.dispatchEvent(new CustomEvent('open-candidate', { detail: candidateId }));
            closeKpiDrawer();
        },
        [closeKpiDrawer]
    );

    const buildKpiRows = useCallback(
        (
            kind:
                | 'jobsAdded'
                | 'shortlists'
                | 'cvsSent'
                | 'firstInterviews'
                | 'furtherInterviews'
                | 'offers'
                | 'rejectedApplications'
                | 'permPlacements'
                | 'websiteApplications'
        ) => {
            const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
            const from = new Date(dateFrom).getTime();
            const to = new Date(dateTo).getTime();
            const inPeriod = (iso?: string) => {
                if (!iso) return false;
                const t = new Date(iso).getTime();
                return t >= from && t <= to;
            };

            if (kind === 'jobsAdded') {
                return filteredVacancies
                    .filter((v: any) =>
                        applied.filterPeriod === 'all'
                            ? true
                            : inPeriod(v?.data?.bullhorn?.dateAdded || v.created_at)
                    )
                    .map((v: any) => ({
                        vacancy: v.title || '—',
                        stage: 'Vacature toegevoegd',
                        at: v?.data?.bullhorn?.dateAdded || v.created_at,
                        vacancyId: v.id,
                        vacancyLiveStatus: formatVacancyLiveStatusLabel(v),
                        ...reportingClientFieldsForVacancy(v, reportingClients),
                    }));
            }

            if (kind === 'websiteApplications') {
                const appRows = websiteReportingMetrics.appRows.map((a: any) => {
                    const vac =
                        resolveVacancyFromApplication(a, vacanciesById) || a.vacancies;
                    return {
                        vacancy: a.vacancies?.title || vac?.title || '—',
                        candidate: a.candidates?.name || '—',
                        stage: a.status || '—',
                        at: a.created_at,
                        vacancyId: a.vacancy_id,
                        candidateId: a.candidate_id,
                        applicationId: a.id,
                        sourceLabel: websiteChannelSourceDisplayLabel(a),
                        vacancyLiveStatus: formatVacancyLiveStatusLabel(vac),
                        applicationLiveStatus: a.status || '—',
                        statusLastChangedAt: a.updated_at || a.created_at,
                        ...reportingClientFieldsForVacancy(vac, reportingClients),
                    };
                });
                const profileRows = websiteReportingMetrics.profileOnlyRows.map((p) => {
                    const cand = p.candidates;
                    const c = Array.isArray(cand) ? cand[0] : cand;
                    return {
                        vacancy: '— (talentpool)',
                        candidate: c?.name || '—',
                        stage: p.status || '—',
                        at: p.created_at || p.updated_at,
                        candidateId: p.candidate_id,
                        sourceLabel: String(p.status || 'Website').trim(),
                        applicationLiveStatus: p.status || '—',
                        statusLastChangedAt: p.updated_at || p.created_at,
                    };
                });
                return [...appRows, ...profileRows];
            }

            const appsForLivePipelineKpi = filteredApps;
            const rows = appsForLivePipelineKpi
                .filter((a: any) => {
                    if (kind === 'shortlists') {
                        return appInFunnelStage(a, 'Shortlist');
                    }
                    if (kind === 'cvsSent') {
                        return appInFunnelStage(a, 'Voorgesteld');
                    }
                    if (kind === 'firstInterviews') return applicationStatusInReportingFirstInterview(a.status);
                    if (kind === 'furtherInterviews') return applicationStatusInReportingFurtherInterview(a.status);
                    if (kind === 'offers') return applicationStatusInReportingOffer(a.status);
                    if (kind === 'rejectedApplications') return applicationStatusInReportingRejected(a.status);
                    if (kind === 'permPlacements') return appInFunnelStage(a, 'Plaatsing');
                    return false;
                })
                .map((a: any) => {
                    const vac =
                        resolveVacancyFromApplication(a, vacanciesById) || a.vacancies;
                    return {
                        vacancy: a.vacancies?.title || vac?.title || '—',
                        candidate: a.candidates?.name || '—',
                        stage: a.status || '—',
                        at: a.updated_at || a.created_at,
                        vacancyId: a.vacancy_id,
                        candidateId: a.candidate_id,
                        applicationId: a.id,
                        vacancyLiveStatus: formatVacancyLiveStatusLabel(vac),
                        applicationLiveStatus: a.status || '—',
                        statusLastChangedAt: a.updated_at || a.created_at,
                        ...reportingClientFieldsForVacancy(vac, reportingClients),
                    };
                });

            if (kind === 'permPlacements') {
                return buildPermPlacementKpiRows(scopedPlacements, vacanciesById, reportingClients);
            }

            return rows;
        },
        [
            filteredApps,
            filteredAppsAllTime,
            filteredVacancies,
            applied.filterPeriod,
            appliedPeriodCustom,
            websiteReportingMetrics,
            reportingClients,
            vacanciesById,
            scopedPlacements,
        ]
    );

    const fetchKpiHistoryRows = useCallback(
        async (kind: Exclude<KpiDrawerKind, null | 'jobsAdded' | 'websiteApplications'>) => {
            const targetCanon = KPI_HISTORY_CANON[kind];
            const appsById = new Map<string, any>();
            for (const a of filteredAppsAllTime) {
                if (a?.id) appsById.set(String(a.id), a);
            }
            const ids = Array.from(appsById.keys());
            if (ids.length === 0) return [] as KpiDrawerRow[];

            const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
            const histFrom = applied.filterPeriod === 'all' ? '2018-01-01T00:00:00.000Z' : dateFrom;
            const histTo = applied.filterPeriod === 'all' ? new Date().toISOString() : dateTo;

            type Best = { stage: string; at: string; vacancyId?: string; candidateId?: string };
            const best = new Map<string, Best>();

            for (let i = 0; i < ids.length; i += 200) {
                const chunk = ids.slice(i, i + 200);
                const { data, error } = await supabase
                    .from('application_stage_history')
                    .select('application_id, vacancy_id, candidate_id, stage, occurred_at')
                    .in('application_id', chunk)
                    .gte('occurred_at', histFrom)
                    .lte('occurred_at', histTo)
                    .order('occurred_at', { ascending: true });
                if (error) throw error;
                for (const row of data || []) {
                    const st = String((row as any).stage || '');
                    const c = reportingStatusToCanonical(st);
                    if (c !== targetCanon) continue;
                    const appId = String((row as any).application_id || '');
                    const at = String((row as any).occurred_at || '');
                    if (!appId || !at) continue;
                    const prev = best.get(appId);
                    if (!prev || new Date(at).getTime() < new Date(prev.at).getTime()) {
                        best.set(appId, {
                            stage: st || String(targetCanon),
                            at,
                            vacancyId: (row as any).vacancy_id,
                            candidateId: (row as any).candidate_id,
                        });
                    }
                }
            }

            const rows: KpiDrawerRow[] = [];
            for (const [appId, v] of best) {
                const app = appsById.get(appId);
                const vac =
                    (app && resolveVacancyFromApplication(app, vacanciesById)) || app?.vacancies;
                rows.push({
                    vacancy: app?.vacancies?.title || vac?.title || '—',
                    candidate: app?.candidates?.name || '—',
                    stage: v.stage || String(targetCanon),
                    at: v.at,
                    vacancyId: v.vacancyId || app?.vacancy_id,
                    candidateId: v.candidateId || app?.candidate_id,
                    applicationId: appId,
                    ...reportingClientFieldsForVacancy(vac, reportingClients),
                });
            }
            rows.sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());

            const appIds = [...new Set(rows.map((r) => r.applicationId).filter(Boolean))] as string[];
            const lastHistAt = new Map<string, string>();
            for (let i = 0; i < appIds.length; i += 200) {
                const chunk = appIds.slice(i, i + 200);
                const { data: histRows, error: hErr } = await supabase
                    .from('application_stage_history')
                    .select('application_id, occurred_at')
                    .in('application_id', chunk)
                    .order('occurred_at', { ascending: false });
                if (hErr) throw hErr;
                const seen = new Set<string>();
                for (const h of histRows || []) {
                    const id = String((h as any).application_id || '');
                    if (!id || seen.has(id)) continue;
                    seen.add(id);
                    lastHistAt.set(id, String((h as any).occurred_at || ''));
                }
            }
            for (const r of rows) {
                const app = r.applicationId ? appsById.get(r.applicationId) : undefined;
                if (!app) continue;
                r.applicationLiveStatus = app.status || '—';
                r.vacancyLiveStatus = formatVacancyLiveStatusLabel(app.vacancies);
                const u = app.updated_at ? new Date(app.updated_at).getTime() : 0;
                const ht = r.applicationId ? new Date(lastHistAt.get(r.applicationId) || 0).getTime() : 0;
                const mx = Math.max(u, ht);
                r.statusLastChangedAt =
                    mx > 0 ? new Date(mx).toISOString() : String(app.updated_at || app.created_at || r.at || '');
            }
            return rows;
        },
        [filteredAppsAllTime, applied.filterPeriod, appliedPeriodCustom, reportingClients, vacanciesById]
    );

    const loadKpiHistoryFootCounts = useCallback(async (): Promise<Record<KpiHistKind, number>> => {
        const kinds = Object.keys(KPI_HISTORY_CANON) as KpiHistKind[];
        const earliest = Object.fromEntries(kinds.map((k) => [k, new Map<string, number>()])) as Record<
            KpiHistKind,
            Map<string, number>
        >;

        const appsById = new Map<string, any>();
        for (const a of filteredAppsAllTime) {
            if (a?.id) appsById.set(String(a.id), a);
        }
        const ids = Array.from(appsById.keys());
        if (ids.length === 0) return emptyKpiHistFootCounts();

        const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
        const histFrom = applied.filterPeriod === 'all' ? '2018-01-01T00:00:00.000Z' : dateFrom;
        const histTo = applied.filterPeriod === 'all' ? new Date().toISOString() : dateTo;

        for (let i = 0; i < ids.length; i += 200) {
            const chunk = ids.slice(i, i + 200);
            const { data, error } = await supabase
                .from('application_stage_history')
                .select('application_id, stage, occurred_at')
                .in('application_id', chunk)
                .gte('occurred_at', histFrom)
                .lte('occurred_at', histTo)
                .order('occurred_at', { ascending: true });
            if (error) throw error;
            for (const row of data || []) {
                const appId = String((row as any).application_id || '');
                const at = String((row as any).occurred_at || '');
                if (!appId || !at) continue;
                const atMs = new Date(at).getTime();
                const st = String((row as any).stage || '');
                const c = reportingStatusToCanonical(st);
                for (const kind of kinds) {
                    if (KPI_HISTORY_CANON[kind] !== c) continue;
                    const m = earliest[kind];
                    const prev = m.get(appId);
                    if (prev === undefined || atMs < prev) m.set(appId, atMs);
                }
            }
        }
        return kinds.reduce(
            (acc, k) => {
                acc[k] = earliest[k].size;
                return acc;
            },
            {} as Record<KpiHistKind, number>
        );
    }, [filteredAppsAllTime, applied.filterPeriod, appliedPeriodCustom]);

    useEffect(() => {
        if (applicationsLoading) return;
        let cancelled = false;
        setKpiHistoryFootCounts(null);
        void (async () => {
            try {
                const counts = await loadKpiHistoryFootCounts();
                if (!cancelled) setKpiHistoryFootCounts(counts);
            } catch {
                if (!cancelled) setKpiHistoryFootCounts(emptyKpiHistFootCounts());
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [applicationsLoading, loadKpiHistoryFootCounts, filtersToken]);

    const openKpiDrawer = useCallback(
        (title: string, rows: KpiDrawerRow[], kind?: KpiDrawerKind | null) => {
            const k = kind ?? null;
            kpiDrawerKindRef.current = k;
            setKpiDrawerSearch('');

            if (isKpiDrawerHistoryKind(k)) {
                setKpiDrawer({
                    open: true,
                    title,
                    kind: k,
                    rows: tagKpiDrawerHistoricalRows([]),
                    loading: true,
                    error: null,
                });
                void (async () => {
                    try {
                        const historyRows = await fetchKpiHistoryRows(k);
                        setKpiDrawer((s) => ({
                            ...s,
                            rows: tagKpiDrawerHistoricalRows(historyRows),
                            loading: false,
                            error: null,
                        }));
                    } catch (e: any) {
                        setKpiDrawer((s) => ({
                            ...s,
                            rows: tagKpiDrawerHistoricalRows([]),
                            loading: false,
                            error:
                                e?.message ||
                                'Kon historiek niet laden. Controleer of de tabel application_stage_history bestaat en gevuld is.',
                        }));
                    }
                })();
                return;
            }

            setKpiDrawer({
                open: true,
                title,
                kind: k,
                rows: tagKpiDrawerLiveRows(rows),
                loading: false,
                error: null,
            });
        },
        [fetchKpiHistoryRows]
    );

    /** Alleen voor tab Vacatures — «Funnel per vacature» (archief ja/nee) */
    const vacanciesForFunnelPerVacature = useMemo(() => {
        let r = filteredVacancies;
        const isArch = (v: any) => (isClientPortal ? isPortalArchivedVacancy(v) : isArchivedVacancy(v));
        const arch = applied.filterVacancyArchive;
        if (arch === 'active' || isClientPortal) {
            r = r.filter((v: any) => !isArch(v));
        } else if (arch === 'archived') {
            r = r.filter((v: any) => isArch(v));
        }
        return r;
    }, [filteredVacancies, applied.filterVacancyArchive, isClientPortal]);

    const applicationsForVacancyFunnel = useMemo(() => {
        const allowed = new Set(vacanciesForFunnelPerVacature.map((v: any) => v.id));
        return filteredApps.filter((a) => !a.vacancy_id || allowed.has(a.vacancy_id));
    }, [filteredApps, vacanciesForFunnelPerVacature]);

    const reportingVacancyIdsKey = useMemo(
        () =>
            filteredVacancies
                .map((v: any) => String(v?.id || '').trim())
                .filter(Boolean)
                .join(','),
        [filteredVacancies]
    );

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setPlacementsLoading(true);
            try {
                const vacancyIds = reportingVacancyIdsKey
                    ? reportingVacancyIdsKey.split(',')
                    : [];
                const rows = await fetchReportingScopedPlacements(supabase, {
                    filterPeriod: applied.filterPeriod,
                    periodCustom: appliedPeriodCustom,
                    opdrachtgeverId: isClientPortal ? undefined : applied.filterClientId || undefined,
                    eindklantId: isClientPortal ? undefined : applied.filterEindklantId || undefined,
                    vacancyIds,
                });
                if (!cancelled) setScopedPlacements(rows);
            } catch (e) {
                console.warn('[Reporting] fetch scoped placements failed', e);
                if (!cancelled) setScopedPlacements([]);
            } finally {
                if (!cancelled) setPlacementsLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [
        reportingVacancyIdsKey,
        applied.filterPeriod,
        appliedPeriodCustom,
        applied.filterClientId,
        applied.filterEindklantId,
        isClientPortal,
        filtersToken,
    ]);

    // Funnel data (global or filtered)
    const permPlacementKpiRows = useMemo(
        () => buildPermPlacementKpiRows(scopedPlacements, vacanciesById, reportingClients),
        [scopedPlacements, vacanciesById, reportingClients]
    );

    const placementsLoadedFromTable = !placementsLoading;
    const permPlacementsCount = useMemo(() => {
        if (placementsLoadedFromTable) return permPlacementKpiRows.length;
        if (dashboard?.kpis?.placements != null) return dashboard.kpis.placements;
        return filteredApps.filter((a) => appInFunnelStage(a, 'Plaatsing')).length;
    }, [
        permPlacementKpiRows.length,
        placementsLoadedFromTable,
        dashboard?.kpis?.placements,
        filteredApps,
    ]);

    /** Funnel + KPI-donuts: stage-historiek in periode (niet huidige pipeline-status). */
    const funnelData = useMemo(() => {
        const hist = kpiHistoryFootCounts;
        return FUNNEL_STAGES.map((stage) => ({
            ...stage,
            value:
                stage.id === 'Plaatsing'
                    ? permPlacementsCount
                    : stage.id === 'Shortlist'
                      ? hist?.shortlists ?? 0
                      : stage.id === 'Voorgesteld'
                        ? hist?.cvsSent ?? 0
                        : stage.id === 'Gesprek'
                          ? (hist?.firstInterviews ?? 0) + (hist?.furtherInterviews ?? 0)
                          : stage.id === 'Aanbod'
                            ? hist?.offers ?? 0
                            : 0,
        }));
    }, [kpiHistoryFootCounts, permPlacementsCount]);

    /** Funnel voor tab Vacatures: zelfde faselogica, alleen sollicitaties op getoonde vacatures (archieffilter). */
    const vacancyTabFunnelData = useMemo(
        () =>
            FUNNEL_STAGES.map((stage) => ({
                ...stage,
                value: applicationsForVacancyFunnel.filter((a) => appInFunnelStage(a, stage.id)).length,
            })),
        [applicationsForVacancyFunnel]
    );

    const funnelDataFromApi = useMemo(
        () => (dashboard ? funnelStagesFromDashboard(dashboard) : null),
        [dashboard]
    );
      /** Funnel + KPI-tellingen volgen gefilterde sollicitaties (zelfde periode als filter). */
    const displayFunnelData = funnelData;

    const overviewFromApi = useMemo(() => {
        if (!dashboard) return null;
        const sol = dashboard.activity.totals.new_candidates_added;
        const placements = dashboard.kpis.placements;
        return {
            activeVacs: dashboard.live.active_vacancies,
            totalApps: sol,
            conversionRate: sol > 0 ? Math.round((placements / sol) * 100) : 0,
            totalHires: placements,
            avgTimeToHire:
                dashboard.efficiency.avg_days_to_placement != null
                    ? Math.round(dashboard.efficiency.avg_days_to_placement)
                    : null,
        };
    }, [dashboard]);

    // KPIs
    const totalHires = funnelData.find(s => s.id === 'Plaatsing')?.value || 0;
    const totalApps = filteredApps.length;
    const activeVacs = filteredVacancies.filter(v => {
        const s = v.status || v.data?.status || v.data?.lifecycleStatus;
        return ['Actief', 'Put_Live', 'Open', 'Doing'].includes(s);
    }).length;
    const closedVacs = filteredVacancies.filter(v => ['Ingevuld','Gesloten'].includes(v.status || v.data?.status)).length;

    const conversionRateLegacy = totalApps > 0 ? Math.round((totalHires / totalApps) * 100) : 0;

    const conversionRate = overviewFromApi?.conversionRate ?? conversionRateLegacy;

    const avgTimeToHire = useMemo(() => {
        const placed = filteredApps.filter(a => ['Plaatsing', 'Aangenomen', 'Geplaatst'].includes(a.status) && a.created_at && a.updated_at);
        if (!placed.length) return null;
        return Math.round(placed.reduce((sum, a) => sum + avgDays(a.created_at, a.updated_at), 0) / placed.length);
    }, [filteredApps]);

    const rawKpiNumbers = useMemo(() => {
        const vacInPeriod = vacanciesCreatedInPeriod;
        const hist = kpiHistoryFootCounts;
        const firstIv = hist?.firstInterviews ?? 0;
        const furtherIv = hist?.furtherInterviews ?? 0;
        const rejected = filteredApps.filter((a: any) =>
            applicationStatusInReportingRejected(a.status)
        ).length;
        return {
            jobsAdded: vacInPeriod.length,
            shortlistsAdded: hist?.shortlists ?? 0,
            cvsSent: hist?.cvsSent ?? 0,
            firstInterviews: firstIv,
            furtherInterviews: furtherIv,
            totalInterviews: firstIv + furtherIv,
            offers: hist?.offers ?? 0,
            rejectedApplications: rejected,
            permPlacements: permPlacementsCount,
            websiteApplications: websiteReportingMetrics.count,
        };
    }, [
        vacanciesCreatedInPeriod,
        filteredApps,
        kpiHistoryFootCounts,
        permPlacementsCount,
        websiteReportingMetrics.count,
    ]);

    /** Live metrics, omzet, doorlooptijd, datakwaliteit en ratio's — uit gefilterde sollicitaties/vacatures. */
    const scopedMetrics = useMemo(
        () =>
            computeReportingScopedMetrics({
                filteredApps,
                filteredAppsAllTime: filteredApps,
                filteredVacancies,
                placementsInPeriod: scopedPlacements,
                recruiters,
                rawKpi: rawKpiNumbers,
                vacanciesById,
                getRecruiterIdFromApp: (a) => {
                    const vac = resolveVacancyFromApplication(a, vacanciesById);
                    const embed = vac ?? (Array.isArray(a?.vacancies) ? a.vacancies[0] : a?.vacancies);
                    return vacancyRecruiterIdFromRow(embed);
                },
            }),
        [
            filteredApps,
            filteredAppsAllTime,
            filteredVacancies,
            scopedPlacements,
            recruiters,
            rawKpiNumbers,
            vacanciesById,
        ],
    );

    const displayActiveVacs = applicationsLoading
        ? null
        : scopedMetrics.live.active_vacancies;
    /** Header & «sollicitaties» = gefilterde applicaties in periode. */
    const displayTotalApps = totalApps;
    const displayTotalHires =
        applicationsLoading || placementsLoading ? null : rawKpiNumbers.permPlacements;
    const displayAvgTimeToHire = applicationsLoading
        ? null
        : scopedMetrics.efficiency.avg_time_to_hire ?? avgTimeToHire;

    const vacancyStatusKpiFootLine = useMemo(() => {
        const list = filteredVacancies.filter((v: any) =>
            applied.filterPeriod === 'all'
                ? true
                : createdAtInSelectedPeriod(v.created_at, applied.filterPeriod, appliedPeriodCustom)
        );
        let live = 0;
        let historical = 0;
        for (const v of list) {
            const lab = formatVacancyLiveStatusLabel(v);
            if (lab === 'Gesloten' || lab === 'Archief') historical++;
            else live++;
        }
        return formatKpiLiveHistFootLine(live, historical);
    }, [filteredVacancies, applied.filterPeriod, appliedPeriodCustom]);

    const kpiDonutVacancyFeet = useMemo(
        () => ({
            shortlists: formatKpiHistoricalOnlyFootLine(kpiHistoryFootCounts?.shortlists ?? null),
            cvsSent: formatKpiHistoricalOnlyFootLine(kpiHistoryFootCounts?.cvsSent ?? null),
            firstInterviews: formatKpiHistoricalOnlyFootLine(kpiHistoryFootCounts?.firstInterviews ?? null),
            furtherInterviews: formatKpiHistoricalOnlyFootLine(kpiHistoryFootCounts?.furtherInterviews ?? null),
            offers: formatKpiHistoricalOnlyFootLine(kpiHistoryFootCounts?.offers ?? null),
            rejectedApplications: formatKpiLiveHistFootLine(
                buildKpiRows('rejectedApplications').length,
                null
            ),
            permPlacements: formatPermPlacementsFootLine(permPlacementsCount),
        }),
        [buildKpiRows, kpiHistoryFootCounts, permPlacementsCount]
    );

    const reportingOtherStatusCounts = useMemo(
        () => countReportingOtherStatuses(filteredApps),
        [filteredApps]
    );

    /** Donut-hoofdgetallen = historiek in gekozen periode (application_stage_history). */
    const historicalKpiDonutValue = useCallback(
        (kind: KpiHistKind) => (kpiHistoryFootCounts === null ? 0 : kpiHistoryFootCounts[kind] || 0),
        [kpiHistoryFootCounts]
    );

    const websiteApplicationsFootLine = useMemo(
        () => formatWebsiteAppsFootLine(buildKpiRows('websiteApplications').length),
        [buildKpiRows]
    );

    const actionableMetrics = useMemo(() => {
        const liveStatuses = new Set(['actief', 'put_live', 'open', 'doing', 'live', 'published', 'active']);
        const now = Date.now();
        const d30 = now - 30 * 86400000;
        const d14 = now - 14 * 86400000;
        const norm = (s: string) => String(s || '').toLowerCase();
        const liveVacs = filteredVacancies.filter((v) => liveStatuses.has(norm(v.status || v.data?.status || v.data?.lifecycleStatus)));
        const liveNoInterview30 = liveVacs.filter((v) => {
            if (new Date(v.created_at).getTime() > d30) return false;
            const appsHere = filteredApps.filter((a) => a.vacancy_id === v.id);
            return !appsHere.some((a) =>
                /gesprek|interview|2e|3e|tweede|derde|vervolg/i.test(a.status || '')
            );
        }).length;
        const liveNoCv14 = liveVacs.filter((v) => {
            if (new Date(v.created_at).getTime() > d14) return false;
            const appsHere = filteredApps.filter((a) => a.vacancy_id === v.id);
            return !appsHere.some((a) => /voorgesteld|cv|submitted|draft_cv/i.test(a.status || ''));
        }).length;
        const candWhen = (c: any) => new Date(c.added_at || c.created_at || 0).getTime();
        const newCandidatesNoJob = candidates.filter((c: any) => {
            const orphan = !filteredApps.some((a) => a.candidate_id === c.id);
            return orphan && candWhen(c) > d30 - 86400000 * 90;
        }).length;
        return {
            contactsNoCall: 0,
            liveJobsNoInterview30: liveNoInterview30,
            liveJobsNoCv14: liveNoCv14,
            newCandidatesNoJob: Math.min(newCandidatesNoJob, candidates.length),
        };
    }, [filteredVacancies, filteredApps, candidates]);

    const jobsOpenOver100Days = useMemo(() => {
        const liveStatuses = new Set(['actief', 'put_live', 'open', 'doing', 'live', 'published', 'active']);
        const norm = (s: string) => String(s || '').toLowerCase();
        return filteredVacancies.filter((v) => {
            if (!liveStatuses.has(norm(v.status || v.data?.status || v.data?.lifecycleStatus))) return false;
            const days = avgDays(v.created_at, new Date().toISOString());
            return days > 100;
        }).length;
    }, [filteredVacancies]);

    const newCompaniesInPeriod = useMemo(() => {
        const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
        return clients.filter((c: any) => {
            const t = new Date(c.created_at || c.added_at || 0).getTime();
            if (!t) return false;
            if (applied.filterPeriod === 'all') return true;
            return t >= new Date(dateFrom).getTime() && t <= new Date(dateTo).getTime();
        }).length;
    }, [clients, applied.filterPeriod, appliedPeriodCustom]);

    // Per vacature: top vacatures by conversion
    const vacancyStats = useMemo(() => {
        const map: Record<string, { title: string; total: number; hired: number; stages: Record<string, number>; clientId?: string }> = {};
        applicationsForVacancyFunnel.forEach((a) => {
            const id = a.vacancy_id;
            if (!id) return;
            if (!map[id]) map[id] = { title: a.vacancies?.title || id, total: 0, hired: 0, stages: {}, clientId: a.vacancies?.client_id };
            map[id].total++;
            const stageKey = funnelStageIdFromStatusBucket(reportingStatusBucket(a.status));
            if (stageKey) map[id].stages[stageKey] = (map[id].stages[stageKey] || 0) + 1;
            if (reportingStatusBucket(a.status) === 'placed') map[id].hired++;
        });
        return Object.entries(map)
            .map(([id, d]) => {
                const vac: any = vacancies.find((v: any) => v.id === id);
                const clientFields = reportingClientFieldsForVacancy(vac, reportingClients);
                const opdrachtgeverId = vacancyOpdrachtgeverId(vac);
                const eindklantId = vacancyEindklantId(vac);
                return {
                    id,
                    vacancy_id: id,
                    opdrachtgever_id: opdrachtgeverId || '—',
                    eindklant_id: eindklantId || '—',
                    opdrachtgeverName: clientFields.opdrachtgeverName,
                    opdrachtgeverLogo: clientFields.opdrachtgeverLogoUrl,
                    eindklantName: clientFields.eindklantName,
                    eindklantLogo: clientFields.eindklantLogoUrl,
                    ...d,
                    conv: d.total > 0 ? Math.round((d.hired / d.total) * 100) : 0,
                };
            })
            .sort((a, b) => b.total - a.total);
    }, [applicationsForVacancyFunnel, vacancies, reportingClients]);

    const filteredVacancyById = useMemo(
        () => new Map<string, any>(filteredVacancies.map((v: any) => [v.id, v])),
        [filteredVacancies]
    );

    /** Nieuwe vacatures in gekozen periode, per toegewezen recruiter */
    const recruiterNewVacancies = useMemo(() => {
        const m = new Map<string, number>();
        filteredVacancies.forEach((v: any) => {
            const r = vacancyRecruiterIdFromRow(v);
            if (!r) return;
            if (!createdAtInSelectedPeriod(v.created_at, applied.filterPeriod, appliedPeriodCustom)) return;
            m.set(r, (m.get(r) || 0) + 1);
        });
        return m;
    }, [filteredVacancies, applied.filterPeriod, appliedPeriodCustom]);

    const recruiterIdsInScope = useMemo(() => {
        const s = new Set<string>();
        filteredVacancies.forEach((v: any) => {
            const r = vacancyRecruiterIdFromRow(v);
            if (r) s.add(r);
        });
        filteredApps.forEach((a) => {
            const vac = a.vacancy_id ? filteredVacancyById.get(a.vacancy_id) : null;
            const r = vacancyRecruiterIdFromRow(vac) || vacancyRecruiterIdFromRow(a.vacancies);
            if (r) s.add(r);
        });
        return Array.from(s);
    }, [filteredVacancies, filteredApps, filteredVacancyById]);

    // Per recruiter stats (+ funnel-kolommen uit application.status)
    const recruiterStats = useMemo(() => {
        const empty = () => ({
            name: '',
            total: 0,
            hired: 0,
            vacancies: new Set<string>(),
            shortlist: 0,
            cvSent: 0,
            gesprek: 0,
            offer: 0,
            placed: 0,
        });
        const map: Record<string, ReturnType<typeof empty>> = {};
        filteredApps.forEach((a) => {
            const vac = a.vacancy_id ? filteredVacancyById.get(a.vacancy_id) : null;
            const rid = vacancyRecruiterIdFromRow(vac) || vacancyRecruiterIdFromRow(a.vacancies);
            if (!rid) return;
            const rName = recruiters.find((r) => r.id === rid)?.name || 'Onbekend';
            if (!map[rid]) {
                map[rid] = empty();
                map[rid].name = rName;
            }
            map[rid].total++;
            if (reportingStatusBucket(a.status) === 'placed') map[rid].hired++;
            if (a.vacancy_id) map[rid].vacancies.add(a.vacancy_id);
            const b = reportingStatusBucket(a.status);
            if (b === 'shortlist') map[rid].shortlist++;
            else if (b === 'cvSent') map[rid].cvSent++;
            else if (b === 'gesprek') map[rid].gesprek++;
            else if (b === 'offer') map[rid].offer++;
            else if (b === 'placed') map[rid].placed++;
        });
        return Object.entries(map)
            .map(([id, d]) => ({
                id,
                name: d.name,
                total: d.total,
                hired: d.hired,
                conv: d.total > 0 ? Math.round((d.hired / d.total) * 100) : 0,
                activeVacs: d.vacancies.size,
                shortlist: d.shortlist,
                cvSent: d.cvSent,
                gesprek: d.gesprek,
                offer: d.offer,
                placed: d.placed,
            }))
            .sort((a, b) => b.hired - a.hired);
    }, [filteredApps, recruiters, filteredVacancyById]);

    /** Vacature-eigenaren met activiteit maar zonder sollicitaties in filter krijgen alsnog een rij */
    const recruiterStatsMerged = useMemo(() => {
        const byId = new Map(recruiterStats.map((r) => [r.id, { ...r }]));
        recruiterIdsInScope.forEach((rid) => {
            if (byId.has(rid)) return;
            const rName = recruiters.find((r) => r.id === rid)?.name || 'Onbekend';
            byId.set(rid, {
                id: rid,
                name: rName,
                total: 0,
                hired: 0,
                conv: 0,
                activeVacs: filteredVacancies.filter((v: any) => vacancyRecruiterIdFromRow(v) === rid).length,
                shortlist: 0,
                cvSent: 0,
                gesprek: 0,
                offer: 0,
                placed: 0,
            });
        });
        return Array.from(byId.values()).sort((a, b) => b.total - a.total || b.hired - a.hired);
    }, [recruiterStats, recruiterIdsInScope, recruiters, filteredVacancies]);

    useEffect(() => {
        if (activeTab !== 'recruiters' || applicationsLoading) return;
        let cancelled = false;
        const run = async () => {
            setRecruiterExtraLoading(true);
            const empty = () => ({
                emails: 0,
                whatsapp: 0,
                calls: 0,
                notes: 0,
                system: 0,
                statusChanges: 0,
            });
            const ids = recruiterIdsInScope;
            if (ids.length === 0) {
                setRecruiterExtraById({});
                setRecruiterExtraLoading(false);
                return;
            }
            const agg: Record<string, ReturnType<typeof empty>> = {};
            ids.forEach((id) => {
                agg[id] = empty();
            });
            try {
                const { dateFrom, dateTo } = getPeriodIso(applied.filterPeriod, appliedPeriodCustom);
                let q = supabase.from('candidate_communications').select('user_id, type').in('user_id', ids);
                if (applied.filterPeriod !== 'all') {
                    q = q.gte('created_at', dateFrom).lte('created_at', dateTo);
                }
                const { data: commRows } = await q.limit(10000);
                for (const row of commRows || []) {
                    const uid = String((row as any).user_id || '');
                    if (!uid || !agg[uid]) continue;
                    const t = String((row as any).type || '').toLowerCase();
                    if (t.includes('whatsapp')) agg[uid].whatsapp++;
                    else if (t.includes('email')) agg[uid].emails++;
                    else if (t === 'call' || t.includes('bel') || t.includes('phone')) agg[uid].calls++;
                    else if (t === 'note') agg[uid].notes++;
                    else if (t === 'system') agg[uid].system++;
                }

                for (const rid of ids) {
                    const vacIds = filteredVacancies
                        .filter((v: any) => vacancyRecruiterIdFromRow(v) === rid)
                        .map((v: any) => v.id);
                    let sc = 0;
                    const chunkSize = 60;
                    for (let i = 0; i < vacIds.length; i += chunkSize) {
                        const chunk = vacIds.slice(i, i + chunkSize);
                        if (chunk.length === 0) continue;
                        let hq = supabase
                            .from('application_stage_history')
                            .select('id', { count: 'exact', head: true })
                            .in('vacancy_id', chunk);
                        if (applied.filterPeriod !== 'all') {
                            hq = hq.gte('occurred_at', dateFrom).lte('occurred_at', dateTo);
                        }
                        const { count } = await hq;
                        sc += count || 0;
                    }
                    if (agg[rid]) agg[rid].statusChanges = sc;
                }
                if (!cancelled) setRecruiterExtraById(agg);
            } catch {
                if (!cancelled) setRecruiterExtraById({});
            } finally {
                if (!cancelled) setRecruiterExtraLoading(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [
        activeTab,
        applicationsLoading,
        recruiterIdsInScope,
        applied.filterPeriod,
        appliedPeriodCustom,
        filtersToken,
        filteredVacancies,
    ]);

    const rejectionReport = useMemo(() => {
        const rejected = filteredApps.filter(isApplicationRejected);
        const byStatus = new Map<string, number>();
        rejected.forEach((a) => {
            const k = String(a.status || '—');
            byStatus.set(k, (byStatus.get(k) || 0) + 1);
        });
        return {
            rejected,
            byStatus: Array.from(byStatus.entries()).sort((a, b) => b[1] - a[1]),
        };
    }, [filteredApps]);

    // Per company stats (opdrachtgever, niet Werftools-tenant client_id)
    const companyStats = useMemo(() => {
        const map: Record<string, { name: string; logo?: string; total: number; hired: number; vacancies: number; appList: any[] }> = {};
        filteredApps.forEach((a) => {
            const vac = resolveVacancyFromApplication(a, vacanciesById);
            const ogId = vacancyOpdrachtgeverId(vac);
            if (!ogId) return;
            const fields = reportingClientFieldsForVacancy(vac, reportingClients);
            const name = fields.opdrachtgeverName || 'Onbekend';
            if (!map[ogId]) {
                map[ogId] = {
                    name,
                    logo: fields.opdrachtgeverLogoUrl,
                    total: 0,
                    hired: 0,
                    vacancies: 0,
                    appList: [],
                };
            }
            map[ogId].total++;
            if (reportingStatusBucket(a.status) === 'placed') map[ogId].hired++;
            map[ogId].appList.push(a);
        });

        filteredVacancies.forEach((v) => {
            const ogId = vacancyOpdrachtgeverId(v);
            if (ogId && map[ogId]) map[ogId].vacancies++;
        });

        return Object.entries(map).map(([id, d]) => {
            const vacs = filteredVacancies.filter((v) => vacancyOpdrachtgeverId(v) === id);
            const h = healthScore(vacs, d.appList);
            return {
                id,
                name: d.name,
                logo: d.logo,
                total: d.total,
                hired: d.hired,
                vacancies: d.vacancies,
                conv: d.total > 0 ? Math.round((d.hired / d.total) * 100) : 0,
                health: h,
            };
        }).sort((a, b) => b.total - a.total);
    }, [filteredApps, filteredVacancies, reportingClients, vacanciesById]);

    // Vacancies per month
    const vacPerMonth = useMemo(() => {
        const map: Record<string, number> = {};
        filteredVacancies.forEach(v => {
            if (!v.created_at) return;
            const d = new Date(v.created_at);
            const k = `${String(d.getMonth() + 1).padStart(2,'0')}/${String(d.getFullYear()).slice(2)}`;
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map).slice(-12).map(([name, value]) => ({ name, value }));
    }, [filteredVacancies]);

    // AI Insights — server engine first, then heuristics
    const aiInsights = useMemo(() => {
        const cast = (x: { type: 'warning' | 'success' | 'info'; title: string; body: string; action?: string }) => x;
        if (dashboard?.insights?.length) {
            return dashboard.insights.map((ins) =>
                cast({
                    type: (ins.metric?.includes('low') ||
                        ins.metric?.includes('drop') ||
                        ins.issue?.toLowerCase().includes('low') ||
                        ins.issue?.toLowerCase().includes('drop')
                        ? 'warning'
                        : 'info') as 'warning' | 'success' | 'info',
                    title: ins.issue,
                    body:
                        ins.suggested_action +
                        (ins.value != null && ins.threshold != null
                            ? ` (waarde ${typeof ins.value === 'number' ? Math.round(ins.value * 10) / 10 : ins.value}, drempel ${ins.threshold})`
                            : ''),
                    action: undefined,
                })
            );
        }

        const insights: { type: 'warning'|'success'|'info'; title: string; body: string; action?: string }[] = [];

        if (conversionRate < 10 && displayTotalApps > 5)
            insights.push({ type: 'warning', title: 'Lage conversie', body: `Slechts ${conversionRate}% van sollicitanten wordt geplaatst. Verbeter je vacaturetekst of selectiecriteria.`, action: 'AI Vacature optimaliseren' });

        if (avgTimeToHire && avgTimeToHire > 45)
            insights.push({ type: 'warning', title: 'Lange doorlooptijd', body: `Gemiddeld ${avgTimeToHire} dagen time-to-hire. Bottleneck kan in Gesprek of Aanbod fase liggen.`, action: 'Bekijk pipeline' });

        if (totalHires > 0 && closedVacs > 0)
            insights.push({ type: 'success', title: `${totalHires} plaatsingen ðŸŽ‰`, body: `${totalHires} kandidaten succesvol geplaatst uit ${closedVacs} afgeronde vacatures. Hires per vacature: ${(totalHires/Math.max(closedVacs,1)).toFixed(1)}.` });

        const shortlistCount = displayFunnelData.find(f => f.id === 'Shortlist')?.value || 0;
        const gesprekCount = displayFunnelData.find(f => f.id === 'Gesprek')?.value || 0;
        if (shortlistCount > 20 && gesprekCount < shortlistCount * 0.3)
            insights.push({ type: 'info', title: 'Shortlist bottleneck', body: `${shortlistCount} kandidaten staan op shortlist maar slechts ${gesprekCount} zijn in gespreksfase. Versnelling nodig.` });

        if (companyStats.filter(c => c.health.score < 50).length > 0) {
            const risk = companyStats.filter(c => c.health.score < 50);
            insights.push({ type: 'warning', title: `${risk.length} bedrijven met churn risico`, body: `${risk.map(c => c.name).slice(0,3).join(', ')} ${risk.length > 3 ? `en ${risk.length-3} meer` : ''} hebben lage activiteit.`, action: 'Bekijk bedrijven' });
        }

        if (insights.length === 0)
            insights.push({ type: 'success', title: 'Alles loopt goed!', body: 'Je metrics zien er gezond uit. Blijf de pipeline actief houden.' });

        return insights;
    }, [dashboard, conversionRate, displayTotalApps, avgTimeToHire, totalHires, closedVacs, displayFunnelData, companyStats]);

    const opdrachtgeverOptions = useMemo(() => {
        const map = new Map<string, ReportingClientOption>();
        const ogIds = new Set<string>();
        reportingVacanciesBase.forEach((v) => registerVacancyClientOnIndex(v, ogIds, new Set()));
        for (const a of applications) {
            const vac = resolveVacancyFromApplication(a, vacanciesById);
            if (vac) registerVacancyClientOnIndex(vac, ogIds, new Set());
        }
        if (!isClientPortal) {
            vacancyClientIndex.opdrachtgeverIds.forEach((id) => ogIds.add(id));
        }
        for (const sid of ogIds) {
            if (map.has(sid)) continue;
            const vac = reportingVacanciesBase.find((v) => vacancyOpdrachtgeverId(v) === sid);
            const indexed = vacancyClientIndex.clientsById[sid];
            map.set(
                sid,
                indexed ||
                    buildReportingClientOptionFromEmbed(
                        sid,
                        reportingClients,
                        vac?.opdrachtgever,
                        vac ? { name: vac?.data?.opdrachtgever?.name } : undefined
                    )
            );
        }
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'nl'));
    }, [
        reportingVacanciesBase,
        applications,
        reportingClients,
        vacanciesById,
        vacancyClientIndex.opdrachtgeverIds,
        vacancyClientIndex.clientsById,
        isClientPortal,
    ]);

    const eindklantOptions = useMemo(() => {
        const map = new Map<string, ReportingClientOption>();
        const ekIds = isClientPortal
            ? new Set(portalWerkplekEindklantIds)
            : (() => {
                  const s = new Set<string>();
                  reportingVacanciesBase.forEach((v) => registerVacancyClientOnIndex(v, new Set(), s));
                  for (const a of applications) {
                      const vac = resolveVacancyFromApplication(a, vacanciesById);
                      if (vac) registerVacancyClientOnIndex(vac, new Set(), s);
                  }
                  vacancyClientIndex.eindklantIds.forEach((id) => s.add(id));
                  return s;
              })();
        for (const sid of ekIds) {
            if (map.has(sid)) continue;
            const vac = reportingVacanciesBase.find((v) => vacancyEindklantId(v) === sid);
            const indexed = isClientPortal ? portalClientsById[sid] : vacancyClientIndex.clientsById[sid];
            map.set(
                sid,
                indexed || buildReportingClientOptionFromEmbed(sid, reportingClients, vac?.eindklant)
            );
        }
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'nl'));
    }, [
        reportingVacanciesBase,
        applications,
        reportingClients,
        vacanciesById,
        vacancyClientIndex.eindklantIds,
        vacancyClientIndex.clientsById,
        isClientPortal,
        portalWerkplekEindklantIds,
        portalClientsById,
    ]);

    const hasFilters =
        filterVacancyId ||
        filterClientId ||
        filterEindklantId ||
        filterRecruiterId ||
        filterPeriod !== 'all' ||
        activeBranchId ||
        filterVacancyArchive !== 'all' ||
        Boolean(customDateFrom || customDateTo);

    const selectedVacancy = vacancies.find(v => v.id === filterVacancyId);
    const selectedOpdrachtgever = opdrachtgeverOptions.find((c) => c.id === filterClientId);
    const selectedPortalEindklant = eindklantOptions.find((e) => e.id === filterClientId);
    const selectedInternalEindklant = eindklantOptions.find((e) => e.id === filterEindklantId);
    const selectedRecruiter = recruiters.find(r => r.id === filterRecruiterId);

    const filteredVacancyOptions = useMemo(() => {
        const q = vacancySearch.toLowerCase().trim();
        return reportingVacanciesBase
            .filter((v: any) => !q || vacancySearchHaystack(v).includes(q))
            .slice(0, 80);
    }, [reportingVacanciesBase, vacancySearch]);

    const filteredClientOptions = useMemo(() => {
        const q = clientListSearch.trim();
        const source = isClientPortal ? eindklantOptions : opdrachtgeverOptions;
        const list = source.filter((c) => !q || searchTextIncludes(clientOptionSearchHaystack(c), q));
        return (q ? list : list.slice(0, 120)).slice(0, q ? 200 : 120);
    }, [isClientPortal, eindklantOptions, opdrachtgeverOptions, clientListSearch]);

    const filteredEindklantOptions = useMemo(() => {
        const q = ekListSearch.trim();
        const list = eindklantOptions.filter((c) => !q || searchTextIncludes(clientOptionSearchHaystack(c), q));
        return (q ? list : list.slice(0, 120)).slice(0, q ? 200 : 120);
    }, [eindklantOptions, ekListSearch]);

    const filteredRecruiterOptions = useMemo(() => {
        const q = recruiterListSearch.trim();
        return recruiters
            .filter(
                (r: any) =>
                    !q || searchTextIncludes([r.name, r.email].filter(Boolean).join(' '), q)
            )
            .slice(0, 80);
    }, [recruiters, recruiterListSearch]);

    const formatRecruiterOptionLabel = (r: { name?: string; email?: string }) => {
        const name = String(r?.name || '').trim() || 'Onbekend';
        const email = String(r?.email || '').trim();
        return email ? `${name} · ${email}` : name;
    };

    /** Donuts: pas tonen als sollicitaties + KPI-historiek klaar zijn (geen «0» tijdens laden). */
    const kpiDonutsLoading =
        applicationsLoading || dashboardLoading || kpiHistoryFootCounts === null;
    const appsPipelineLoading = applicationsLoading;
    /** Onderste blokken: gefilterde sollicitaties (niet wachten op extern dashboard-API). */
    const overviewBlocksLoading = applicationsLoading;
    const overviewFunnelLoading = applicationsLoading || kpiHistoryFootCounts === null;
    const aiInsightsBlockLoading =
        applicationsLoading ? true : dashboardLoading && !dashboard?.insights?.length;

    return (
        <div className="flex h-full flex-col overflow-hidden bg-gradient-to-b from-slate-50 via-white to-red-50/20">

            {/* Top Header — z-50 so filter dropdowns stay above the dim overlay + main content */}
            <div className="relative z-50 shrink-0 overflow-visible border-b border-slate-200/60 bg-white/80 px-4 py-3 backdrop-blur-md md:px-6 lg:px-8">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#e51b23] to-red-700 shadow-sm">
                            <BarChart2 size={15} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-[15px] font-bold text-slate-900">Rapportage & Insights</h1>
                            <p className="text-[11px] text-slate-400 font-medium">
                                {applicationsLoading || dashboardLoading ? (
                                    <span className="text-slate-300">Gefilterde aantallen laden…</span>
                                ) : (
                                    <>
                                        {filteredVacancies.length} vacatures · {displayTotalApps} sollicitaties · {displayTotalHires} plaatsingen
                                    </>
                                )}
                            </p>
                            {dashboardError && (
                                <p className="text-[10px] text-amber-600 mt-0.5">{dashboardError} — fallback op lokale data.</p>
                            )}
                            {applicationsLoadError && (
                                <p className="text-[10px] text-rose-600 mt-0.5">{applicationsLoadError}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                    {(loading || dashboardLoading || applicationsLoading) && (
                        <div className="flex max-w-[280px] items-start gap-2 text-right sm:items-center">
                            <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin text-[#e51b23] sm:mt-0" aria-hidden />
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold leading-snug text-slate-600">
                                    {[
                                        loading && 'Basis & filters',
                                        applicationsLoading && 'Sollicitaties',
                                        dashboardLoading && 'KPI-dashboard',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ') || 'Bezig…'}
                                    …
                                </p>
                                <p className="text-[9px] font-medium leading-snug text-slate-400">
                                    Kan bij veel vacatures even duren.
                                </p>
                            </div>
                        </div>
                    )}
                    {hasFilters && !loading && (
                        <button onClick={() => {
                            setFilterVacancyId('');
                            setFilterClientId('');
                            setFilterEindklantId('');
                            setFilterRecruiterId('');
                            setFilterPeriod('all');
                            setCustomDateFrom('');
                            setCustomDateTo('');
                            setActiveBranchId('');
                            setFilterVacancyArchive('all');
                            setClientListSearch('');
                            setEkListSearch('');
                            setRecruiterListSearch('');
                            setVacancySearch('');
                            // Apply immediately after reset
                            setLastAppliedSnapshot(buildDefaultReportingFilterSnapshot());
                            setFiltersToken((n) => n + 1);
                        }}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors">
                            <X size={10} /> Filters wissen
                        </button>
                    )}
                    {filtersDirty && !loading && (
                        <button
                            type="button"
                            onClick={applyFilters}
                            disabled={!canApplyFilters}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-colors ${
                                canApplyFilters
                                    ? 'bg-[#e51b23] text-white hover:bg-red-700'
                                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            }`}
                            title={
                                canApplyFilters
                                    ? 'Pas filters toe en start laden'
                                    : 'Kies een geldige periode (custom: vul van+t/m in)'
                            }
                        >
                            Toepassen
                        </button>
                    )}
                    </div>
                </div>

                {/* Filter bar — full width responsive grid */}
                <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
                    {/* Vacancy */}
                    <div className="relative min-w-0">
                        <button type="button" onClick={() => { setVacancyOpen(!vacancyOpen); setClientOpen(false); setEindklantOpen(false); setRecruiterOpen(false); setContactOpen(false); setPeriodOpen(false); setBranchMenuOpen(false); }}
                            className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterVacancyId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            <Briefcase size={11} />
                            <span className="max-w-[220px] truncate">{formatVacancyFilterButtonLabel(selectedVacancy)}</span>
                            {filterVacancyId ? <X size={10} onClick={e => { e.stopPropagation(); setFilterVacancyId(''); }} /> : <ChevronDown size={10} />}
                        </button>
                        {vacancyOpen && (
                            <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                <div className="p-2 border-b border-slate-100">
                                    <input autoFocus value={vacancySearch} onChange={e => setVacancySearch(e.target.value)} placeholder="Zoek nr., ID, titel…" className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-[#e51b23]" />
                                </div>
                                <div className="max-h-52 overflow-y-auto">
                                    <button onClick={() => { setFilterVacancyId(''); setVacancyOpen(false); setVacancySearch(''); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle vacatures</button>
                                    {filteredVacancyOptions.map((v: any) => {
                                        const m = vacancyDropdownMeta(v);
                                        return (
                                        <button key={v.id} onClick={() => { setFilterVacancyId(v.id); setVacancyOpen(false); setVacancySearch(''); }}
                                            className={`w-full text-left px-3 py-2 text-[11px] hover:bg-red-50 ${filterVacancyId === v.id ? 'bg-red-50 text-red-800 font-semibold' : 'text-slate-700'}`}>
                                            <span className="truncate block">{v.title}</span>
                                            <span className="text-[10px] text-slate-400 block font-mono tabular-nums">
                                                {m.num && <>#{m.num} · </>}OG {m.og || '—'} · EK {m.ek || '—'}
                                            </span>
                                        </button>
                                    );})}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Eindklant (clientportal) of Opdrachtgever + Eindklant (intern) */}
                    {isClientPortal ? (
                        <div className="relative min-w-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setClientOpen(!clientOpen);
                                    setEindklantOpen(false);
                                    setVacancyOpen(false);
                                    setRecruiterOpen(false);
                                    setContactOpen(false);
                                    setPeriodOpen(false);
                                    setBranchMenuOpen(false);
                                }}
                                className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterClientId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                            >
                                <Building2 size={11} />
                                <span className="max-w-[180px] truncate">
                                    {selectedPortalEindklant
                                        ? `${selectedPortalEindklant.name} ${formatReportingEntityNum(selectedPortalEindklant.id)}`
                                        : 'Alle Eindklant/Werkplek'}
                                </span>
                                {filterClientId ? (
                                    <X size={10} onClick={(e) => { e.stopPropagation(); setFilterClientId(''); }} />
                                ) : (
                                    <ChevronDown size={10} />
                                )}
                            </button>
                            {clientOpen && (
                                <div className="absolute top-full left-0 mt-1 w-80 max-w-[95vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                    <div className="p-2 border-b border-slate-100">
                                        <input
                                            autoFocus
                                            value={clientListSearch}
                                            onChange={(e) => setClientListSearch(e.target.value)}
                                            placeholder="Zoek id, naam, locatie…"
                                            className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-[#e51b23]"
                                        />
                                    </div>
                                    <div className="max-h-52 overflow-y-auto">
                                        <button
                                            type="button"
                                            onClick={() => { setFilterClientId(''); setClientOpen(false); setClientListSearch(''); }}
                                            className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50"
                                        >
                                            Alle Eindklant/Werkplek
                                        </button>
                                        {filteredClientOptions.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => {
                                                    setFilterClientId(c.id);
                                                    setClientOpen(false);
                                                    setClientListSearch('');
                                                }}
                                                className={`w-full text-left px-3 py-2 text-[11px] hover:bg-red-50 ${filterClientId === c.id ? 'bg-red-50 text-red-800 font-semibold' : 'text-slate-700'}`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    {c.logoUrl ? (
                                                        <img
                                                            src={c.logoUrl}
                                                            alt=""
                                                            className="h-6 w-6 shrink-0 rounded-md border border-slate-100 object-contain bg-white"
                                                        />
                                                    ) : (
                                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-100 bg-slate-50">
                                                            <Building2 size={12} className="text-slate-400" />
                                                        </span>
                                                    )}
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate">{c.name}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono tabular-nums">
                                                            {formatClientOptionMeta(c)}
                                                        </span>
                                                    </span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="relative min-w-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setClientOpen(!clientOpen);
                                        setEindklantOpen(false);
                                        setVacancyOpen(false);
                                        setRecruiterOpen(false);
                                        setContactOpen(false);
                                        setPeriodOpen(false);
                                        setBranchMenuOpen(false);
                                    }}
                                    className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterClientId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                    title="Opdrachtgever"
                                >
                                    <Building2 size={11} />
                                    <span className="max-w-[180px] truncate">
                                        {selectedOpdrachtgever
                                            ? `${selectedOpdrachtgever.name} ${formatClientOptionMeta(selectedOpdrachtgever)}`
                                            : 'Alle opdrachtgevers'}
                                    </span>
                                    {filterClientId ? (
                                        <X size={10} onClick={(e) => { e.stopPropagation(); setFilterClientId(''); }} />
                                    ) : (
                                        <ChevronDown size={10} />
                                    )}
                                </button>
                                {clientOpen && (
                                    <div className="absolute top-full left-0 mt-1 w-80 max-w-[95vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                        <div className="p-2 border-b border-slate-100">
                                            <input
                                                autoFocus
                                                value={clientListSearch}
                                                onChange={(e) => setClientListSearch(e.target.value)}
                                                placeholder="Zoek id, naam, locatie…"
                                                className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-[#e51b23]"
                                            />
                                        </div>
                                        <div className="max-h-52 overflow-y-auto">
                                            <button
                                                type="button"
                                                onClick={() => { setFilterClientId(''); setClientOpen(false); setClientListSearch(''); }}
                                                className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50"
                                            >
                                                Alle opdrachtgevers
                                            </button>
                                            {filteredClientOptions.length === 0 ? (
                                                <p className="px-3 py-2 text-[11px] text-slate-400 italic">
                                                    Geen opdrachtgever op vacatures
                                                </p>
                                            ) : (
                                                filteredClientOptions.map((c) => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setFilterClientId(c.id);
                                                            setClientOpen(false);
                                                            setClientListSearch('');
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-red-50 ${filterClientId === c.id ? 'bg-red-50 text-red-800 font-semibold' : 'text-slate-700'}`}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            {c.logoUrl ? (
                                                                <img
                                                                    src={c.logoUrl}
                                                                    alt=""
                                                                    className="h-6 w-6 shrink-0 rounded-md border border-slate-100 object-contain bg-white"
                                                                />
                                                            ) : (
                                                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-100 bg-slate-50">
                                                                    <Building2 size={12} className="text-slate-400" />
                                                                </span>
                                                            )}
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block truncate">{c.name}</span>
                                                                <span className="text-[10px] text-slate-400 font-mono tabular-nums">
                                                                    {formatClientOptionMeta(c)}
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative min-w-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEindklantOpen(!eindklantOpen);
                                        setClientOpen(false);
                                        setVacancyOpen(false);
                                        setRecruiterOpen(false);
                                        setContactOpen(false);
                                        setPeriodOpen(false);
                                        setBranchMenuOpen(false);
                                    }}
                                    className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterEindklantId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                    title="Eindklant/Werkplek"
                                >
                                    <Building2 size={11} />
                                    <span className="max-w-[180px] truncate">
                                        {selectedInternalEindklant
                                            ? `${selectedInternalEindklant.name} ${formatClientOptionMeta(selectedInternalEindklant)}`
                                            : 'Alle Eindklant/Werkplek'}
                                    </span>
                                    {filterEindklantId ? (
                                        <X size={10} onClick={(e) => { e.stopPropagation(); setFilterEindklantId(''); }} />
                                    ) : (
                                        <ChevronDown size={10} />
                                    )}
                                </button>
                                {eindklantOpen && (
                                    <div className="absolute top-full left-0 mt-1 w-80 max-w-[95vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                        <div className="p-2 border-b border-slate-100">
                                            <input
                                                autoFocus
                                                value={ekListSearch}
                                                onChange={(e) => setEkListSearch(e.target.value)}
                                                placeholder="Zoek id, naam, locatie…"
                                                className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-[#e51b23]"
                                            />
                                        </div>
                                        <div className="max-h-52 overflow-y-auto">
                                            <button
                                                type="button"
                                                onClick={() => { setFilterEindklantId(''); setEindklantOpen(false); setEkListSearch(''); }}
                                                className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50"
                                            >
                                                Alle Eindklant/Werkplek
                                            </button>
                                            {filteredEindklantOptions.length === 0 ? (
                                                <p className="px-3 py-2 text-[11px] text-slate-400 italic">Geen eindklant op vacatures</p>
                                            ) : (
                                                filteredEindklantOptions.map((c) => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setFilterEindklantId(c.id);
                                                            setEindklantOpen(false);
                                                            setEkListSearch('');
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-red-50 ${filterEindklantId === c.id ? 'bg-red-50 text-red-800 font-semibold' : 'text-slate-700'}`}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            {c.logoUrl ? (
                                                                <img
                                                                    src={c.logoUrl}
                                                                    alt=""
                                                                    className="h-6 w-6 shrink-0 rounded-md border border-slate-100 object-contain bg-white"
                                                                />
                                                            ) : (
                                                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-100 bg-slate-50">
                                                                    <Building2 size={12} className="text-slate-400" />
                                                                </span>
                                                            )}
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block truncate">{c.name}</span>
                                                                <span className="text-[10px] text-slate-400 font-mono tabular-nums">
                                                                    {formatClientOptionMeta(c)}
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Recruiter */}
                    {!isClientPortal && (
                        <div className="relative min-w-0">
                        <button type="button" onClick={() => { setRecruiterOpen(!recruiterOpen); setVacancyOpen(false); setClientOpen(false); setEindklantOpen(false); setContactOpen(false); setPeriodOpen(false); setBranchMenuOpen(false); }}
                            className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterRecruiterId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            <User size={11} />
                            <span className="max-w-[180px] truncate">
                                {selectedRecruiter
                                    ? formatRecruiterOptionLabel(selectedRecruiter)
                                    : 'Alle recruiters'}
                            </span>
                            {filterRecruiterId ? <X size={10} onClick={e => { e.stopPropagation(); setFilterRecruiterId(''); }} /> : <ChevronDown size={10} />}
                        </button>
                        {recruiterOpen && (
                            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                <div className="p-2 border-b border-slate-100">
                                    <input
                                        autoFocus
                                        value={recruiterListSearch}
                                        onChange={(e) => setRecruiterListSearch(e.target.value)}
                                        placeholder="Zoek naam, e-mail…"
                                        className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-[#e51b23]"
                                    />
                                </div>
                                <div className="max-h-52 overflow-y-auto">
                                    <button onClick={() => { setFilterRecruiterId(''); setRecruiterOpen(false); setRecruiterListSearch(''); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle recruiters</button>
                                    {filteredRecruiterOptions.map((r: any) => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            onClick={() => {
                                                setFilterRecruiterId(r.id);
                                                setRecruiterOpen(false);
                                                setRecruiterListSearch('');
                                            }}
                                            className={`w-full text-left px-3 py-2 text-[11px] hover:bg-red-50 ${filterRecruiterId === r.id ? 'bg-red-50 text-red-800 font-semibold' : 'text-slate-700'}`}
                                        >
                                            <span className="block truncate font-medium">{r.name || 'Onbekend'}</span>
                                            {r.email ? (
                                                <span className="block truncate text-[10px] text-slate-400">{r.email}</span>
                                            ) : null}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Period Filter */}
                    <div className="relative min-w-0">
                        <button type="button" onClick={() => { setPeriodOpen(!periodOpen); setVacancyOpen(false); setClientOpen(false); setEindklantOpen(false); setRecruiterOpen(false); setContactOpen(false); setBranchMenuOpen(false); }}
                            className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${filterPeriod !== 'all' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            <Clock size={11} />
                            <span className="max-w-[280px] truncate">
                                {reportingPeriodLabel(filterPeriod)}
                                {filterPeriod !== 'custom' || (customDateFrom && customDateTo) ? (
                                    <span className="text-slate-500 font-normal">
                                        {' '}
                                        · {reportingPeriodRangeLabel(filterPeriod, periodCustom)}
                                    </span>
                                ) : null}
                            </span>
                            <ChevronDown size={10} />
                        </button>
                        {periodOpen && (
                            <div className="absolute top-full left-0 mt-1 w-64 max-h-[min(70vh,420px)] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                {(
                                    [
                                        ['today', 'Vandaag'],
                                        ['week', 'Afgelopen 7 dagen'],
                                        ['month', 'Deze maand'],
                                        ['year', 'Dit jaar'],
                                        ['all', 'Altijd'],
                                        ['custom', 'Aangepast'],
                                    ] as const
                                ).map(([id, label]) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => {
                                            setFilterPeriod(id);
                                            if (id !== 'custom') {
                                                setPeriodOpen(false);
                                            }
                                        }}
                                        className={`w-full text-left px-3 py-2 text-[11px] ${
                                            filterPeriod === id
                                                ? 'bg-red-50 text-red-800 font-semibold'
                                                : 'text-slate-700 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="block">{label}</span>
                                        <span className="block text-[10px] font-normal text-slate-500 mt-0.5">
                                            {reportingPeriodRangeLabel(
                                                id,
                                                id === 'custom' ? periodCustom : undefined
                                            )}
                                        </span>
                                    </button>
                                ))}
                                {filterPeriod === 'custom' && (
                                    <div className="border-t border-slate-100 p-2 space-y-2 bg-slate-50/80">
                                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Van / tot</p>
                                        <input
                                            type="date"
                                            value={customDateFrom}
                                            onChange={(e) => setCustomDateFrom(e.target.value)}
                                            className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white"
                                        />
                                        <input
                                            type="date"
                                            value={customDateTo}
                                            onChange={(e) => setCustomDateTo(e.target.value)}
                                            className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => applyFilters()}
                                            disabled={!canApplyFilters}
                                            className={`w-full py-1.5 rounded-lg text-[11px] font-bold ${
                                                canApplyFilters
                                                    ? 'bg-[#e51b23] text-white hover:bg-red-700'
                                                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                            }`}
                                        >
                                            Toepassen
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Branches (reporting-only bundles — beheer in KPI-doelen) */}
                    <div className="relative min-w-0">
                        <button type="button" onClick={() => { setBranchMenuOpen(!branchMenuOpen); setVacancyOpen(false); setClientOpen(false); setEindklantOpen(false); setRecruiterOpen(false); setContactOpen(false); setPeriodOpen(false); }}
                            className={`flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${activeBranchId ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            <GitBranch size={11} />
                            <span className="max-w-[130px] truncate">{activeBranchId ? branches.find(s => s.id === activeBranchId)?.name : 'Branches'}</span>
                            {activeBranchId ? <X size={10} onClick={e => { e.stopPropagation(); setActiveBranchId(''); }} /> : <ChevronDown size={10} />}
                        </button>
                        {branchMenuOpen && (
                            <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
                                <div className="p-2 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase">Branches</span>
                                </div>
                                <div className="max-h-52 overflow-y-auto">
                                    {branches.length === 0 ? (
                                        <p className="text-[11px] text-slate-400 p-3 italic">Nog geen branches. Stel ze in bij Rapportage → KPI-doelen.</p>
                                    ) : (
                                        branches.map(s => (
                                            <div key={s.id} className="flex items-center justify-between hover:bg-red-50 group">
                                                <button type="button" onClick={() => { setActiveBranchId(s.id); setBranchMenuOpen(false); }}
                                                    className={`flex-1 text-left px-3 py-2 text-[11px] ${activeBranchId === s.id ? 'text-red-800 font-semibold' : 'text-slate-700'}`}>
                                                    {s.name}{' '}
                                                    <span className="text-[9px] text-slate-400 ml-1">
                                                        ({s.kind === 'clients' ? `${s.memberIds.length} bedrijven` : `${s.memberIds.length} recruiters`})
                                                    </span>
                                                </button>
                                                <button type="button" onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!confirm(`Branch "${s.name}" verwijderen?`)) return;
                                                    deleteReportingBranch(user?.clientId, s.id);
                                                    setBranches(loadReportingBranches(user?.clientId));
                                                    if (activeBranchId === s.id) setActiveBranchId('');
                                                }} className="px-3 text-slate-300 hover:text-red-500 hidden group-hover:block"><Trash2 size={12}/></button>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="p-2 border-t border-slate-100">
                                    <button type="button" onClick={() => {
                                        const name = prompt('Naam voor branch (bedrijven uit huidige weergave):');
                                        if (name) {
                                            const cIds = Array.from(
                                                new Set(
                                                    filteredVacancies
                                                        .flatMap((v) => [
                                                            vacancyOpdrachtgeverId(v),
                                                            vacancyEindklantId(v),
                                                        ])
                                                        .filter(Boolean)
                                                )
                                            );
                                            if (cIds.length === 0) { alert('Geen bedrijven in huidige weergave.'); return; }
                                            addReportingBranch(user?.clientId, { name, kind: 'clients', memberIds: cIds as string[] });
                                            setBranches(loadReportingBranches(user?.clientId));
                                            setBranchMenuOpen(false);
                                        }
                                    }} className="w-full text-center py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold rounded-lg transition-colors">
                                        Huidige weergave als branch (bedrijven)
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>                {/* Tabs */}
                <div className="flex items-center gap-0 mt-3 border-b border-slate-100 -mb-3">
                    {TABS.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-semibold border-b-2 transition-all ${activeTab === tab.id ? 'border-[#e51b23] text-red-800' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                                <Icon size={13} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Click-outside overlay */}
            {(vacancyOpen || clientOpen || eindklantOpen || recruiterOpen || contactOpen || periodOpen || branchMenuOpen) && (
                <div className="fixed inset-0 z-30 bg-transparent" onClick={() => { setVacancyOpen(false); setClientOpen(false); setEindklantOpen(false); setRecruiterOpen(false); setContactOpen(false); setPeriodOpen(false); setBranchMenuOpen(false); }} />
            )}

            {/* Content */}
            <div className="relative z-0 min-h-0 flex-1 overflow-y-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
                    <>
                        {/* ===== TAB: OVERZICHT ===== */}
                        {activeTab === 'overview' && (
                            <div className="space-y-8">
                                {/* Primary KPI grid — targets from KPI-doelen settings */}
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4">
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('Vacatures toegevoegd', buildKpiRows('jobsAdded'), 'jobsAdded')}>
                                        <DonutKpiCard
                                            label="Vacatures toegevoegd"
                                            value={rawKpiNumbers.jobsAdded}
                                            target={kpiTargets.jobsAdded}
                                            loading={kpiDonutsLoading}
                                            footLine={vacancyStatusKpiFootLine}
                                        />
                                    </button>
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('Shortlists', [], 'shortlists')}>
                                        <DonutKpiCard
                                            label="Shortlists"
                                            value={historicalKpiDonutValue('shortlists')}
                                            target={kpiTargets.shortlistsAdded}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.shortlists}
                                        />
                                    </button>
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('Voorgesteld', [], 'cvsSent')}>
                                        <DonutKpiCard
                                            label="Voorgesteld"
                                            value={historicalKpiDonutValue('cvsSent')}
                                            target={kpiTargets.cvsSent}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.cvsSent}
                                        />
                                    </button>
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('1e gesprekken', [], 'firstInterviews')}>
                                        <DonutKpiCard
                                            label="1e gesprekken"
                                            value={historicalKpiDonutValue('firstInterviews')}
                                            target={kpiTargets.firstInterviews}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.firstInterviews}
                                        />
                                    </button>
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('Vervolg gesprekken', [], 'furtherInterviews')}>
                                        <DonutKpiCard
                                            label="Vervolg gesprekken"
                                            value={historicalKpiDonutValue('furtherInterviews')}
                                            target={kpiTargets.furtherInterviews}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.furtherInterviews}
                                        />
                                    </button>
                                    <button type="button" className="text-left" onClick={() => openKpiDrawer('Aanbiedingen', [], 'offers')}>
                                        <DonutKpiCard
                                            label="Aanbiedingen"
                                            value={historicalKpiDonutValue('offers')}
                                            target={kpiTargets.offers}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.offers}
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() =>
                                            openKpiDrawer(
                                                'Afgewezen',
                                                buildKpiRows('rejectedApplications'),
                                                'rejectedApplications'
                                            )
                                        }
                                    >
                                        <DonutKpiCard
                                            label="Afgewezen"
                                            value={rawKpiNumbers.rejectedApplications}
                                            target={kpiTargets.rejectedApplications ?? 0}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.rejectedApplications}
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() =>
                                            openKpiDrawer(
                                                'Perm plaatsingen',
                                                permPlacementKpiRows,
                                                'permPlacements'
                                            )
                                        }
                                    >
                                        <DonutKpiCard
                                            label="Perm plaatsingen"
                                            value={rawKpiNumbers.permPlacements}
                                            target={kpiTargets.permPlacements}
                                            loading={kpiDonutsLoading}
                                            footLine={kpiDonutVacancyFeet.permPlacements}
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() => openKpiDrawer('Website sollicitaties', buildKpiRows('websiteApplications'), 'websiteApplications')}
                                    >
                                        <DonutKpiCard
                                            label="Website sollicitaties"
                                            value={rawKpiNumbers.websiteApplications}
                                            target={kpiTargets.websiteApplications}
                                            loading={kpiDonutsLoading}
                                            footLine={websiteApplicationsFootLine}
                                        />
                                    </button>
                                </div>

                                {reportingOtherStatusCounts.length > 0 && (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm">
                                        <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                                            Overige statussen
                                        </h2>
                                        <p className="mt-1 text-[10px] text-slate-400">
                                            Live telling op application.status binnen gekozen periode (created_at of updated_at).
                                        </p>
                                        <ul className="mt-3 space-y-1.5">
                                            {reportingOtherStatusCounts.map(({ label, count }) => (
                                                <li
                                                    key={label}
                                                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2"
                                                >
                                                    <span className="text-[11px] font-semibold text-slate-700">{label}</span>
                                                    <span className="text-[12px] font-black tabular-nums text-slate-900">
                                                        {count.toLocaleString('nl-NL')}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                )}

                                {/* Actionable insights */}
                                <section className="rounded-2xl border border-red-100/80 bg-gradient-to-br from-white via-red-50/25 to-red-50/30 p-5 shadow-xl shadow-red-100/40 backdrop-blur-md">
                                    <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Actiepunten</h2>
                                    {appsPipelineLoading ? (
                                        <ReportingSectionLoading message="Sollicitaties laden voor actiepunten…" />
                                    ) : (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {[
                                                ['Nieuwe contactpersonen zonder call', actionableMetrics.contactsNoCall],
                                                ['Live vacatures >30d zonder gesprek', actionableMetrics.liveJobsNoInterview30],
                                                ['Live vacatures >14d zonder CV verstuurd', actionableMetrics.liveJobsNoCv14],
                                                ['Nieuwe kandidaten nog niet voorgesteld', actionableMetrics.newCandidatesNoJob],
                                            ].map(([label, val]) => (
                                                <div key={String(label)} className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                                    <span className="text-[12px] font-semibold leading-snug text-slate-700">{label}</span>
                                                    <span className="text-xl font-black tabular-nums text-[#e51b23]">{val}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                {/* Live metrics */}
                                {overviewBlocksLoading ? (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Live metrics</h2>
                                        <ReportingSectionLoading message="Live cijfers voor huidige filters laden…" />
                                    </section>
                                ) : (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Live metrics</h2>
                                        <p className="mb-3 text-[10px] font-medium text-slate-400">
                                            Huidige pipeline binnen toegepaste filters (niet beperkt tot rapportageperiode).
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                            {[
                                                ['In interview-fase', scopedMetrics.live.candidates_in_interview],
                                                ['Live vacatures', scopedMetrics.live.active_vacancies],
                                                ['Pipeline', scopedMetrics.live.candidates_in_pipeline],
                                                ['Wacht op feedback', scopedMetrics.live.candidates_awaiting_feedback],
                                            ].map(([label, val]) => (
                                                <div key={String(label)} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-3 text-center">
                                                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
                                                    <p className="mt-1 text-2xl font-black text-slate-900">{val}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Activity summary */}
                                <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                    <div className="mb-4 flex items-center justify-between gap-2">
                                        <h2 className="text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Activiteit</h2>
                                        <button
                                            type="button"
                                            onClick={() => setActivityExtraOpen(!activityExtraOpen)}
                                            disabled={overviewBlocksLoading}
                                            className="text-[11px] font-bold text-[#e51b23] hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            {activityExtraOpen ? 'Minder' : 'Meer'}
                                        </button>
                                    </div>
                                    {overviewBlocksLoading ? (
                                        <ReportingSectionLoading message="Activiteit voor huidige filters laden…" />
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="flex justify-between rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-2.5 text-[13px] font-semibold text-slate-800">
                                                <span>Sollicitaties (periode, gefilterd)</span>
                                                <span className="font-black text-[#e51b23]">{displayTotalApps}</span>
                                            </div>
                                            <div className="flex justify-between rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-2.5 text-[13px] font-semibold text-slate-800">
                                                <span>Nieuwe bedrijven</span>
                                                <span className="font-black text-[#e51b23]">
                                                    {newCompaniesInPeriod} / {kpiTargets.newCompanies ?? DEFAULT_REPORTING_KPI_TARGETS.newCompanies}
                                                </span>
                                            </div>
                                            <div className="flex justify-between rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-2.5 text-[13px] font-semibold text-slate-800">
                                                <span>Vacatures toegevoegd / doel</span>
                                                <span className="font-black text-[#e51b23]">
                                                    {rawKpiNumbers.jobsAdded} / {kpiTargets.jobsAdded}
                                                </span>
                                            </div>
                                            {activityExtraOpen && scopedMetrics.activity.by_recruiter.length > 0 && (
                                                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                                                    <p className="text-[10px] font-bold uppercase text-slate-400">Top recruiters (activiteit)</p>
                                                    {scopedMetrics.activity.by_recruiter.slice(0, 8).map((r) => (
                                                        <div key={r.recruiter_id} className="flex justify-between text-[12px] text-slate-700">
                                                            <span className="truncate pr-2">{r.name}</span>
                                                            <span className="shrink-0 font-bold">{r.activity_score ?? 0}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </section>

                                {/* Revenue */}
                                {overviewBlocksLoading ? (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <div className="mb-4 flex items-center justify-between">
                                            <h2 className="text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Omzet</h2>
                                        </div>
                                        <ReportingSectionLoading message="Omzet voor huidige filters laden…" />
                                    </section>
                                ) : (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <div className="mb-4 flex items-center justify-between">
                                            <h2 className="text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Omzet</h2>
                                            <button
                                                type="button"
                                                onClick={() => setRevenueExtraOpen(!revenueExtraOpen)}
                                                className="text-[11px] font-bold text-[#e51b23] hover:underline"
                                            >
                                                {revenueExtraOpen ? 'Minder' : 'Meer'}
                                            </button>
                                        </div>
                                        <p className="mb-3 text-[10px] font-medium text-slate-400">
                                            Perm plaatsingen = rijen in `placements` (created_at in periode, filter opdrachtgever/eindklant). Omzet uit fee op die plaatsingen.
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                            <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase text-emerald-700">Perm plaatsingen</p>
                                                <p className="text-xl font-black text-emerald-900">
                                                    {scopedMetrics.revenue.placements_count} / {kpiTargets.permPlacements}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase text-slate-500">Omzet (perm)</p>
                                                <p className="text-xl font-black text-slate-900">
                                                    €{scopedMetrics.revenue.revenue_generated.toLocaleString('nl-NL')}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase text-slate-500">Gem. per plaatsing</p>
                                                <p className="text-xl font-black text-slate-900">
                                                    {scopedMetrics.revenue.avg_revenue_per_placement != null
                                                        ? `€${scopedMetrics.revenue.avg_revenue_per_placement.toLocaleString('nl-NL')}`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase text-slate-500">Contract (placeholder)</p>
                                                <p className="text-xl font-black text-slate-400">—</p>
                                            </div>
                                        </div>
                                        {revenueExtraOpen && (
                                            <p className="mt-3 text-[11px] text-slate-500">
                                                Omzet is een som van fee/placement_fee op geplaatste kandidaten binnen filter en periode.
                                            </p>
                                        )}
                                    </section>
                                )}

                                {/* Process efficiency */}
                                {overviewBlocksLoading ? (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Proces — doorlooptijd</h2>
                                        <ReportingSectionLoading message="Doorlooptijden voor huidige filters laden…" />
                                    </section>
                                ) : (
                                    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Proces — doorlooptijd</h2>
                                        <p className="mb-3 text-[10px] font-medium text-slate-400">
                                            Gemiddelden op sollicitaties in de gekozen periode en filters.
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                            <div className="rounded-xl border border-red-100 bg-red-50/50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase tracking-wide text-red-800">Gem. dagen tot 1e gesprek</p>
                                                <p className="mt-1 text-lg font-black text-red-950">
                                                    {scopedMetrics.efficiency.avg_days_to_first_interview != null
                                                        ? `${scopedMetrics.efficiency.avg_days_to_first_interview} d`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-red-100 bg-red-50/50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase tracking-wide text-red-800">Gem. dagen tot plaatsing</p>
                                                <p className="mt-1 text-lg font-black text-red-950">
                                                    {scopedMetrics.efficiency.avg_days_to_placement != null
                                                        ? `${scopedMetrics.efficiency.avg_days_to_placement} d`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-red-100 bg-red-50/50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase tracking-wide text-red-800">Gem. dagen tussen fases</p>
                                                <p className="mt-1 text-lg font-black text-red-950">
                                                    {scopedMetrics.efficiency.avg_days_between_stages != null
                                                        ? `${scopedMetrics.efficiency.avg_days_between_stages} d`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div className="rounded-xl border border-red-100 bg-red-50/50 px-3 py-3">
                                                <p className="text-[10px] font-bold uppercase tracking-wide text-red-800">Time-to-hire (snapshot)</p>
                                                <p className="mt-1 text-lg font-black text-red-950">
                                                    {displayAvgTimeToHire != null ? `${displayAvgTimeToHire} d` : '—'}
                                                </p>
                                            </div>
                                        </div>
                                    </section>
                                )}

                                {/* Data quality */}
                                {overviewBlocksLoading ? (
                                    <section className="rounded-2xl border border-amber-200/60 bg-amber-50/30 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-amber-800">Datakwaliteit</h2>
                                        <ReportingSectionLoading message="Datakwaliteit voor huidige filters laden…" />
                                    </section>
                                ) : (
                                    <section className="rounded-2xl border border-amber-200/60 bg-amber-50/30 p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-amber-800">Datakwaliteit</h2>
                                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                            <div className="flex justify-between rounded-lg border border-white/80 bg-white/90 px-4 py-2 text-[13px] font-semibold">
                                                <span>Zonder CV / kernvelden</span>
                                                <span className="font-black text-amber-900">
                                                    {scopedMetrics.data_quality.candidates_missing_cv +
                                                        scopedMetrics.data_quality.candidates_missing_email_or_phone}
                                                </span>
                                            </div>
                                            <div className="flex justify-between rounded-lg border border-white/80 bg-white/90 px-4 py-2 text-[13px] font-semibold">
                                                <span>Dubbele e-mails (scope)</span>
                                                <span className="font-black text-amber-900">
                                                    {scopedMetrics.data_quality.duplicate_emails_detected}
                                                </span>
                                            </div>
                                            <div className="flex justify-between rounded-lg border border-white/80 bg-white/90 px-4 py-2 text-[13px] font-semibold">
                                                <span>
                                                    Inactief (&gt; {scopedMetrics.data_quality.candidates_idle_days.threshold_days}{' '}
                                                    dagen)
                                                </span>
                                                <span className="font-black text-amber-900">
                                                    {scopedMetrics.data_quality.candidates_idle_days.count}
                                                </span>
                                            </div>
                                            <div className="flex justify-between rounded-lg border border-white/80 bg-white/90 px-4 py-2 text-[13px] font-semibold">
                                                <span>Vacatures open &gt; 100 dagen</span>
                                                <span className="font-black text-amber-900">{jobsOpenOver100Days}</span>
                                            </div>
                                        </div>
                                    </section>
                                )}

                                {/* Ratios */}
                                {overviewBlocksLoading ? (
                                    <section className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Ratio&apos;s</h2>
                                        <ReportingSectionLoading message="Ratio's voor huidige filters laden…" />
                                    </section>
                                ) : (
                                    <section className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-5 shadow-lg backdrop-blur-sm">
                                        <h2 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-[#e51b23]">Ratio&apos;s</h2>
                                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                                            {(
                                                [
                                                    ['jobs_to_shortlists', 'Vacatures → shortlists'],
                                                    ['shortlists_to_cv_sent', 'Shortlists → CV'],
                                                    ['cv_sent_to_interview', 'CV → gesprek'],
                                                    ['interview_to_offer', 'Gesprek → aanbod'],
                                                    ['offer_to_placement', 'Aanbod → plaatsing'],
                                                ] as const
                                            ).map(([key, label]) => (
                                                <div
                                                    key={key}
                                                    className="rounded-xl border border-slate-100 bg-white px-3 py-4 text-center shadow-sm transition hover:border-red-200 hover:shadow-md"
                                                >
                                                    <p className="text-[9px] font-bold uppercase leading-tight text-slate-500">{label}</p>
                                                    <p className="mt-2 font-mono text-lg font-black text-slate-900">
                                                        {scopedMetrics.ratios[key]?.ratio ?? '—'}
                                                    </p>
                                                    <p className="text-[10px] font-semibold text-[#e51b23]">
                                                        {scopedMetrics.ratios[key]?.pct != null
                                                            ? `${scopedMetrics.ratios[key]?.pct}%`
                                                            : ''}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* AI Insights */}
                                <div className="rounded-2xl border border-red-200/80 bg-gradient-to-br from-red-50/90 via-white to-red-50/40 p-5 shadow-xl backdrop-blur-md">
                                    <div className="mb-4 flex items-center gap-2">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#e51b23] to-red-700 shadow-lg shadow-red-500/20">
                                            <Brain size={16} className="text-white" />
                                        </div>
                                        <h3 className="text-sm font-black tracking-tight text-red-950">AI-insights</h3>
                                    </div>
                                    {aiInsightsBlockLoading ? (
                                        <ReportingSectionLoading message="Insights berekenen…" className="min-h-[140px]" />
                                    ) : (
                                    <div className="space-y-2">
                                        {aiInsights.map((ins, i) => (
                                            <div
                                                key={i}
                                                className={`flex items-start gap-3 rounded-xl border p-3 ${
                                                    ins.type === 'warning'
                                                        ? 'border-amber-200 bg-amber-50/90'
                                                        : ins.type === 'success'
                                                          ? 'border-emerald-200 bg-emerald-50/90'
                                                          : 'border-blue-200 bg-blue-50/90'
                                                }`}
                                            >
                                                <div
                                                    className={`mt-0.5 shrink-0 ${
                                                        ins.type === 'warning'
                                                            ? 'text-amber-500'
                                                            : ins.type === 'success'
                                                              ? 'text-emerald-500'
                                                              : 'text-blue-500'
                                                    }`}
                                                >
                                                    {ins.type === 'warning' ? (
                                                        <AlertTriangle size={16} />
                                                    ) : ins.type === 'success' ? (
                                                        <CheckCircle2 size={16} />
                                                    ) : (
                                                        <AlertCircle size={16} />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[13px] font-bold text-slate-900">{ins.title}</p>
                                                    <p className="mt-0.5 text-[12px] leading-relaxed text-slate-600">{ins.body}</p>
                                                    {ins.action && (
                                                        <button
                                                            type="button"
                                                            className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold ${
                                                                ins.type === 'warning' ? 'text-amber-700' : 'text-emerald-700'
                                                            }`}
                                                        >
                                                            {ins.action} <ArrowRight size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    )}
                                </div>

                                {/* Funnel + trend */}
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <Card title="Recruitment funnel" icon={<Target size={12} />}>
                                        {overviewFunnelLoading ? (
                                            <ReportingSectionLoading message="Funneldata laden (dashboard + sollicitaties)…" className="mt-3" />
                                        ) : (
                                        <div className="mt-3 space-y-2">
                                            {displayFunnelData.map((stage, i) => {
                                                const prev = i > 0 ? displayFunnelData[i - 1].value : stage.value;
                                                const conv = prev > 0 ? Math.round((stage.value / prev) * 100) : 0;
                                                const pctOfTotal =
                                                    displayFunnelData[0].value > 0
                                                        ? Math.round((stage.value / displayFunnelData[0].value) * 100)
                                                        : 0;
                                                return (
                                                    <div key={stage.id}>
                                                        <div className="mb-1 flex items-center justify-between">
                                                            <span className="text-[11px] font-semibold text-slate-700">{stage.label}</span>
                                                            <div className="flex items-center gap-3">
                                                                {i > 0 && (
                                                                    <span
                                                                        className={`text-[10px] font-semibold ${conv < 30 ? 'text-rose-500' : conv < 60 ? 'text-amber-500' : 'text-emerald-600'}`}
                                                                    >
                                                                        {conv}% conv.
                                                                    </span>
                                                                )}
                                                                <span className="w-8 text-right text-[12px] font-bold text-slate-800">{stage.value}</span>
                                                            </div>
                                                        </div>
                                                        <div className="relative h-5 overflow-hidden rounded-lg bg-slate-100">
                                                            <div
                                                                className="h-full rounded-lg transition-all duration-700"
                                                                style={{
                                                                    width: `${Math.max(pctOfTotal, stage.value > 0 ? 5 : 0)}%`,
                                                                    backgroundColor: stage.color,
                                                                }}
                                                            />
                                                            {stage.value === 0 && (
                                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-medium text-slate-400">
                                                                    Geen data
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        )}
                                    </Card>

                                    <Card title="Vacatures per maand" icon={<TrendingUp size={12} />}>
                                        {vacPerMonth.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={vacPerMonth} barSize={18}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                                    <YAxis hide />
                                                    <Tooltip
                                                        contentStyle={{
                                                            borderRadius: '12px',
                                                            fontSize: 11,
                                                            border: 'none',
                                                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                                        }}
                                                        cursor={{ fill: '#f8fafc' }}
                                                    />
                                                    <Bar dataKey="value" fill="#e51b23" radius={[6, 6, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Empty />
                                        )}
                                    </Card>
                                </div>

                                {appsPipelineLoading ? (
                                    <Card title="Bedrijven — health" icon={<Shield size={12} />}>
                                        <ReportingSectionLoading
                                            message="Bedrijfsstatistieken laden…"
                                            className="mt-3 border-0 bg-transparent"
                                        />
                                    </Card>
                                ) : (
                                    companyStats.length > 0 && (
                                    <Card title="Bedrijven — health" icon={<Shield size={12} />}>
                                        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                                            {companyStats.slice(0, 6).map((c) => (
                                                <div key={c.id} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${c.health.bg}`}>
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        {c.logo ? (
                                                            <img src={c.logo} className="h-5 w-5 shrink-0 rounded object-contain" alt="" />
                                                        ) : (
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-200 text-[8px] font-bold text-slate-500">
                                                                {c.name[0]}
                                                            </div>
                                                        )}
                                                        <span className="truncate text-[12px] font-semibold text-slate-800">{c.name}</span>
                                                    </div>
                                                    <div className="ml-2 flex shrink-0 items-center gap-2">
                                                        <span className="text-[10px] font-medium text-slate-500">{c.hired} geplaatst</span>
                                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${c.health.bg} ${c.health.color}`}>
                                                            {c.health.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                    )
                                )}
                            </div>
                        )}

                        {/* ===== TAB: VACATURES ===== */}
                        {activeTab === 'vacancies' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <KpiCard label="Totaal vacatures" value={filteredVacancies.length} icon={<Briefcase size={13}/>} color="indigo" loading={applicationsLoading} />
                                    <KpiCard label="Actief" value={displayActiveVacs} icon={<Flame size={13}/>} color="emerald" loading={applicationsLoading} />
                                    <KpiCard label="Ingevuld" value={closedVacs} icon={<CheckCircle2 size={13}/>} color="slate" loading={applicationsLoading} />
                                    <KpiCard label="Avg. time-to-hire" value={displayAvgTimeToHire !== null && displayAvgTimeToHire !== undefined ? `${displayAvgTimeToHire}d` : '—'} icon={<Clock size={13}/>} color="amber" raw loading={applicationsLoading} />
                                </div>

                                {!isClientPortal && (
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2 text-[11px] text-slate-600">
                                        <Archive size={14} className="text-slate-400 shrink-0" />
                                        <span className="font-medium text-slate-500">Archief</span>
                                        <select
                                            value={filterVacancyArchive}
                                            onChange={(e) => {
                                                const val = e.target.value as 'all' | 'active' | 'archived';
                                                setFilterVacancyArchive(val);
                                                const p = parseAppliedReportingFilters(lastAppliedSnapshot);
                                                setLastAppliedSnapshot(
                                                    JSON.stringify({ ...p, filterVacancyArchive: val })
                                                );
                                            }}
                                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 outline-none focus:border-[#e51b23]"
                                        >
                                            <option value="all">Alles (actief + archief)</option>
                                            <option value="active">Alleen niet-archief</option>
                                            <option value="archived">Alleen archief</option>
                                        </select>
                                    </div>
                                </div>
                                )}

                                <Card title="Funnel per vacature" icon={<Target size={12}/>}>
                                    {applicationsLoading ? (
                                        <ReportingSectionLoading message="Funnel per vacature laden…" className="mt-3 border-0 bg-transparent" />
                                    ) : vacancyStats.length === 0 ? (
                                        <Empty />
                                    ) : (
                                        <div className="overflow-x-auto mt-2">
                                            <table className="w-full text-left">
                                                <thead>
                                                    <tr className="border-b border-slate-100">
                                                        <th className="pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Vacature</th>
                                                        <th className="pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider min-w-[140px]">Opdrachtgever</th>
                                                        <th className="pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider min-w-[140px]">Eindklant / Werkplek</th>
                                                        {FUNNEL_STAGES.map(s => (
                                                            <th key={s.id} className="pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center px-2">{s.label}</th>
                                                        ))}
                                                        <th className="pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Conversie</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {vacancyStats.map((v) => (
                                                        <tr
                                                            key={v.id}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => setVacancyDetailModalId(v.vacancy_id)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    setVacancyDetailModalId(v.vacancy_id);
                                                                }
                                                            }}
                                                            className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer"
                                                        >
                                                            <td className="py-2.5 pr-4">
                                                                <p className="text-[12px] font-semibold text-slate-800 truncate max-w-[220px]">{v.title}</p>
                                                                <p className="text-[10px] text-slate-400">{v.total} sollicitaties</p>
                                                            </td>
                                                            <td className="py-2.5 pr-3">
                                                                <div className="flex min-w-0 max-w-[180px] items-center gap-2">
                                                                    {v.opdrachtgeverLogo ? (
                                                                        <img src={v.opdrachtgeverLogo} alt="" className="h-7 w-7 shrink-0 rounded-lg border border-slate-100 object-contain bg-white" />
                                                                    ) : (
                                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[9px] font-bold text-slate-500">
                                                                            {(v.opdrachtgeverName || '?').charAt(0)}
                                                                        </div>
                                                                    )}
                                                                    <span className="truncate text-[11px] font-medium text-slate-700">{v.opdrachtgeverName}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-2.5 pr-3">
                                                                <div className="flex min-w-0 max-w-[180px] items-center gap-2">
                                                                    {v.eindklantLogo ? (
                                                                        <img src={v.eindklantLogo} alt="" className="h-7 w-7 shrink-0 rounded-lg border border-slate-100 object-contain bg-white" />
                                                                    ) : (
                                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[9px] font-bold text-slate-500">
                                                                            {(v.eindklantName || '?').charAt(0)}
                                                                        </div>
                                                                    )}
                                                                    <span className="truncate text-[11px] font-medium text-slate-700">{v.eindklantName}</span>
                                                                </div>
                                                            </td>
                                                            {FUNNEL_STAGES.map(s => (
                                                                <td key={s.id} className="py-2.5 px-2 text-center">
                                                                    <span className={`text-[12px] font-bold ${(v.stages[s.id] || 0) > 0 ? 'text-slate-800' : 'text-slate-200'}`}>
                                                                        {v.stages[s.id] || 0}
                                                                    </span>
                                                                </td>
                                                            ))}
                                                            <td className="py-2.5 text-right">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${v.conv >= 20 ? 'bg-emerald-50 text-emerald-700' : v.conv >= 5 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-600'}`}>
                                                                    {v.conv}%
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Card>

                                {/* Bottleneck analysis — zelfde filters als tabel (sollicitaties op geselecteerde vacatures / periode) */}
                                <Card title="Drop-off analyse per fase" icon={<AlertTriangle size={12}/>}>
                                    {applicationsLoading ? (
                                        <ReportingSectionLoading message="Funnel laden voor drop-off…" className="mt-3 border-0 bg-transparent" />
                                    ) : (
                                    <div className="space-y-3 mt-2">
                                        {FUNNEL_STAGES.slice(0,-1).map((stage, i) => {
                                            const curr = vacancyTabFunnelData[i].value;
                                            const next = vacancyTabFunnelData[i+1].value;
                                            const dropoff = curr > 0 ? Math.round(((curr - next) / curr) * 100) : 0;
                                            return (
                                                <div key={stage.id} className="flex items-center gap-3">
                                                    <div className="w-36 shrink-0">
                                                        <span className="text-[11px] font-semibold text-slate-700">{stage.label}</span>
                                                        <ArrowRight size={10} className="inline ml-1 text-slate-400" />
                                                        <span className="text-[11px] font-semibold text-slate-700 ml-1">{FUNNEL_STAGES[i+1].label}</span>
                                                    </div>
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full transition-all" style={{ width: `${100 - dropoff}%`, backgroundColor: dropoff > 70 ? '#ef4444' : dropoff > 40 ? '#f59e0b' : '#10b981' }} />
                                                    </div>
                                                    <span className={`text-[11px] font-bold w-16 text-right ${dropoff > 70 ? 'text-rose-600' : dropoff > 40 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                        {dropoff}% drop
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 w-20 text-right tabular-nums">{curr} → {next}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    )}
                                </Card>

                                {vacancyDetailModalId && (
                                    <VacancyDetailModal
                                        vacancyId={vacancyDetailModalId}
                                        currentUser={user}
                                        onClose={() => setVacancyDetailModalId(null)}
                                    />
                                )}
                            </div>
                        )}

                        {/* ===== TAB: RECRUITERS ===== */}
                        {activeTab === 'recruiters' && (
                            <div className="space-y-4">
                                {applicationsLoading ? (
                                    <ReportingSectionLoading message="Recruiterstatistieken laden…" className="min-h-[200px] rounded-2xl border border-slate-100 bg-white shadow-sm" />
                                ) : recruiterStatsMerged.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                        <User size={28} className="mb-2 opacity-20" />
                                        <p className="text-sm font-medium">Geen recruiter data beschikbaar</p>
                                        <p className="text-[11px] text-slate-300 mt-1">Wijs recruiters toe aan vacatures (kolom of vacaturedata) om data te zien.</p>
                                    </div>
                                ) : (
                                    <>
                                        <Card title="Activiteit per recruiter" icon={<Users size={12} />}>
                                            <p className="mt-1 text-[10px] text-slate-400">
                                                Pipeline = sollicitaties in deze filters/periode. Recruiter = toegewezen op de vacature. Mail/WhatsApp/bel/notities uit kandidaat-communicatie met jouw user-id; statuswijzigingen =
                                                events op jouw vacatures (tabel application_stage_history).{' '}
                                                {recruiterExtraLoading ? <span className="font-semibold text-indigo-600">Activiteit laden…</span> : null}
                                            </p>
                                            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-100">
                                                <table className="w-full min-w-[1100px] text-left text-[11px]">
                                                    <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                                                        <tr>
                                                            <th className="px-3 py-2">Recruiter</th>
                                                            <th className="px-3 py-2 text-center" title="Nieuwe vacatures in gekozen periode">
                                                                Nieuwe vac.
                                                            </th>
                                                            <th className="px-3 py-2 text-center">Vac&apos;s</th>
                                                            <th className="px-3 py-2 text-center">Sollic.</th>
                                                            <th className="px-3 py-2 text-center">Shortlist</th>
                                                            <th className="px-3 py-2 text-center">Voorgesteld</th>
                                                            <th className="px-3 py-2 text-center">Gesprek</th>
                                                            <th className="px-3 py-2 text-center">Aanbod</th>
                                                            <th className="px-3 py-2 text-center">Geplaatst</th>
                                                            <th className="px-3 py-2 text-center">
                                                                <span className="inline-flex items-center gap-0.5">
                                                                    <Mail size={10} /> Mail
                                                                </span>
                                                            </th>
                                                            <th className="px-3 py-2 text-center">
                                                                <span className="inline-flex items-center gap-0.5">
                                                                    <MessageCircle size={10} /> App
                                                                </span>
                                                            </th>
                                                            <th className="px-3 py-2 text-center">
                                                                <span className="inline-flex items-center gap-0.5">
                                                                    <Phone size={10} /> Bel
                                                                </span>
                                                            </th>
                                                            <th className="px-3 py-2 text-center">Notities</th>
                                                            <th className="px-3 py-2 text-center">Status Î”</th>
                                                            <th className="px-3 py-2 text-right">Conv.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {recruiterStatsMerged.map((r) => {
                                                            const ex = recruiterExtraById[r.id] || {
                                                                emails: 0,
                                                                whatsapp: 0,
                                                                calls: 0,
                                                                notes: 0,
                                                                system: 0,
                                                                statusChanges: 0,
                                                            };
                                                            return (
                                                                <tr key={r.id} className="hover:bg-slate-50/80">
                                                                    <td className="px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">
                                                                        {recruiterNewVacancies.get(r.id) ?? 0}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{r.activeVacs}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums font-bold text-slate-800">{r.total}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{r.shortlist}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{r.cvSent}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{r.gesprek}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{r.offer}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-emerald-700 font-bold">{r.hired}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{ex.emails}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{ex.whatsapp}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{ex.calls}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-slate-600">{ex.notes}</td>
                                                                    <td className="px-3 py-2 text-center tabular-nums text-indigo-700 font-semibold">{ex.statusChanges}</td>
                                                                    <td className="px-3 py-2 text-right font-bold text-[#e51b23]">{r.conv}%</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Card>

                                        <Card title="Recruiter Leaderboard" icon={<Award size={12}/>}>
                                            <div className="space-y-2 mt-2">
                                                {recruiterStatsMerged.map((r, i) => (
                                                    <div key={r.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${i === 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}>
                                                        <div className="flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold shrink-0" style={{ backgroundColor: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#c2845a' : '#e2e8f0', color: i <= 2 ? 'white' : '#64748b' }}>
                                                            {i + 1}
                                                        </div>
                                                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center text-[11px] font-bold text-[#e51b23] shrink-0">
                                                            {r.name.charAt(0)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[12px] font-bold text-slate-800">{r.name}</p>
                                                            <p className="text-[10px] text-slate-400">{r.activeVacs} vacatures · {r.total} sollicitaties</p>
                                                        </div>
                                                        <div className="flex items-center gap-4 shrink-0">
                                                            <div className="text-center">
                                                                <p className="text-[16px] font-black text-emerald-600">{r.hired}</p>
                                                                <p className="text-[9px] text-slate-400 uppercase tracking-wider">Geplaatst</p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className={`text-[16px] font-black ${r.conv >= 15 ? 'text-[#e51b23]' : r.conv >= 5 ? 'text-amber-600' : 'text-rose-500'}`}>{r.conv}%</p>
                                                                <p className="text-[9px] text-slate-400 uppercase tracking-wider">Conversie</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Card>

                                        <Card title="Plaatsingen per recruiter" icon={<BarChart2 size={12}/>}>
                                            {recruiterStatsMerged.filter(r => r.hired > 0).length > 0 ? (
                                                <ResponsiveContainer width="100%" height={200}>
                                                    <BarChart data={recruiterStatsMerged.filter(r => r.total > 0).slice(0, 10)} barSize={24}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                                        <YAxis hide />
                                                        <Tooltip contentStyle={{ borderRadius: '8px', fontSize: 11, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} cursor={{ fill: '#f8fafc' }} />
                                                        <Bar dataKey="hired" name="Plaatsingen" fill="#10b981" radius={[3,3,0,0]} />
                                                        <Bar dataKey="total" name="Sollicitaties" fill="#e2e8f0" radius={[3,3,0,0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : <Empty label="Nog geen plaatsingen geregistreerd" />}
                                        </Card>
                                    </>
                                )}
                            </div>
                        )}

                        {/* ===== TAB: REJECTIES ===== */}
                        {activeTab === 'rejections' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    <KpiCard label="Totaal afwijzingen" value={rejectionReport.rejected.length} icon={<AlertTriangle size={13}/>} color="rose" loading={applicationsLoading} />
                                    <KpiCard
                                        label="Afgewezen op shortlist-fase"
                                        value={
                                            rejectionReport.rejected.filter((a) =>
                                                reportingStatusBucket(a.status) === 'shortlist'
                                            ).length
                                        }
                                        icon={<AlertCircle size={13}/>}
                                        color="amber"
                                        loading={applicationsLoading}
                                    />
                                    <KpiCard label="Conversie naar plaatsing (overall)" value={`${conversionRate}%`} icon={<Target size={13}/>} color="indigo" raw loading={applicationsLoading} />
                                </div>

                                <Card title="Afwijzingen per application.status" icon={<Filter size={12} />}>
                                    {applicationsLoading ? (
                                        <ReportingSectionLoading message="Statussen laden…" className="mt-3 border-0 bg-transparent" />
                                    ) : rejectionReport.byStatus.length === 0 ? (
                                        <Empty label="Geen afwijzingen in deze selectie" />
                                    ) : (
                                        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-100">
                                            <table className="w-full min-w-[320px] text-left text-[11px]">
                                                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                                                    <tr>
                                                        <th className="px-3 py-2">Status</th>
                                                        <th className="px-3 py-2 text-right">Aantal</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-50">
                                                    {rejectionReport.byStatus.map(([status, count]) => (
                                                        <tr key={status} className="hover:bg-slate-50/80">
                                                            <td className="px-3 py-2 font-medium text-slate-800">{status}</td>
                                                            <td className="px-3 py-2 text-right tabular-nums font-bold text-rose-600">{count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Card>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    <Card title="Afwijsredenen" icon={<AlertTriangle size={12}/>}>
                                        {applicationsLoading ? (
                                            <ReportingSectionLoading message="Afwijzingen laden…" className="mt-3 border-0 bg-transparent" />
                                        ) : (
                                        <div className="space-y-2 mt-2">
                                            {(() => {
                                                const reasonCounts: Record<string, number> = {};
                                                rejectionReport.rejected.forEach((a) => {
                                                    const reason =
                                                        a.candidates?.ai_parsed_data?.rejectionReason ||
                                                        (a as any).data?.rejectionReason ||
                                                        'Geen reden opgegeven';
                                                    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
                                                });
                                                const sorted = Object.entries(reasonCounts).sort((a,b) => b[1] - a[1]);
                                                if (sorted.length === 0) return <Empty label="Geen afwijzingen gevonden" />;
                                                return sorted.map(([reason, count]) => (
                                                    <div key={reason} className="flex items-center justify-between p-2 rounded-lg border border-slate-100 bg-slate-50">
                                                        <span className="text-[11px] font-semibold text-slate-700 truncate pr-2">{reason}</span>
                                                        <span className="text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full shrink-0">{count}</span>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                        )}
                                    </Card>

                                    <Card title="Recente afwijzingen" icon={<Filter size={12}/>}>
                                        {applicationsLoading ? (
                                            <ReportingSectionLoading message="Afwijzingen laden…" className="mt-3 border-0 bg-transparent" />
                                        ) : (
                                        <div className="space-y-2 mt-2 max-h-64 overflow-y-auto pr-1">
                                            {rejectionReport.rejected.slice(0, 20).map((a) => (
                                                <div key={a.id} className="p-3 rounded-xl border border-slate-100 hover:border-rose-200 transition-colors bg-white">
                                                    <div className="flex justify-between items-start mb-1 gap-2">
                                                        <span className="text-[12px] font-bold text-slate-800">{a.candidates?.name || 'Onbekend'}</span>
                                                        <span className="text-[9px] text-slate-400 shrink-0">{new Date(a.updated_at || a.created_at).toLocaleDateString('nl-NL')}</span>
                                                    </div>
                                                    <p className="text-[10px] text-slate-500 truncate mb-1">Vacature: {a.vacancies?.title}</p>
                                                    <p className="text-[10px] font-semibold text-slate-600 mb-1">Status: {a.status || '—'}</p>
                                                    <p className="text-[10px] font-bold text-rose-600 inline-block px-1.5 py-0.5 rounded bg-rose-50">
                                                        {(a as any).candidates?.ai_parsed_data?.rejectionReason || (a as any).data?.rejectionReason || 'Geen reden'}
                                                    </p>
                                                </div>
                                            ))}
                                            {rejectionReport.rejected.length === 0 && <Empty label="Geen afwijzingen gevonden" />}
                                        </div>
                                        )}
                                    </Card>
                                </div>
                            </div>
                        )}

                        {/* ===== TAB: CONVERSIES ===== */}
                        {activeTab === 'conversions' && (
                            <div className="space-y-4">
                                <Card title="Conversie percentages per fase" icon={<Target size={12}/>}>
                                    <p className="mt-1 text-[10px] text-slate-400">
                                        Op basis van gefilterde sollicitaties en huidige application.status (zelfde funnel als Overzicht-KPI's wanneer geen dashboard-override actief is).
                                    </p>
                                    {applicationsLoading ? (
                                        <ReportingSectionLoading message="Conversiedata laden…" className="mt-4 border-0 bg-transparent" />
                                    ) : (
                                    <div className="mt-4 space-y-4">
                                        {FUNNEL_STAGES.slice(0,-1).map((stage, i) => {
                                            const curr = funnelData[i].value;
                                            const next = funnelData[i+1].value;
                                            const conv = curr > 0 ? Math.round((next / curr) * 100) : 0;
                                            return (
                                                <div key={stage.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm" style={{ backgroundColor: stage.color }}>
                                                            {i+1}
                                                        </div>
                                                        <div>
                                                            <p className="text-[12px] font-bold text-slate-800">{stage.label} <ArrowRight size={10} className="inline mx-1 text-slate-400" /> {FUNNEL_STAGES[i+1].label}</p>
                                                            <p className="text-[10px] text-slate-500 mt-0.5">{curr} kandidaten in deze fase</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden hidden md:block">
                                                            <div className="h-full rounded-full transition-all" style={{ width: `${conv}%`, backgroundColor: conv >= 50 ? '#10b981' : conv >= 20 ? '#e51b23' : '#f59e0b' }} />
                                                        </div>
                                                        <div className="text-right">
                                                            <p className={`text-lg font-black ${conv >= 50 ? 'text-emerald-600' : conv >= 20 ? 'text-[#e51b23]' : 'text-amber-600'}`}>{conv}%</p>
                                                            <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Conversie</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    )}
                                </Card>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    <Card title="Top Conversie Vacatures" icon={<Briefcase size={12}/>}>
                                        {applicationsLoading ? (
                                            <ReportingSectionLoading message="Vacature-conversies laden…" className="mt-3 border-0 bg-transparent" />
                                        ) : (
                                        <div className="space-y-2 mt-2">
                                            {vacancyStats.filter(v => v.conv > 0).slice(0, 5).map((v, i) => (
                                                <div key={v.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 border-b border-slate-50">
                                                    <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{i+1}. {v.title}</span>
                                                    <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">{v.conv}% ({v.hired}/{v.total})</span>
                                                </div>
                                            ))}
                                            {vacancyStats.filter(v => v.conv > 0).length === 0 && <Empty label="Geen conversies gevonden" />}
                                        </div>
                                        )}
                                    </Card>

                                    <Card title="Lage Conversie Vacatures (< 5%)" icon={<AlertTriangle size={12}/>}>
                                        {applicationsLoading ? (
                                            <ReportingSectionLoading message="Vacature-conversies laden…" className="mt-3 border-0 bg-transparent" />
                                        ) : (
                                        <div className="space-y-2 mt-2">
                                            {vacancyStats.filter(v => v.conv < 5 && v.total >= 5).slice(0, 5).map((v, i) => (
                                                <div key={v.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-rose-50 border-b border-slate-50">
                                                    <span className="text-[11px] font-semibold text-slate-700 truncate mr-2">{v.title}</span>
                                                    <span className="text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full shrink-0">{v.conv}% ({v.hired}/{v.total})</span>
                                                </div>
                                            ))}
                                            {vacancyStats.filter(v => v.conv < 5 && v.total >= 5).length === 0 && <Empty label="Geen vacatures met lage conversie gevonden" />}
                                        </div>
                                        )}
                                    </Card>
                                </div>
                            </div>
                        )}
                    </>
            </div>

            {/* KPI detail drawer (slide-in from right) */}
            {kpiDrawer.open && (() => {
                const sourceRows = kpiDrawer.rows;
                const historyDrawer = isKpiDrawerHistoryKind(kpiDrawer.kind);
                const q = kpiDrawerSearch.trim();
                const displayRows = q
                    ? sourceRows.filter((r) => searchTextIncludes(kpiDrawerRowSearchHaystack(r), q))
                    : sourceRows;
                const activeCount = displayRows.length;
                const totalCount = sourceRows.length;
                return (
                <div className="fixed inset-0 z-[280]">
                    <div
                        className="absolute inset-0 bg-slate-900/20 backdrop-blur-[1px]"
                        onClick={closeKpiDrawer}
                    />
                    <aside className="absolute right-0 top-0 h-full w-full max-w-[min(1100px,96vw)] bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-[slideInRight_180ms_ease-out]">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Details</p>
                                <h3 className="text-[14px] font-black text-slate-900 truncate">{kpiDrawer.title}</h3>
                                <p className="mt-1 text-[10px] font-medium text-slate-400">
                                    {kpiDrawer.loading ? (
                                        <>Lijst laden…</>
                                    ) : (
                                        <>
                                            {activeCount.toLocaleString('nl-NL')}
                                            {q && totalCount !== activeCount
                                                ? ` van ${totalCount.toLocaleString('nl-NL')}`
                                                : ''}{' '}
                                            resultaten
                                            {historyDrawer
                                                ? ' (stage-historiek in gekozen periode)'
                                                : kpiDrawer.kind === 'permPlacements'
                                                  ? ' (plaatsingen binnen filters en periode)'
                                                  : ' (binnen gekozen periode)'}
                                        </>
                                    )}
                                    {filtersDirty ? ' — niet-toegepaste filters staan nog open' : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-200"
                                onClick={closeKpiDrawer}
                            >
                                <X size={12} /> Sluiten
                            </button>
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
                            {!kpiDrawer.loading && sourceRows.length > 0 && (
                                <div className="shrink-0 rounded-lg border border-slate-200 bg-slate-50/80 p-2">
                                    <div className="relative">
                                        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="search"
                                            value={kpiDrawerSearch}
                                            onChange={(e) => setKpiDrawerSearch(e.target.value)}
                                            placeholder="Zoek vacature, kandidaat, opdrachtgever, eindklant…"
                                            className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-8 text-[11px] outline-none focus:border-[#e51b23]"
                                        />
                                        {kpiDrawerSearch ? (
                                            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setKpiDrawerSearch('')}>
                                                <X size={12} />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                            <div className="min-h-0 flex-1 overflow-auto">
                            {kpiDrawer.error && (
                                <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-medium text-rose-800">
                                    {kpiDrawer.error}
                                </div>
                            )}
                            {kpiDrawer.loading ? (
                                <ReportingSectionLoading message="Lijst laden…" className="border-0 bg-transparent" />
                            ) : displayRows.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
                                    <p className="text-[11px] font-semibold text-slate-500">
                                        {q
                                            ? 'Geen rijen voor deze zoekopdracht.'
                                            : 'Geen resultaten voor deze KPI in de gekozen periode.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-slate-200">
                                    <table className="min-w-[1380px] w-full text-[10px]">
                                        <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                                            <tr className="text-slate-500">
                                                {!historyDrawer && (
                                                    <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Type</th>
                                                )}
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Vacature</th>
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Opdrachtgever</th>
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Eindklant</th>
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Kandidaat</th>
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Status</th>
                                                <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Datum</th>
                                                {!historyDrawer && (
                                                    <>
                                                        <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Vac. live</th>
                                                        <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Pipeline</th>
                                                        <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Gewijzigd</th>
                                                    </>
                                                )}
                                                {kpiDrawer.kind === 'websiteApplications' && (
                                                    <th className="whitespace-nowrap px-2 py-1.5 text-left font-bold">Bron</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {displayRows.slice(0, 500).map((r, i) => (
                                                <tr key={i} className="hover:bg-red-50/40">
                                                    {!historyDrawer && (
                                                        <td className="whitespace-nowrap px-2 py-1">
                                                            <span
                                                                className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                                                    r.recordKind === 'historical'
                                                                        ? 'bg-indigo-50 text-indigo-700'
                                                                        : 'bg-emerald-50 text-emerald-700'
                                                                }`}
                                                            >
                                                                {kpiDrawerRecordKindLabel(r.recordKind)}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td className="max-w-[200px] px-2 py-1">
                                                        {r.vacancyId ? (
                                                            <button
                                                                type="button"
                                                                className="block w-full truncate text-left text-[10px] font-semibold text-indigo-700 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-900"
                                                                onClick={() => navigateToVacancy(r.vacancyId!)}
                                                            >
                                                                {r.vacancy || '—'}
                                                            </button>
                                                        ) : (
                                                            <span className="block truncate text-[10px] font-semibold text-slate-800">{r.vacancy || '—'}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1 max-w-[140px]">
                                                        <ReportingKpiClientCell compact name={r.opdrachtgeverName} logoUrl={r.opdrachtgeverLogoUrl} />
                                                    </td>
                                                    <td className="px-2 py-1 max-w-[140px]">
                                                        <ReportingKpiClientCell compact name={r.eindklantName} logoUrl={r.eindklantLogoUrl} />
                                                    </td>
                                                    <td className="max-w-[140px] px-2 py-1">
                                                        {r.candidateId ? (
                                                            <button
                                                                type="button"
                                                                className="block w-full truncate text-left text-[10px] text-indigo-700 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-900"
                                                                onClick={() => navigateToCandidate(r.candidateId!)}
                                                            >
                                                                {r.candidate || '—'}
                                                            </button>
                                                        ) : (
                                                            <span className="block truncate text-[10px] text-slate-700">{r.candidate || '—'}</span>
                                                        )}
                                                    </td>
                                                    <td className="max-w-[120px] truncate px-2 py-1 text-slate-600" title={r.stage}>{r.stage || '—'}</td>
                                                    <td className="whitespace-nowrap px-2 py-1 font-mono tabular-nums text-slate-500">
                                                        {r.at ? new Date(r.at).toLocaleDateString('nl-NL') : '—'}
                                                    </td>
                                                    {!historyDrawer && (
                                                        <>
                                                            <td className="whitespace-nowrap px-2 py-1 font-semibold text-slate-700">
                                                                {r.vacancyLiveStatus || '—'}
                                                            </td>
                                                            <td className="max-w-[100px] truncate px-2 py-1 text-slate-700" title={r.applicationLiveStatus}>
                                                                {r.applicationLiveStatus || '—'}
                                                            </td>
                                                            <td className="whitespace-nowrap px-2 py-1 font-mono tabular-nums text-slate-500">
                                                                {r.statusLastChangedAt
                                                                    ? new Date(r.statusLastChangedAt).toLocaleDateString('nl-NL')
                                                                    : '—'}
                                                            </td>
                                                        </>
                                                    )}
                                                    {kpiDrawer.kind === 'websiteApplications' && (
                                                        <td className="px-2 py-1 font-medium text-slate-700">{r.sourceLabel || '—'}</td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {displayRows.length > 500 && (
                                <p className="mt-2 text-[10px] text-slate-400">
                                    We tonen max. 500 regels. Verfijn zoeken of filters.
                                </p>
                            )}
                            </div>
                        </div>
                    </aside>
                </div>
                );
            })()}
        </div>
    );
};

// --- Sub-components ---
const KpiCard = ({
    label,
    value,
    icon,
    color,
    raw = false,
    loading = false,
}: {
    label: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    raw?: boolean;
    loading?: boolean;
}) => {
    if (loading) {
        return (
            <div className="flex min-h-[96px] flex-col items-center justify-center gap-2 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[#e51b23]" aria-hidden />
                <p className="text-center text-[10px] font-semibold text-slate-400">Laden…</p>
            </div>
        );
    }
    const colors: Record<string, { bg: string; icon: string; val: string }> = {
        indigo:  { bg: 'bg-red-50', icon: 'text-[#e51b23]', val: 'text-red-900' },
        brand:   { bg: 'bg-red-50', icon: 'text-[#e51b23]', val: 'text-red-900' },
        blue:    { bg: 'bg-red-50', icon: 'text-[#e51b23]', val: 'text-red-900' },
        violet:  { bg: 'bg-red-50', icon: 'text-[#e51b23]', val: 'text-red-900' },
        emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500', val: 'text-emerald-700'},
        amber:   { bg: 'bg-amber-50',   icon: 'text-amber-500',   val: 'text-amber-700'  },
        slate:   { bg: 'bg-slate-50',   icon: 'text-slate-400',   val: 'text-slate-700'  },
        rose:    { bg: 'bg-rose-50',    icon: 'text-rose-500',    val: 'text-rose-700'   },
    };
    const c = colors[color] || colors.slate;
    return (
        <div className="bg-white border border-slate-100 rounded-xl px-4 py-3 shadow-sm">
            <div className={`inline-flex w-6 h-6 rounded-lg ${c.bg} ${c.icon} items-center justify-center mb-2`}>{icon}</div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-tight">{label}</p>
            <p className={`text-2xl font-black ${c.val} mt-0.5`}>{typeof value === 'number' ? value.toLocaleString('nl-NL') : value}</p>
        </div>
    );
};

const Card = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
        <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            {icon} {title}
        </h3>
        {children}
    </div>
);

const Empty = ({ label = 'Geen data beschikbaar' }: { label?: string }) => (
    <p className="text-[11px] text-slate-300 italic py-6 text-center">{label}</p>
);

export default Reporting;
